import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";


import Papa from "papaparse";
import { storage } from "./firebase";
import { ref, getDownloadURL, uploadString, getMetadata } from "firebase/storage";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  writeBatch
} from "firebase/firestore";

import { onAuthStateChanged } from "firebase/auth";



import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { db } from "./firebase";
import { useHostVenues, usedAtVenueRecently } from "./venues";

import { uploadBytes } from "firebase/storage";  // make sure this import is present at the top

import { updateMetadata } from "firebase/storage"; // make sure this is imported
import { auth } from "./firebase"; // you already have auth in Quizmaster, import here too
import { appAlert } from "./components/AppDialog";
import { isJigsawOn } from "./quizHelpers";

// Utility function to extract Spotify embed URL from iframe code
function extractSpotifyUrl(iframeCode) {
  if (!iframeCode || typeof iframeCode !== 'string') return null;
  
  // Try to extract src attribute from iframe string
  const srcMatch = iframeCode.match(/src=["']([^"']+)["']/i);
  if (srcMatch && srcMatch[1]) {
    const url = srcMatch[1].trim();
    // Check if it's a Spotify embed URL
    if (url.includes('open.spotify.com/embed')) {
      return url;
    }
  }
  
  // If no match, check if the string itself is already a Spotify URL
  if (iframeCode.includes('open.spotify.com/embed')) {
    return iframeCode.trim();
  }
  
  return null;
}

// Extract track ID from Spotify embed URL
function extractSpotifyTrackId(spotifyUrl) {
  if (!spotifyUrl) return null;
  const match = spotifyUrl.match(/\/track\/([a-zA-Z0-9]+)/);
  return match ? match[1] : null;
}

// Construct Spotify preview URL directly (no API credentials needed!)
function getSpotifyPreviewUrl(trackId) {
  if (!trackId) return null;
  // Direct preview URL pattern - publicly accessible, no auth needed
  return `https://p.scdn.co/mp3-preview/${trackId}`;
}

function useQuestionLibrary(editRowIdx, editDraft) {
  const [qlSearch, setQlSearch] = useState("");
  const [qlLoading, setQlLoading] = useState(false);
  const [qlItems, setQlItems] = useState([]);

  const loadQuestionLibraryOnce = useCallback(async () => {
    // Don’t re-fetch if we’re loading or already have items
    if (qlLoading || qlItems.length) return;
    setQlLoading(true);
    try {
      const snap = await getDocs(collection(db, "QuestionLibrary"));
      const all = [];
      snap.forEach(d => all.push(d.data() || {}));
      setQlItems(all);
    } catch (e) {
      console.error("Failed to load QuestionLibrary:", e);
      setQlItems([]);
    } finally {
      setQlLoading(false);
    }
  }, [qlLoading, qlItems.length]);

  // Filtered, category-scoped, and sorted view
  const qlFiltered = useMemo(() => {
    const q = qlSearch.trim().toLowerCase();

    // Current row's category (from the editor)
    const catUpper = String(editDraft?.CAT || "").toUpperCase();

    // Normalise a couple of internal aliases
    // (TRUEFALSE → TRUE/FALSE)
    const alias = { TRUEFALSE: "TRUE/FALSE" };
    const wantedType = alias[catUpper] || catUpper;

    const asText = (v) => Array.isArray(v) ? v.join(", ") : String(v ?? "");

    // Fields to search when the user types in the search box
    const fields = ["question", "questionType", "genre", "answer", "tags", "geographicRelevance", "notes"];

    // 1) Keep only same-category items (if we have a category)
    let list = qlItems.filter(it => {
      if (!wantedType) return true;
      const t = String(it.questionType || "").toUpperCase();
      return t === wantedType;
    });

    // 2) Apply text search (optional)
    if (q) {
      list = list.filter(it => fields.some(f => asText(it[f]).toLowerCase().includes(q)));
    }

    // 3) Sort by interestRating desc, then by question text
    list.sort((a, b) => {
      const ai = Number(a.interestRating || 0);
      const bi = Number(b.interestRating || 0);
      if (bi !== ai) return bi - ai;
      const aq = asText(a.question);
      const bq = asText(b.question);
      return aq.localeCompare(bq);
    });

    return list.slice(0, 200);
  }, [qlItems, qlSearch, editDraft?.CAT]);



  // Auto-load when preview opens on a blank Question
  useEffect(() => {
    if (editRowIdx !== null && !String(editDraft?.Q || "").trim()) {
      loadQuestionLibraryOnce();
    }
  }, [editRowIdx, editDraft?.Q, loadQuestionLibraryOnce]);

  return {
    qlSearch, setQlSearch,
    qlLoading,
    qlItems,
    qlFiltered,
    loadQuestionLibraryOnce
  };
}


// === Helpers borrowed from the live Quiz screen ===
function cleanQuestionText(question) {
  if (!question) return "";
  return String(question)
    .replace(/^\{B\}\s*/i, "")
    .replace(/^\{Q(?:,TXT|,NUM)?\}\s*/i, "")
    .replace(/^\{TXT\}\s*/i, "")
    .replace(/^\{NUM\}\s*/i, "")
    .replace(/^\{E\}\s*/i, "")
    //.replace(/\{LB\}/gi, "\n")

}



// === MULTIANSWER helpers: detect/toggle [bracketed] correctness ===
function isBracketedCorrect(s) {
  return /^\s*\[(.*?)\]\s*$/.test(String(s || ""));
}

function stripBrackets(s) {
  return String(s || "").replace(/^\s*\[(.*?)\]\s*$/, "$1");
}

function setBracketed(text, makeCorrect) {
  const core = stripBrackets(text).trim();
  if (!core) return text;        // don't add [] to empty strings
  return makeCorrect ? `[${core}]` : core;
}




// Keep the editable hero question big but not oversized (≈25% smaller than before)
function computeHeroFontSize(text) {
  const len = (text || "").length;
  // original breakpoints, but scale the vw fallback down by ~25%
  let vw;
  if (len > 220) vw = 2.4;    // was 3.2
  else if (len > 170) vw = 2.7; // was 3.6
  else if (len > 130) vw = 3.1; // was 4.2
  else if (len > 100) vw = 3.6; // was 4.8
  else if (len > 70)  vw = 4.2; // was 5.6
  else if (len > 45)  vw = 4.95; // was 6.6
  else                vw = 5.7;  // was 7.6

  // also cap the absolute max height a touch lower (prevents vertical scrolling)
  return `clamp(18px, ${vw}vw, 7.9vh)`;
}

// --- Uniform answer pill height (preview) ---
// Figure out a single height that fits all visible pills within the space
// below the hero and above the NOTES bar (no scroll).
function getVisibleAnswersCount(row) {
  const typeUpper = canonicalCategoryUpper(row?.CAT || row?.type);
  // Breadcrumbs always renders fixed slots: A1 + A2..A11.
  // Never size based on how many clue fields currently have text.
  if (typeUpper === "BREADCRUMBS") return 11;
  const keys = typeUpper === "BREADCRUMBS"
    ? ["A1", "A2", "A3", "A4", "A5", "A6", "A7", "A8", "A9", "A10", "A11"]
    : ["A1", "A2", "A3", "A4", "A5", "A6"];
  return keys.map((k) => row?.[k])
    .filter((v) => String(v ?? "").trim() !== "").length || 1;
}

// Compute a uniform height for the answer pills inside the PREVIEW modal.
// We reserve extra vertical space when there is NO side media so the NOTES area
// is never overlapped, and we also cap the max pill height in that case.
//
// Usage elsewhere stays the same; mediaEl is optional.
function calcUniformPillHeight({
  containerEl,        // the big preview box (".quiz-bg")
  heroEl,             // top hero question box (heroBoxRef)
  mediaEl = null,     // OPTIONAL: the right-hand preview media (image/video)
  notesReserve = 122, // base px reserved for footer (visible explanation + notes + music)
  topChrome = 86,     // TIME/POINTS/TYPE chrome + spacing
  gaps = 22,          // the <ol> gap between pills
  count = 4,          // number of visible answers
  minH = 54,          // clamp height min
  maxH = 110,         // clamp height max (with media)
  noMediaMaxH = 88    // cap when there is no side media (pills can look huge in a tall modal)
}) {
  if (!containerEl) return 74;

  const hHero = heroEl?.clientHeight || 0;
  const hasMedia = !!(mediaEl && mediaEl.clientWidth && mediaEl.clientHeight);

  // When there is NO media, pills tend to stretch too tall. Reserve more
  // space for NOTES and clamp the max pill height a bit lower.
  const extraNotes = hasMedia ? 0 : 36;            // +36px gives ~2 extra lines
  const effectiveNotes = notesReserve + extraNotes;
  const effectiveMaxH = hasMedia ? maxH : noMediaMaxH;      // lower cap when no media

  const usable = containerEl.clientHeight - hHero - effectiveNotes - topChrome;
  const slots = Math.max(1, count);
  const totalGaps = Math.max(0, (slots - 1) * gaps);
  const raw = Math.floor((usable - totalGaps) / slots);

  return Math.max(minH, Math.min(effectiveMaxH, raw));
}

// Breadcrumbs edit popup: the footer is position:absolute, so in-flow content does not
// "push" the footer. Measure the real vertical span from the answer stack to the footer.
function calcBreadcrumbsPillHeightFromLayout({ areaEl, footerEl, containerEl, count, gapPx = 5 }) {
  if (!areaEl || !footerEl || !containerEl) return 26;
  const a = areaEl.getBoundingClientRect();
  const f = footerEl.getBoundingClientRect();
  const c = containerEl.getBoundingClientRect();
  // Space above the fixed footer, measured inside the modal
  const bottomLimit = Math.min(f.top, c.bottom) - 4;
  let avail = bottomLimit - a.top - 6;
  avail = Math.max(0, avail);
  const slots = Math.max(1, count);
  const dynamicGap = avail > 360 ? 8 : avail > 300 ? 6 : avail > 250 ? 5 : gapPx;
  const totalGaps = Math.max(0, (slots - 1) * dynamicGap);
  const raw = Math.floor((avail - totalGaps) / slots);
  if (!Number.isFinite(raw)) return 26;
  // Keep readable by default, while still fitting all clues on short/wide windows.
  return Math.max(20, Math.min(42, raw));
}

// Breadcrumbs: footer sits below the scroll region (in-flow) — use scroll bottom minus <ol> top
function calcBreadcrumbsPillHeightFromScrollRegion({ scrollEl, olEl, count, gapBase = 4 }) {
  if (!scrollEl || !olEl) return 26;
  const s = scrollEl.getBoundingClientRect();
  const o = olEl.getBoundingClientRect();
  let avail = s.bottom - o.top - 10;
  avail = Math.max(0, avail);
  const slots = Math.max(1, count);
  const dynamicGap = avail > 380 ? 8 : avail > 300 ? 6 : avail > 220 ? 5 : gapBase;
  const totalGaps = Math.max(0, (slots - 1) * dynamicGap);
  const raw = Math.floor((avail - totalGaps) / slots);
  if (!Number.isFinite(raw)) return 24;
  // Keep Breadcrumb rows readable; allow scroll rather than collapsing to hairline rows.
  return Math.max(20, Math.min(44, raw));
}

// UI placeholders: first clue revealed = hardest help (A2) … last = lightest (A11)
const BREADCRUMBS_CLUE_PLACEHOLDERS = {
  2:  "Clue – Impossible",
  3:  "Clue – Brutal",
  4:  "Clue – Severe",
  5:  "Clue – Hard",
  6:  "Clue – Tricky",
  7:  "Clue – Fair",
  8:  "Clue – Easy",
  9:  "Clue – Simple",
  10: "Clue – Giveaway",
  11: "Clue – Obvious",
};

function getBreadcrumbsPillPlaceholder(displayIdx) {
  if (displayIdx === 0) return "Correct answer";
  const ax = displayIdx + 1;
  return BREADCRUMBS_CLUE_PLACEHOLDERS[ax] || "Clue";
}

// Fixed clue slot keys in CSV (reveal order: A2 first … A10 last, A11 optional)
const BREADCRUMBS_CLUE_KEYS = ["A2", "A3", "A4", "A5", "A6", "A7", "A8", "A9", "A10", "A11"];

function reorderListImmutable(list, startIndex, endIndex) {
  const next = list.slice();
  const [moved] = next.splice(startIndex, 1);
  next.splice(endIndex, 0, moved);
  return next;
}

// Some CSVs use short "BRE" (grid shows 3-letter type); it must match full BREADCRUMBS in this editor
function canonicalCategoryUpper(raw) {
  const u = String(raw ?? "").trim().toUpperCase();
  if (u === "BRE" || u === "BREADCRUMB") return "BREADCRUMBS";
  return u;
}
function isBreadcrumbsCategory(raw) {
  return canonicalCategoryUpper(raw) === "BREADCRUMBS";
}

/** P1 / P1_URL are not used in the CSV editor for these types (match live: no question image). */
function isP1MediaBlackoutType(raw) {
  const u = canonicalCategoryUpper(String(raw ?? ""));
  return u === "WHEEL" || u === "BREADCRUMBS" || u === "OUTOFORDER";
}

function mediaDisabledLabelForCategory(catUpper) {
  if (catUpper === "WHEEL") return "MEDIA disabled for WHEEL";
  if (catUpper === "BREADCRUMBS") return "MEDIA disabled for BREADCRUMBS";
  if (catUpper === "OUTOFORDER") return "MEDIA disabled for OUT OF ORDER";
  return "MEDIA disabled";
}

/** Trove: P1, P1_URL, and VIDEO_URL cells are blacked (no image/video for these slide types). */
const TROVE_SLIDE_MEDIA_BLACKOUT_CATS = new Set([
  "INTRODUCTION", "END QUIZ", "JOIN QUIZ", "BREAK TIME",
  "SHOWROUNDS", "STARTROUND", "ENDROUND"
]);
function isTroveSlideMediaBlackoutCat(upperCategory) {
  return TROVE_SLIDE_MEDIA_BLACKOUT_CATS.has(String(upperCategory || "").toUpperCase());
}

function getMultiAnswerSuffixLikeLive(row) {
  const type = String(row.CAT || "").toUpperCase();
  if (type !== "MULTIANSWER") return "";
  const answers = [row.A1, row.A2, row.A3, row.A4, row.A5, row.A6].filter(Boolean);
  const count = answers.filter(a => /^\s*\[.*?\]\s*$/.test(a || "")).length;
  return count > 1 ? ` (SELECT ${count})` : "";
}

// Map a CSV row to the "q" object QuizScreen expects
function rowToQ(row) {
  const typeUpper = canonicalCategoryUpper(row?.CAT || row?.type);
  const answerKeys = typeUpper === "BREADCRUMBS"
    ? ["A1", "A2", "A3", "A4", "A5", "A6", "A7", "A8", "A9", "A10", "A11"]
    : ["A1", "A2", "A3", "A4", "A5", "A6"];
  const answers = answerKeys.map((k) => row?.[k]).filter(a => a !== undefined);
  // Default order: A, B, C... based on which are non-empty
  const answersOrder = answers.map((_, idx) => idx);
  return {
    question: row.Q || "",
    answers,
    answersOrder,
    type: row.CAT || "",
    CAT: row.CAT || "",
    time: Number.parseInt(row.TIME, 10) || 0,
    TIME: row.TIME,
    BACKGROUND_URL: row.BACKGROUND_URL || "",
    SOUND_URL: row.SOUND_URL || "",
    VIDEO_URL: row.VIDEO_URL || "",
    P1_URL: row.P1_URL || "",
  };
}

// === Exact jigsaw reveal used live ===
function hashSeed(str) {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) hash = ((hash << 5) + hash) + str.charCodeAt(i);
  return hash >>> 0;
}
function mulberry32(a) {
  return function() {
    var t = a += 0x6D2B79F5;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  }
}
function seededShuffleJigsaw(arr, seed = 1) {
  let a = arr.slice();
  let random = mulberry32(seed);
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
// Lazy load tiny media previews so we don't load 100+ assets at once.
function LazyMedia({ kind, src, onClick, style }) {
  const [visible, setVisible] = React.useState(false);
  const holderRef = React.useRef(null);
  const vidRef = React.useRef(null);             // ← add

  React.useEffect(() => {
    if (!src) return;
    const el = holderRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        setVisible(true);
        io.disconnect();
      }
    }, { rootMargin: "200px" });
    io.observe(el);
    return () => { try { io.disconnect(); } catch {} };
  }, [src]);

  // When the tiny preview is a video, load metadata and render the first frame
  React.useEffect(() => {
    if (kind !== "video" || !visible || !vidRef.current) return;
    const v = vidRef.current;
    const onMeta = () => { try { v.currentTime = 0.001; } catch {} };
    v.preload = "metadata";
    v.muted = true;
    v.playsInline = true;
    v.addEventListener("loadedmetadata", onMeta, { once: true });
    return () => { try { v.removeEventListener("loadedmetadata", onMeta); } catch {} };
  }, [kind, visible, src]);

  const base = {
    width: 48, height: 48, objectFit: "cover",    // cover = nicer crop in a square
    display: "block", margin: "0 auto", cursor: "zoom-in"
  };

  return (
    <div
      ref={holderRef}
      style={{ width: 48, height: 48, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto", ...(style||{}) }}
      onClick={(e) => { e.stopPropagation(); if (visible && onClick) onClick(e); }}
    >
      {!visible ? (
        <span style={{ fontSize: 10, opacity: 0.7 }}>preview…</span>
      ) : kind === "video" ? (
        <video ref={vidRef} src={src} style={base} playsInline muted preload="metadata" />
      ) : (
        <img src={src} alt="" loading="lazy" style={base} />
      )}
    </div>
  );
}

// ===== QuestionLibrary integration helpers =====

// Map a QuestionLibrary record into our row shape while preserving any existing values.
// Only fill blanks; never clobber a field the user already set.
// ===== QuestionLibrary integration helpers =====

// Map a QuestionLibrary record (your lower-case schema) into our row shape,
// filling ONLY blanks in the current row.
function mapLibraryItemToRow(prevRow = {}, lib = {}) {
  const out = { ...prevRow };
  const keepOr = (prior, next) => {
    const cur = prior ?? "";
    return String(cur).trim() !== "" ? prior : (next ?? "");
  };

  // Source fields (exactly as per your screenshot)
  const Question  = lib.question;
  const Genre     = lib.genre;
  const Category  = lib.questionType;            // map to CAT
  const Answer    = lib.answer;
  const A2        = lib.A2;
  const A3        = lib.A3;
  const A4        = lib.A4;
  const A5        = lib.A5;
  const A6        = lib.A6;
  const A7        = lib.A7;
  const A8        = lib.A8;
  const A9        = lib.A9;
  const A10       = lib.A10;
  const A11       = lib.A11;

  const interestRating              = lib.interestRating;
  const interestRatingJustification = lib.interestRatingJustification;
  const difficulty                  = lib.difficulty;
  const flag                        = lib.accuracyFlag;
  const minAge                      = lib.minAge;
  const maxAge                      = lib.maxAge;
  const demographicAppeal           = Array.isArray(lib.demographicAppeal) ? lib.demographicAppeal.join(", ") : lib.demographicAppeal;
  const tags                        = Array.isArray(lib.tags) ? lib.tags.join(", ") : lib.tags;
  const geographicRelevance         = Array.isArray(lib.geographicRelevance) ? lib.geographicRelevance.join(", ") : lib.geographicRelevance;
  const notes                       = lib.notes;
  const visibleExplanation          = lib.visibleExplanation;
  const timeSensitivity             = lib.timeSensitivityYear;  // if you want the raw year
  const dateAdded                   = lib.dateAdded;
  const media                       = lib.mediaSuggestion;      // suggestion/filename/text

  // Apply to our row shape
  out.Q   = keepOr(out.Q,   Question);
  // Always use library's questionType for CAT when loading from library (don't preserve existing CAT)
  // This ensures questions loaded from library show the correct category (e.g., NEARESTANSWER not BETANSWER)
  out.CAT = String(Category || "").trim() !== "" ? String(Category || "").toUpperCase() : (out.CAT || "");

  out.GENRE                         = keepOr(out.GENRE, Genre);
  out.INTEREST_RATING               = keepOr(out.INTEREST_RATING, interestRating);
  out.INTEREST_RATING_JUSTIFICATION = keepOr(out.INTEREST_RATING_JUSTIFICATION, interestRatingJustification);
  out.DIFFICULTY                    = keepOr(out.DIFFICULTY ?? out.Difficulty, difficulty);
  out.FLAG                          = keepOr(out.FLAG ?? out.Flag, flag);
  out.MIN_AGE                       = keepOr(out.MIN_AGE ?? out.min_age, minAge);
  out.MAX_AGE                       = keepOr(out.MAX_AGE ?? out.max_age, maxAge);
  out.DEMOGRAPHIC_APPEAL            = keepOr(out.DEMOGRAPHIC_APPEAL, demographicAppeal);
  out.TAGS                          = keepOr(out.TAGS, tags);
  out.GEOGRAPHIC_RELEVANCE          = keepOr(out.GEOGRAPHIC_RELEVANCE, geographicRelevance);
  out.TIME_SENSITIVITY              = keepOr(out.TIME_SENSITIVITY, timeSensitivity);
  out.DATE_ADDED                    = keepOr(out.DATE_ADDED, dateAdded);

  // Answers (A1 is correct)
  out.A1 = keepOr(out.A1, Answer);
  out.A2 = keepOr(out.A2, A2);
  out.A3 = keepOr(out.A3, A3);
  out.A4 = keepOr(out.A4, A4);
  out.A5 = keepOr(out.A5, A5);
  out.A6 = keepOr(out.A6, A6);
  out.A7 = keepOr(out.A7, A7);
  out.A8 = keepOr(out.A8, A8);
  out.A9 = keepOr(out.A9, A9);
  out.A10 = keepOr(out.A10, A10);
  out.A11 = keepOr(out.A11, A11);

  // Media: if you also store a direct URL, add it here as P1_URL
  out.P1 = keepOr(out.P1, media);
  
  // Notes and Visible Explanation
  out.NOTES = keepOr(out.NOTES, notes);
  out.visibleExplanation = keepOr(out.visibleExplanation, visibleExplanation);
  
  // Audio: map Audiolink to SOUND_URL and SpotifyUrl to SPOTIFY_URL
  const audiolink = lib.Audiolink;
  const spotifyUrl = lib.SpotifyUrl;
  out.SOUND_URL = keepOr(out.SOUND_URL, audiolink);
  out.SPOTIFY_URL = keepOr(out.SPOTIFY_URL, spotifyUrl);

  return out;
}


// Lightweight, inline picker UI used inside the preview modal when Q is empty.
function QuestionLibraryPicker({
  items,
  loading,
  search,
  setSearch,
  onChoose,
  onCancel
}) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(0,0,0,0.76)",
        backdropFilter: "blur(2px)",
        zIndex: 5000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 14
      }}
      onClick={onCancel}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: "min(1100px, 94vw)",
          maxHeight: "80vh",
          overflow: "hidden",
          background: "linear-gradient(180deg, rgba(28,34,52,0.92), rgba(18,20,34,0.92))",
          border: "1px solid rgba(255,255,255,0.18)",
          borderRadius: 14,
          boxShadow: "0 22px 60px rgba(0,0,0,0.6)",
          color: "#fff",
          padding: "12px 12px 10px",
          display: "flex",
          flexDirection: "column",
          gap: 10
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontWeight: 900, fontSize: "1.1rem" }}>
            Pick from Question Library
          </div>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by question, genre, category, tags…"
            style={{
              flex: 1,
              background: "rgba(255,255,255,0.12)",
              color: "#fff",
              border: "1px solid #3b4a6a",
              borderRadius: 10,
              padding: "8px 10px",
              outline: "none",
              fontWeight: 700
            }}
            spellCheck={false}
          />
          <button
            onClick={onCancel}
            style={{
              background: "rgba(255,255,255,0.10)",
              color: "#fff",
              border: "1px solid #888",
              borderRadius: 10,
              padding: "8px 12px",
              fontWeight: 800,
              cursor: "pointer"
            }}
          >
            Cancel
          </button>
        </div>

        <div
          style={{
            overflow: "auto",
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(0,0,0,0.24)"
          }}
        >
          {loading ? (
            <div style={{ padding: 16, fontWeight: 800, opacity: 0.85 }}>
              Loading library…
            </div>
          ) : items.length === 0 ? (
            <div style={{ padding: 16, fontWeight: 800, opacity: 0.85 }}>
              No results.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "rgba(255,255,255,0.08)" }}>
                  <th style={{ padding: "8px 10px", textAlign: "left" }}>Question</th>
                  <th style={{ padding: "8px 10px", textAlign: "left" }}>Category</th>
                  <th style={{ padding: "8px 10px", textAlign: "left" }}>Genre</th>
                  <th style={{ padding: "8px 10px", textAlign: "left" }}>Answer</th>
                  <th style={{ padding: "8px 10px", textAlign: "left" }}>Tags</th>
                  <th style={{ padding: "8px 10px" }}>Add</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={idx} style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                    <td style={{ padding: "8px 10px" }}>{it.question}</td>
                    <td style={{ padding: "8px 10px" }}>{it.questionType}</td>
                    <td style={{ padding: "8px 10px" }}>{it.genre}</td>
                    <td style={{ padding: "8px 10px" }}>{it.answer}</td>
                    <td style={{ padding: "8px 10px" }}>
                      {Array.isArray(it.tags) ? it.tags.join(", ") : it.tags}
                    </td>
                    <td style={{ padding: "8px 10px", textAlign: "center" }}>
                      <button
                        onClick={() => onChoose(it)}
                        style={{
                          background: "#47e373",
                          color: "#111",
                          fontWeight: 900,
                          border: "none",
                          borderRadius: 10,
                          padding: "6px 14px",
                          cursor: "pointer",
                          boxShadow: "0 8px 22px rgba(0,0,0,0.35)"
                        }}
                      >
                        Use this
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ fontSize: ".94rem", opacity: 0.8, paddingLeft: 2 }}>
          Tip: we only fill blanks. Your existing values are preserved.
        </div>
      </div>
    </div>
  );
}


// Partially-transparent jigsaw so the image is only partly covered
function JigsawImageReveal({
  src,
  timer = 30,
  totalTime = 30,
  rows = 6,
  cols = 8,
  style,
  coverOpacity = 0.68   // <— tweak this to show more/less of the image
}) {
  const [naturalSize, setNaturalSize] = React.useState({ width: 1, height: 1 });

  React.useEffect(() => {
    if (!src) return;
    const img = new window.Image();
    img.onload = () => setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
    img.src = src;
  }, [src]);

  const numTiles = rows * cols;
  const elapsed = Math.max(0, totalTime - timer);
  const revealCount = Math.floor((elapsed / totalTime) * numTiles);

  const indexes = Array.from({ length: numTiles }, (_, i) => i);
  const seed = hashSeed(src || "");
  const shuffledIndexes = seededShuffleJigsaw(indexes, seed);
  const revealedSet = new Set(shuffledIndexes.slice(0, revealCount));

  return (
    <div
      style={{
        position: "relative",
        overflow: "visible",
        aspectRatio:
          naturalSize.width && naturalSize.height
            ? naturalSize.width + " / " + naturalSize.height
            : undefined,
        ...style
      }}
    >
      {/* Base image always fully visible */}
      <img
        src={src}
        alt="Jigsaw Visual"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          display: "block",
          borderRadius: style?.borderRadius || "0"
        }}
      />

      {/* Partially-transparent covering tiles (hide unrevealed parts only) */}
      <div
        style={{
          pointerEvents: "none",
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 20
        }}
      >
        {shuffledIndexes.map((idx) => {
          if (revealedSet.has(idx)) return null;
          const row = Math.floor(idx / cols);
          const col = idx % cols;
          return (
            <div
              key={idx}
              style={{
                position: "absolute",
                top: `${(row * 100) / rows}%`,
                left: `${(col * 100) / cols}%`,
                width: `${100 / cols}%`,
                height: `${100 / rows}%`,
                // Slightly see-through gradient so the image is visible underneath
                background:
                  "linear-gradient(120deg, rgba(37,43,62,0.90) 60%, rgba(17,17,17,0.90) 100%)",
                opacity: coverOpacity,         // <— transparency control
                border: "1px solid #222",
                borderRadius: 2,
                boxShadow: "0 1px 9px #1117",
                transition: "opacity 0.33s cubic-bezier(.67,0,.38,1.1)",
                willChange: "opacity"
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

// Reusable centred "Quizmaster Bob" modal
function BobModal({
  open,
  onClose,
  title = "Quizmaster Bob says",
  message,
  confirmText = "OK",
  cancelText,              // if provided, shows a Cancel button
  onConfirm,               // if provided, shows confirm button with this handler
  imageSrc = "/media/Quizmasterbobpopup.png"
}) {
  if (!open) return null;
  const handleConfirm = () => { try { onConfirm && onConfirm(); } finally { onClose && onClose(); } };
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2147483647
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
            width: "min(560px, 92vw)",
                  background:
                    "linear-gradient(180deg, rgba(34,42,58,0.78) 0%, rgba(27,33,48,0.78) 100%)", // add alpha
                  border: "1px solid rgba(255,255,255,0.20)",
                  borderRadius: 16,
                  boxShadow: "0 18px 60px rgba(0,0,0,0.45)",
                  padding: "18px 18px 16px",
                  color: "#eaf2ff",
                  textAlign: "center"
        }}
      >
        <div
          style={{
            fontFamily: "'Montserrat','Open Sans',Arial,sans-serif",
            fontWeight: 900,
            fontSize: "1.2rem",
            color: "#ffe066",
            textShadow: "2px 2px 10px #222, 0 0 14px #09f",
            marginBottom: 6
          }}
        >
          {title}
        </div>

        <img
          src={imageSrc}
          alt="Quizmaster Bob"
          style={{
            display: "block",
            width: 96,
            height: 96,
            objectFit: "cover",
            borderRadius: "50%",
            margin: "2px auto 10px",
            boxShadow: "0 6px 22px rgba(0,0,0,0.35)",
            border: "2px solid rgba(255,255,255,0.75)"
          }}
        />

        <div style={{ fontSize: "1.05rem", margin: "6px 4px 14px" }}>
          {message}
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
          {cancelText && (
            <button
              onClick={onClose}
              style={{
                background: "rgba(255,255,255,0.12)",
                color: "#fff",
                fontWeight: 800,
                border: "1px solid #888",
                borderRadius: 10,
                padding: "10px 20px",
                cursor: "pointer",
                boxShadow: "0 4px 14px rgba(0,0,0,0.35)"
              }}
            >
              {cancelText}
            </button>
          )}
          <button
            autoFocus
            onClick={handleConfirm}
            style={{
              background: "#47e373",
              color: "#111",
              fontWeight: 900,
              border: "none",
              borderRadius: 10,
              padding: "10px 22px",
              cursor: "pointer",
              boxShadow: "0 8px 22px rgba(0,0,0,0.35)"
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}




const catOptions = [
  "Default Category",
  "TRUE/FALSE",
  "MULTICHOICE",
  "VANISHINGCHOICE",
  "MULTIANSWER",
  "OUTOFORDER",
  "NEARESTANSWER",
  "JACKPOT",
  "WHEEL",
  "TYPE",
  "BREADCRUMBS",
  "BUZZER",
  "BETANSWER",
  "WHOAMI",
  "ENDROUND",
  "STARTROUND",
  "SHOWROUNDS",
  "BREAK TIME"

];


// You can adjust these as needed for each type
const CAT_DEFAULTS = {
  "TRUE/FALSE": { TIME: "30", POINTS: "5", Q: "Answer TRUE or FALSE" },
  "MULTICHOICE": { TIME: "40", POINTS: "10", Q: "Select the correct answer" },
  "VANISHINGCHOICE": { TIME: "50", POINTS: "15", Q: "Select before options disappear!" },
  "MULTIANSWER": { TIME: "50", POINTS: "5", Q: "Surround each correct answer (including in 'CORRECT') with square brackets []. Points are awarded for EVERY correct answer e.g. 3x5" },
  "NEARESTANSWER": { TIME: "60", POINTS: "20", Q: "Enter the closest value" },
  "JACKPOT": { TIME: "60", POINTS: "0", Q: "Jackpot question - no points, scored manually", NOTES: "JACKPOT questions have no points and are scored manually by the host" },
  "WHEEL": { TIME: "50", POINTS: "20", Q: "Wheel of Fortune-style question" },
  "TYPE": { TIME: "50", POINTS: "20", Q: "Type your answer" },
  "BREADCRUMBS": { TIME: "60", POINTS: "20", Q: "Reveal clues; first correct guess wins", NOTES: "A1 = correct answer; A2–A11 = clues (order = reveal order)." },
  "BUZZER": { TIME: "20", POINTS: "20", Q: "Buzz in your answer" },
  "BETANSWER": { TIME: "60", POINTS: "25", Q: "Bet your points, then answer!" },
   "OUTOFORDER": { TIME: "70", POINTS: "5", Q: "Arrange these in the correct order", NOTES: "Players receive points for each option that they have correctly ordered" }, 
  "WHOAMI": { TIME: "30", POINTS: "25", Q: "Who am I?" },
  "ENDROUND": { TIME: "", POINTS: "", Q: "" },
  "STARTROUND": {
    TIME: "",
    POINTS: "",
    Q: "",
    NOTES:
      "Team round tag choice: turn On under Quiz Options (host). In QUESTION start with hashtags — use #TeamChoice or #PlayerChoice so the eligible team (lowest round score among teams that answered the last scored question) picks this round's library tag on their phones after the previous ENDROUND. Add an optional genre tag (e.g. #Movie, #TV) to limit tag options to that genre; with only #TeamChoice / #PlayerChoice, tags come from the full library pool. Placeholder questions in this round merge the chosen tag when pulling from the Question Library.",
  },
  "DEFAULT CATEGORY": { TIME: "", POINTS: "", Q: "" },
  "SHOWROUNDS": { TIME: "0", POINTS: "0", Q: "Upcoming Rounds" },
  // NEW Types
  "JOIN QUIZ": { Q: "Join The Quiz!", NOTES: "This screen allows players to join your Quiz" },
  "INTRODUCTION": { Q: "Welcome!", NOTES: "This screen will show the Title of your Quiz and your A.I host will introduce themselves" },
  "END QUIZ": { Q: "End of Quiz" },
  "BREAK TIME": { Q: "Break Time!", TIME: "300" }, // Default 5 min break
 

};

// ==== Category colour map (cell + <option> backgrounds) ====
const CAT_PALETTE = {
  "MULTICHOICE":      { bg: "#1f8a4c", opt: "#2ea862", fg: "#fff" }, // green
  "VANISHINGCHOICE":  { bg: "#1f5fbf", opt: "#2f77e1", fg: "#fff" }, // blue
  "MULTIANSWER":      { bg: "#e67e22", opt: "#f39c12", fg: "#111" }, // orange
  "TRUE/FALSE":       { bg: "#f4d03f", opt: "#f7dc6f", fg: "#111" }, // yellow
  "NEARESTANSWER":    { bg: "#6a3fb2", opt: "#7e57c2", fg: "#fff" }, // purple
  "JACKPOT":          { bg: "#d4af37", opt: "#e6c866", fg: "#111" }, // gold
  "WHEEL":            { bg: "#3949ab", opt: "#5c6bc0", fg: "#fff" }, // indigo
  "TYPE":             { bg: "#00acc1", opt: "#26c6da", fg: "#111" }, // cyan
  "BREADCRUMBS":      { bg: "#8d6e63", opt: "#a1887f", fg: "#fff" }, // brown/tan
  "BUZZER":           { bg: "#d81b60", opt: "#ec407a", fg: "#fff" }, // pink
  "BETANSWER":        { bg: "#ffb300", opt: "#ffc107", fg: "#111" }, // amber
  "WHOAMI":           { bg: "#00897b", opt: "#26a69a", fg: "#fff" }, // teal
  "OUTOFORDER": { bg: "#FFDAB9", opt: "#FFC8A2", fg: "#111" }, //peach



  // Display-like rows stay RED
  "DEFAULT CATEGORY": { bg: "#c62828", opt: "#e53935", fg: "#fff" },
  "STARTROUND":       { bg: "#c62828", opt: "#e53935", fg: "#fff" },
  "ENDROUND":         { bg: "#c62828", opt: "#e53935", fg: "#fff" },
  "DISPLAY":          { bg: "#c62828", opt: "#e53935", fg: "#fff" },
  "SLIDE":            { bg: "#c62828", opt: "#e53935", fg: "#fff" },
  "SHOWROUNDS":       { bg: "#c62828", opt: "#e53935", fg: "#fff" },
  // NEW Types also RED
  "JOIN QUIZ":        { bg: "#c62828", opt: "#e53935", fg: "#fff" },
  "INTRODUCTION":     { bg: "#c62828", opt: "#e53935", fg: "#fff" },
  "END QUIZ":         { bg: "#c62828", opt: "#e53935", fg: "#fff" },
  "BREAK TIME":       { bg: "#c62828", opt: "#e53935", fg: "#fff" }
};

function getCatPalette(catValue) {
  const key = canonicalCategoryUpper(catValue);
  return CAT_PALETTE[key] || { bg: "#2b3d55", opt: "#3b5275", fg: "#fff" };
}

// For rendering the option list: show "Display" instead of "Default Category"
function renderCatLabel(opt) {
  return opt === "Default Category" ? "Display" : opt;
}




const specialCats = ["STARTROUND", "ENDROUND", "Default Category"];




// === ShowRounds preview used in the editor modal ===
// Lists all STARTROUND rows as Multichoice-style coloured buttons, 6 per column.
// Non-interactive, reuses option A..F colours by repeating.
// === ShowRounds preview used in the editor modal ===
// Lists all STARTROUND rows as Multichoice-style coloured buttons, 6 per column.
// Non-interactive, reuses option A..F colours by repeating.
// === ShowRounds preview used in the editor modal ===
// Uniform pill size: every pill has the same width/height; text shrinks to fit.
// Non-interactive, reuses option A..F colours by repeating.
// === ShowRounds preview used in the editor modal ===
// - Lists STARTROUND and BREAK TIME items up to (but not after) the first END QUIZ
// - STARTROUND titles are auto-numbered: "1: <Round Name>"
// - BREAK TIME shows as "BREAK - <minutes> minutes" and gets the glimmer effect
// === ShowRounds preview used in the editor modal (compact, left-aligned) ===
// - Lists STARTROUND and BREAK TIME items up to the first END QUIZ
// - STARTROUND titles auto-numbered "1: ..."
// - BREAK TIME shows as "BREAK - <minutes> minutes" and uses correct-glimmer
// === ShowRounds preview used in the editor modal (compact, left-aligned) ===
// - Lists STARTROUND and BREAK TIME items up to the first END QUIZ
// - STARTROUND titles auto-numbered "1: ..."
// - BREAK TIME shows "BREAK - <minutes> minutes" and uses correct-glimmer
// - Auto-columns so we don't stack too tall; max 4 columns, max 5 items/col
// === ShowRounds preview (compact, left-aligned, no overflow) ===
// - Lists STARTROUND + BREAK TIME until the first END QUIZ
// - STARTROUND numbers shown as "1: Title"
// - BREAK TIME shown as "BREAK - <minutes> minutes" with glimmer class
// - Auto columns (2..4), max 5 items per column
// - Per-pill font size is computed so text never overflows
function ShowRoundsPreview({ rows }) {
  const list = Array.isArray(rows) ? rows : [];
  const items = [];

  for (let i = 0; i < list.length; i++) {
    const r = list[i] || {};
    const type = String(r.CAT || r.type || "").trim().toUpperCase();
    if (type === "END QUIZ") break;
    if (type === "STARTROUND") {
      const raw = r.Q || r.title || r.question || "";
      const title = (cleanQuestionText(raw) || "").trim() || "This Round needs a Title";
      items.push({ kind: "round", title });
    }
    if (type === "BREAK TIME") {
      const secs = parseInt(r.TIME, 10);
      const mins = Number.isFinite(secs) ? Math.max(0, Math.round(secs / 60)) : 0;
      items.push({ kind: "break", title: mins > 0 ? `BREAK - ${mins} minutes` : "BREAK" });
    }
  }

  if (!items.length) {
    return <div style={{ color: "#fff", opacity: 0.85, padding: "12px 0" }}>No upcoming rounds.</div>;
  }

  // Number only rounds
  let n = 0;
  const display = items.map(it =>
    it.kind === "round" ? { ...it, show: `${++n}: ${it.title}` } : { ...it, show: it.title }
  );

  // Layout constants (compact)
  const PILL_WIDTH = 500;     // px
  const PILL_HEIGHT = 50;     // px
  const GAP = 10;

  // Compute a per-pill font size (px) based on label length and available width
  // This is more aggressive than clamp() so it won't spill at small widths.
  const computePillFontPx = (t) => {
    const len = Math.max(1, String(t || "").length);
    // rough width-per-character heuristic (includes heavy weight & shadow)
    // tweak coeff as needed; 0.62 chosen from testing your screenshots
    const est = PILL_WIDTH / (len * 0.62);
    // keep it readable but contained
    return Math.max(14, Math.min(32, Math.floor(est)));
  };

  // Auto columns to control total height
  const maxPerCol = 5;
  const colCount = Math.min(4, Math.max(2, Math.ceil(display.length / maxPerCol)));
  const perCol = Math.ceil(display.length / colCount);
  const columns = Array.from({ length: colCount }, (_, c) =>
    display.slice(c * perCol, (c + 1) * perCol)
  ).filter(col => col.length);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${columns.length}, ${PILL_WIDTH}px)`,
        gap: `${GAP * 1.5}px`,
        justifyContent: "center",
        alignItems: "start",
        width: "100%",
        padding: "6px 4px 0"
      }}
    >
      {columns.map((col, ci) => (
        <ol key={ci} className="answer-list" style={{ listStyle: "none", margin: 0, padding: 0, gap: `${GAP}px`, pointerEvents: "none" }}>
          {col.map((item, ri) => {
            const letter = String.fromCharCode(97 + (ri % 6)); // a..f palette
            const extraClass = item.kind === "break" ? "correct-glimmer" : "";
            const fpx = computePillFontPx(item.show);
            return (
              <li key={ri} style={{ display: "flex", justifyContent: "center" }}>
                <div
                  className={`answer-option option-${letter} ${extraClass}`}
                  style={{
                    width: `${PILL_WIDTH}px`,
                    height: `${PILL_HEIGHT}px`,
                    minWidth: `${PILL_WIDTH}px`,
                    minHeight: `${PILL_HEIGHT}px`,
                    padding: "0 1.1em",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    textAlign: "left",
                    color: "#fff",
                    fontWeight: 900,
                    lineHeight: 1.1,
                    userSelect: "none",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: `${fpx}px`,          // ← auto-shrinks per pill
                    borderRadius: 999
                  }}
                  title={item.show}
                >
                  {item.show}
                </div>
              </li>
            );
          })}
        </ol>
      ))}
    </div>
  );
}



// Use the same computeAnswerFontSize as quizscreen.js for consistent sizing
// EVEN MORE AGGRESSIVE SCALING to prevent text cutoff
const computeAnswerFontSize = (text) => {
  const len = (String(text || "")).length;
  // >>> ULTRA-AGGRESSIVE SCALING FOR LONGER TEXT <<<
  if (len > 100) return "clamp(0.5rem, 0.6vw, 0.9rem)"; // Extra Extra Tiny
  if (len > 90) return "clamp(0.55rem, 0.65vw, 1.0rem)";
  if (len > 80) return "clamp(0.6rem, 0.7vw, 1.1rem)";
  if (len > 70) return "clamp(0.65rem, 0.8vw, 1.2rem)";
  if (len > 60) return "clamp(0.7rem, 0.9vw, 1.3rem)";
  if (len > 50) return "clamp(0.75rem, 1.0vw, 1.4rem)";
  if (len > 45) return "clamp(0.8rem, 1.1vw, 1.5rem)";
  if (len > 40) return "clamp(0.85rem, 1.2vw, 1.6rem)";
  if (len > 35) return "clamp(0.9rem, 1.3vw, 1.7rem)";
  if (len > 30) return "clamp(1.0rem, 1.4vw, 1.8rem)";
  if (len > 25) return "clamp(1.1rem, 1.5vw, 2.0rem)";
  if (len > 22) return "clamp(1.3rem, 1.7vw, 2.3rem)";
  return "clamp(1.6rem, 2.2vw, 2.8rem)";               // Base size
};

// Breadcrumbs: up to 11 answer/clue rows — type + pill height follow viewport (scroll + shrink).
const computeBreadcrumbsPreviewFontSize = (text, pillH = 34) => {
  const len = (String(text || "")).length;
  // First react to physical row height (short viewports / compact windows).
  if (pillH <= 15) return "clamp(0.36rem, 0.5vw, 0.6rem)";
  if (pillH <= 18) return "clamp(0.4rem, 0.52vw, 0.64rem)";
  if (pillH <= 20) return "clamp(0.44rem, 0.56vw, 0.72rem)";
  if (pillH <= 24) return "clamp(0.5rem, 0.66vw, 0.84rem)";
  if (pillH <= 28) return "clamp(0.56rem, 0.76vw, 0.96rem)";
  if (pillH <= 32) return "clamp(0.62rem, 0.86vw, 1.05rem)";
  if (pillH <= 36) return "clamp(0.68rem, 0.96vw, 1.12rem)";
  if (len > 100) return "clamp(0.4rem, 0.5vw, 0.65rem)";
  if (len > 80) return "clamp(0.45rem, 0.55vw, 0.7rem)";
  if (len > 60) return "clamp(0.5rem, 0.65vw, 0.8rem)";
  if (len > 40) return "clamp(0.55rem, 0.75vw, 0.9rem)";
  if (len > 25) return "clamp(0.6rem, 0.9vw, 1.0rem)";
  if (len > 15) return "clamp(0.65rem, 1.0vw, 1.1rem)";
  return "clamp(0.7rem, 1.1vw, 1.15rem)";
};






export default function CsvQuizEdit() {


const [editRowIdx, setEditRowIdx] = useState(null);
const [editDraft, setEditDraft] = useState(null);


// --- Library overlay visibility (must be top-level; not inside conditional JSX) ---
const noQuestion = !String(editDraft?.Q || "").trim();
// When you open a row for editing, auto-show the library if there's no question.
// Keep it in state so user can close it, and it won't re-open until the next edit.
const [showLib, setShowLib] = useState(false);

useEffect(() => {
  // When a new row is opened, decide whether to show the library overlay
  if (editRowIdx !== null) {
    setShowLib(noQuestion);
  } else {
    // closed the modal
    setShowLib(false);
  }
}, [editRowIdx, noQuestion]);


const {
  qlSearch, setQlSearch,
  qlLoading,
  qlItems,
  qlFiltered,
  loadQuestionLibraryOnce
} = useQuestionLibrary(editRowIdx, editDraft);


  
// These CAT types are visible but NOT selectable
const DISABLED_CAT_SET = new Set(["BUZZER", "WHOAMI", "JOIN QUIZ", "INTRODUCTION", "END QUIZ"]);

// Ownership gating
const [ownerUid, setOwnerUid] = useState(null);
const [readOnly, setReadOnly] = useState(true); // default: disable Save until we know ownership

const [readOnlyNoticeShown, setReadOnlyNoticeShown] = useState(false);
// NEW: don't show warning until ownership is resolved
const [ownershipChecked, setOwnershipChecked] = useState(false);

// Auth: track signed-in user uid (must be above effects that depend on it)
const [currentUid, setCurrentUid] = useState(auth.currentUser?.uid || null);
useEffect(() => {
  const unsub = onAuthStateChanged(auth, (u) => setCurrentUid(u?.uid || null));
  return () => unsub();
}, []);

// Hashtag preview state
const [hashtagGenre, setHashtagGenre] = useState(null);
const [hashtagTags, setHashtagTags] = useState([]);
const [previewQuestions, setPreviewQuestions] = useState([]);
const [previewQuestionIndex, setPreviewQuestionIndex] = useState(0);
const [previewLoading, setPreviewLoading] = useState(false);
const [previewReadyCount, setPreviewReadyCount] = useState(0);
const [previewFlaggedCount, setPreviewFlaggedCount] = useState(0);
// The host's own venues (Account → Your venues) plus Test Venue.
const { venues: hostVenues } = useHostVenues();
const venueOptions = [...hostVenues, "Test Venue"];
const [selectedVenue, setSelectedVenue] = useState(() => localStorage.getItem("opt_venue") || "");

// Genre dropdown state (triggered by typing #)
const [genreDropdown, setGenreDropdown] = useState({ show: false, rowIdx: null, position: null });
const genreInputRefs = React.useRef({}); // Store input refs by row index
const genreOptions = [
  "Art",
  "Famous People",
  "Food and Drink",
  "Games",
  "Geography",
  "History",
  "Literature",
  "Movie",
  "Music",
  "Pop Culture",
  "Psychology",
  "Pop Culture/Weird",
  "Science",
  "Sport",
  "TV",
  "Weird",
  "Wordplay"
];

// Resolve parsed hashtag genre to canonical value from genreOptions (case- and space-insensitive)
function resolveCanonicalGenre(parsedGenre, options) {
  if (!parsedGenre || !options || !Array.isArray(options)) return parsedGenre || null;
  const trimmed = String(parsedGenre).trim();
  if (!trimmed) return null;
  const exact = options.find((g) => g === trimmed);
  if (exact) return exact;
  const lower = trimmed.toLowerCase();
  const caseMatch = options.find((g) => g.toLowerCase() === lower);
  if (caseMatch) return caseMatch;
  const noSpaces = lower.replace(/\s+/g, "");
  const spaceMatch = options.find((g) => g.replace(/\s+/g, "").toLowerCase() === noSpaces);
  if (spaceMatch) return spaceMatch;
  return trimmed;
}

// True only when the first tag matches a known genre (so we use genre in Firestore query). Otherwise tags-only.
function isKnownGenre(parsedGenre, options) {
  if (!parsedGenre || !options || !Array.isArray(options)) return false;
  const canonical = resolveCanonicalGenre(parsedGenre, options);
  return options.indexOf(canonical) !== -1;
}

// Manual save state for dynamic questions
const originalPreviewDataRef = useRef(null);
const [hasChanges, setHasChanges] = useState(false);
const [showRatingSlider, setShowRatingSlider] = useState(false);

// CSV changes tracking
const [hasCsvChanges, setHasCsvChanges] = useState(false);
const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
const [pendingNavigation, setPendingNavigation] = useState(null);
const originalRowsRef = useRef(null);
const originalFilenameRef = useRef(null);

// Max characters allowed for any answer text.
// Matches: "He invented it while working on a completely different project." (63)
const MAX_ANSWER_CHARS = 60;
const MAX_QUESTION_CHARS = 200;

// --- search state + row refs ---

// --- editable hero question refs (prevent caret jump) ---
const questionEditableRef = useRef(null);
const questionInitRef = useRef(false);
const accuracyFlagRepairRef = useRef(new Set());



// Scroll container for the grid
const gridWrapRef = useRef(null);

// CSV grid: per-column pixel widths (drag header edges to resize)
const [csvColumnWidths, setCsvColumnWidths] = useState({});
const csvColumnWidthsRef = useRef(csvColumnWidths);
csvColumnWidthsRef.current = csvColumnWidths;

const onCsvColResizeMouseDown = useCallback((columnKey, e) => {
  e.preventDefault();
  e.stopPropagation();
  const startX = e.clientX;
  const startW = csvColumnWidthsRef.current[columnKey] ?? defaultCsvColumnWidth(columnKey);
  const minW = columnKey === "Q" ? 140 : 80;
  const maxW = columnKey === "Q" ? 1400 : 560;

  const onMove = (ev) => {
    ev.preventDefault();
    const dx = ev.clientX - startX;
    const nw = Math.round(Math.min(maxW, Math.max(minW, startW + dx)));
    setCsvColumnWidths((prev) => ({ ...prev, [columnKey]: nw }));
  };
  const onUp = () => {
    document.removeEventListener("mousemove", onMove);
    document.removeEventListener("mouseup", onUp);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  };
  document.addEventListener("mousemove", onMove);
  document.addEventListener("mouseup", onUp);
  document.body.style.cursor = "col-resize";
  document.body.style.userSelect = "none";
}, []);

// Cinematic intro (big centered question visible)
const [showIntro, setShowIntro] = React.useState(false);  // hero on when true
const [introDone, setIntroDone] = React.useState(false);  // when true, render answers
const [answersStagger, setAnswersStagger] = React.useState(false);
const [animKey, setAnimKey] = React.useState(0);
const rafRef = React.useRef({ r1: 0, r2: 0 });
const tRef = React.useRef({ hold: 0, clear: 0, rulesUp: 0, rulesDown: 0 });

const lastQuestionRef = React.useRef("");

// NEW: rules card visibility control (independent of hero)
const [rulesCardVisible, setRulesCardVisible] = React.useState(false);



  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [filename, setFilename] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [stats, setStats] = useState([]);
const [bgUrl, setBgUrl] = useState("");
// Hide filename columns plus VIDEO_URL; keep P1_URL/SOUND_URL/BACKGROUND_URL visible
const HIDDEN_COLUMNS = [
  "P1", "SOUND", "VIDEO", "BACKGROUND", "P2", "P3", "P4", "P5", "P6", "STYLE",
  "VIDEO_URL", "visibleExplanation", "SPOTIFY_URL", "flag", "JIGSAW"
];

useEffect(() => {
  const vis = headers.filter((h) => !HIDDEN_COLUMNS.includes(h));
  setCsvColumnWidths((prev) => {
    const next = { ...prev };
    vis.forEach((h) => {
      if (next[h] === undefined) next[h] = defaultCsvColumnWidth(h);
    });
    Object.keys(next).forEach((k) => {
      if (!vis.includes(k)) delete next[k];
    });
    return next;
  });
}, [headers]);

const filteredHeaders = headers.filter(h => !HIDDEN_COLUMNS.includes(h));
const fileInputRef = useRef();  // to trigger hidden file upload input
const mp3UploadRef = useRef();  // for MP3 uploads in preview popup
const [mediaTarget, setMediaTarget] = useState({ row: null, col: null }); // store clicked cell
const [uploadingMedia, setUploadingMedia] = useState({ row: null, col: null });
const [mediaMappingMsg, setMediaMappingMsg] = useState("");
const [mediaMappingCount, setMediaMappingCount] = useState(0);

const [previewImg, setPreviewImg] = useState(null); // holds image URL or null
const [audioMeta, setAudioMeta] = useState({ duration: null }); 
const [editingMediaCell, setEditingMediaCell] = useState(null);

// Row edit modal state







const rowRefs = useRef([]);
const [searchTerm, setSearchTerm] = useState("");
const [lastFoundIndex, setLastFoundIndex] = useState(-1);
const [highlightedRow, setHighlightedRow] = useState(null);
const [showToggleAllFlagsModal, setShowToggleAllFlagsModal] = useState(false);

// --- drag autoscroll state/refs ---
const [isDragging, setIsDragging] = useState(false);
const lastMouseYRef = useRef(null);
const scrollRAF = useRef(0);
// --- helpers: type sniffing for media (file + url) ---
const VIDEO_EXTS = new Set(["mp4","webm","mov","m4v","avi","wmv","mkv"]);
const IMAGE_EXTS = new Set(["jpg","jpeg","png","gif","bmp","webp","svg"]);


// ---- Upload limits (tweak if you like) ----
const LIMITS = {
  imageBytes: 2 * 1024 * 1024, // 2 MB
  audioSecs: 4 * 60,           // 4 minutes
  videoSecs: 2 * 60            // 2 minutes
};

// Quick type guards
const isImageFile = (f) => f?.type?.startsWith?.("image/");
const isAudioFile = (f) => f?.type?.startsWith?.("audio/");
const isVideoFile = (f) => f?.type?.startsWith?.("video/");

// ---- Filename cleaning ----
// Example: `Back to the Future Trivia (3).png`
//   -> `Back_to_the_Future_Trivia_3_media_1752042839479.png`
function sanitiseForStorage(originalName, prefix = "media") {
  const raw = String(originalName || "file");
  // Pull ext from the rightmost dot (falls back to mime if needed later)
  const m = /\.([a-zA-Z0-9]+)$/.exec(raw);
  const ext = m ? m[1].toLowerCase() : "";
  const basename = m ? raw.slice(0, -m[0].length) : raw;

  // Normalize to ASCII, strip quotes/brackets, and compress punctuation to "_"
  const ascii = basename.normalize("NFKD").replace(/[^\x00-\x7F]/g, "");
  let safe = ascii
    .replace(/[()"'\[\]{}<>]/g, "")   // remove problematic brackets/quotes
    .replace(/[^a-zA-Z0-9._-]+/g, "_") // everything else -> underscore
    .replace(/_+/g, "_")               // collapse runs of _
    .replace(/^_+|_+$/g, "");          // trim _ at ends

  if (!safe) safe = "file";
  const unique = Date.now();           // same pattern you mentioned: "game_1752…"
  return `${safe}_${prefix}_${unique}${ext ? "." + ext : ""}`;
}

// ---- Duration read for audio/video ----
function getMediaDurationSec(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement(isAudioFile(file) ? "audio" : "video");
    el.preload = "metadata";
    el.src = url;
    const done = () => { try { URL.revokeObjectURL(url); } catch {} };
    el.onloadedmetadata = () => {
      const d = Number.isFinite(el.duration) ? el.duration : NaN;
      done();
      resolve(d);
    };
    el.onerror = () => { done(); resolve(NaN); };
  });
}

// ---- One gatekeeper to enforce per-column limits ----
async function enforceUploadLimitsAndType(file, columnName, alertFn) {
  // Column intent (P1 can be image OR video; BACKGROUND must be image; SOUND is audio; VIDEO is video)
  const col = String(columnName || "").toUpperCase();

  // Reject obvious mismatches (defensive; "accept" attr usually prevents these)
  if (col.startsWith("BACKGROUND") && !isImageFile(file)) {
    alertFn("This cell only accepts images (JPG/PNG/WebP/SVG).");
    return false;
  }
  if (col.startsWith("SOUND") && !isAudioFile(file)) {
    alertFn("This cell only accepts audio (MP3/WAV/OGG/AAC/M4A/FLAC).");
    return false;
  }
  if (col.startsWith("VIDEO") && !isVideoFile(file)) {
    alertFn("This cell only accepts video (MP4/WEBM/MOV/M4V/AVI/WMV/MKV).");
    return false;
  }
  // P1 (MEDIA) can be image or video; disallow audio in MEDIA on purpose
  if (col.startsWith("P1") && isAudioFile(file)) {
    alertFn("Media (P1) doesn’t accept audio files. Use the AUDIO column instead.");
    return false;
  }

  // Size limit for images
  if (isImageFile(file) && file.size > LIMITS.imageBytes) {
    const mb = (LIMITS.imageBytes / (1024 * 1024)).toFixed(0);
    alertFn(`This image is too large. Max size is ${mb} MB.`);
    return false;
  }

  // Duration limits (audio/video only)
  if (isAudioFile(file) || isVideoFile(file)) {
    const dur = await getMediaDurationSec(file);
    if (!Number.isFinite(dur)) return true; // can’t measure; let it pass
    if (isAudioFile(file) && dur > LIMITS.audioSecs) {
      alertFn(`Audio is too long. Max length is ${Math.floor(LIMITS.audioSecs / 60)}:${String(LIMITS.audioSecs % 60).padStart(2, "0")} (you selected ${Math.floor(dur/60)}:${String(Math.round(dur%60)).padStart(2,"0")}).`);
      return false;
    }
    if (isVideoFile(file) && dur > LIMITS.videoSecs) {
      alertFn(`Video is too long. Max length is ${Math.floor(LIMITS.videoSecs / 60)}:${String(LIMITS.videoSecs % 60).padStart(2, "0")} (you selected ${Math.floor(dur/60)}:${String(Math.round(dur%60)).padStart(2,"0")}).`);
      return false;
    }
  }

  return true;
}





// --- dynamic fit for the hero editable question ---
const heroBoxRef = useRef(null);
const [heroFontSize, setHeroFontSize] = useState(48); // initial, will be auto-fit

// Preview: one height for every pill so they grow/shrink together
const [pillHeight, setPillHeight] = useState(74);
const previewWrapRef = useRef(null); // attach to the main ".quiz-bg" container
const previewFooterRef = useRef(null); // footer block (visible explanation + notes + audio controls)
/* Breadcrumbs: top of the answers column; used with footer rect to get true available height */
const previewBreadcrumbsAreaRef = useRef(null);
/* Breadcrumbs: scrollable region (footer does not cover clues) */
const previewBreadcrumbsScrollRef = useRef(null);

// Popup throttle for answer-length limit
const answerLimitWarnedRef = useRef(false);

// Branded limit popup (centred modal)
const [limitModal, setLimitModal] = React.useState({ open: false, message: "" });

// Branded confirmation modal (same look as Bob popup)
const [confirmModal, setConfirmModal] = React.useState({
  open: false,
  message: "",
  onConfirm: null
});

// Generic "alert" style popup
const openBobAlert = React.useCallback((message) => {
  setLimitModal({ open: true, message });
}, []);
const closeBobAlert = React.useCallback(() => {
  setLimitModal({ open: false, message: "" });
}, []);

// Generic "confirm" style popup
const openBobConfirm = React.useCallback((message, onConfirm) => {
  setConfirmModal({ open: true, message, onConfirm });
}, []);
const closeBobConfirm = React.useCallback(() => {
  setConfirmModal({ open: false, message: "", onConfirm: null });
}, []);




// Limit any answer string to MAX_ANSWER_CHARS and show a one-time alert when exceeded.
// Limit any answer string to MAX_ANSWER_CHARS and show a one-time alert when exceeded.
const limitAnswer = React.useCallback((s) => {
  const str = String(s ?? "");
  if (str.length <= MAX_ANSWER_CHARS) return [str, false];
  openBobAlert(`The answer limit is ${MAX_ANSWER_CHARS} characters.`);
  return [str.slice(0, MAX_ANSWER_CHARS), true];
}, [openBobAlert]);

// Warn-and-block when an edit would exceed MAX_ANSWER_CHARS.
// Works for typing & paste, even when maxLength is set.
// Warn-and-block when an edit would exceed MAX_ANSWER_CHARS.
// Works for typing & paste, even when maxLength is set.
const warnAndBlockIfOver = React.useCallback((e, currentVal, colNameOrKey, dataOverride) => {
  if (!/^A[1-6]$/.test(String(colNameOrKey || ""))) return;

  const el = e.currentTarget;
  const data = dataOverride ?? e.data ?? "";
  const selStart = el.selectionStart ?? String(currentVal).length;
  const selEnd   = el.selectionEnd   ?? String(currentVal).length;
  const replacing = Math.max(0, selEnd - selStart);

  const proposedLength = String(currentVal).length - replacing + String(data).length;
  if (proposedLength > MAX_ANSWER_CHARS) {
    try { e.preventDefault(); } catch {}
    openBobAlert(`Answer limit is ${MAX_ANSWER_CHARS} characters.`);
  }
}, [openBobAlert]);



function fitHeroText() {
  const box = heroBoxRef.current;
  const el  = questionEditableRef.current;
  if (!box || !el) return;

  // Bounds we must fit into
  const maxW = box.clientWidth;
  const maxH = box.clientHeight;

  // Start from current size (or our last chosen size) and shrink
  const style = window.getComputedStyle(el);
  const startPx = parseFloat(style.fontSize) || heroFontSize || 48;

  // Limits and step
  const MIN = 14;       // do not go smaller than this
  const STEP = 1.5;     // shrink step in px

  let s = Math.min(startPx, 300); // clamp a sane upper bound
  el.style.fontSize = s + "px";

  // Try current → then shrink until both width & height fit
  // (scroll* measures include overflow)
  while (s > MIN && (el.scrollWidth > maxW || el.scrollHeight > maxH)) {
    s -= STEP;
    el.style.fontSize = s + "px";
  }

  setHeroFontSize(Math.max(MIN, s));
}




function gotoPrevRow() {
  // Save current edits before navigating
  saveEdit();
  
  setEditRowIdx((idx) => {
    const nextIdx = Math.max(0, (idx ?? 0) - 1);
    // load that row into the editor
    setEditDraft((d) => ({ ...(rows[nextIdx] || {} ) }));
    return nextIdx;
  });
}

function gotoNextRow() {
  // Save current edits before navigating
  saveEdit();
  
  setEditRowIdx((idx) => {
    const last = rows.length - 1;
    const nextIdx = Math.min(last, (idx ?? 0) + 1);
    setEditDraft((d) => ({ ...(rows[nextIdx] || {} ) }));
    return nextIdx;
  });
}


function getExtFromNameOrUrl(s = "") {
  const clean = String(s).split("?")[0].split("#")[0];
  const m = /\.([a-z0-9]+)$/i.exec(clean);
  return m ? m[1].toLowerCase() : "";
}
function looksLikeVideoFile(file) {
  if (file?.type?.startsWith?.("video/")) return true;
  return VIDEO_EXTS.has(getExtFromNameOrUrl(file?.name || ""));
}
function urlLooksVideo(url) {
  return VIDEO_EXTS.has(getExtFromNameOrUrl(url));
}

function formatDuration(total) {
  if (!Number.isFinite(total) || total < 0) return "";
  const s = Math.floor(total % 60);
  const m = Math.floor((total / 60) % 60);
  const h = Math.floor(total / 3600);
  if (h > 0) return `${h}:${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`;
  return `${m}:${String(s).padStart(2,"0")}`;
}



// Try to pull a clean filename from a Firebase Storage URL (or any URL)
function fileNameFromUrl(url = "") {
  try {
    // For Firebase storage: /o/<encoded-path>?...
    const m = /\/o\/([^?]+)/.exec(String(url));
    if (m) {
      const decoded = decodeURIComponent(m[1]); // e.g. "media/Poster.png"
      const parts = decoded.split("/");
      return parts[parts.length - 1] || "";
    }
    // Fallback: last path segment
    const u = new URL(String(url));
    const last = u.pathname.split("/").pop() || "";
    return decodeURIComponent(last);
  } catch {
    return "";
  }
}



function normaliseMediaName(name) {
  const raw = String(name || "").trim().replace(/^["']|["']$/g, "");
  const base = raw.replace(
    /^(.+?\.(mp3|m4a|wav|aac|ogg|flac|mp4|m4v|mov|wmv|webm|mkv|jpg|jpeg|png|gif|webp|bmp|svg)).*$/i,
    "$1"
  );
  if (!base) return [];
  const withSpaces = base;
  const withUnderscores = base.replace(/\s+/g, "_");
  return withUnderscores === withSpaces ? [withSpaces] : [withUnderscores, withSpaces];
}


useEffect(() => {
  let lateTimer = 0;

  const recalc = () => {
    if (editRowIdx === null) return;
    const count = getVisibleAnswersCount(editDraft || {});
    const isBc = isBreadcrumbsCategory(editDraft?.CAT);

    const doMeasure = () => {
      const container = previewWrapRef.current;
      if (!container) return;
      const fr = previewFooterRef.current;
      const ar = previewBreadcrumbsAreaRef.current; // the <ol> for BREADCRUMBS
      const sc = previewBreadcrumbsScrollRef.current;
      if (isBc && sc && ar) {
        const hScroll = calcBreadcrumbsPillHeightFromScrollRegion({
          scrollEl: sc,
          olEl: ar,
          count
        });
        setPillHeight(hScroll);
        return;
      }
      if (isBc && ar && fr) {
        const hLayout = calcBreadcrumbsPillHeightFromLayout({
          areaEl: ar,
          footerEl: fr,
          containerEl: container,
          count,
          gapPx: 4
        });
        setPillHeight(hLayout);
        return;
      }
      if (isBc) {
        setPillHeight(20);
        return;
      }
      const footerHeight = fr?.offsetHeight || 0;
      const dynamicFooterReserve = footerHeight > 0
        ? footerHeight + 22
        : 122;
      const h = calcUniformPillHeight({
        containerEl: container,
        heroEl: heroBoxRef.current,
        count,
        notesReserve: dynamicFooterReserve,
        topChrome: 86,
        gaps: 22,
        minH: 54,
        maxH: 110,
        noMediaMaxH: 88
      });
      setPillHeight(h);
    };

    if (lateTimer) window.clearTimeout(lateTimer);
    requestAnimationFrame(() => {
      requestAnimationFrame(doMeasure);
    });
    lateTimer = window.setTimeout(doMeasure, 200);
  };

  // Debounce the expensive calculation to avoid running on every keystroke
  let timeoutId;
  const debouncedRecalc = () => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(recalc, 150); // Wait 150ms after last change
  };

  // Run on mount and when editRowIdx changes
  recalc();

  // Debounce when editDraft fields change (user typing)
  debouncedRecalc();

  const ro = new ResizeObserver(recalc);
  if (previewWrapRef.current) ro.observe(previewWrapRef.current);
  if (heroBoxRef.current) ro.observe(heroBoxRef.current);
  if (previewFooterRef.current) ro.observe(previewFooterRef.current);
  if (previewBreadcrumbsAreaRef.current) ro.observe(previewBreadcrumbsAreaRef.current);
  if (previewBreadcrumbsScrollRef.current) ro.observe(previewBreadcrumbsScrollRef.current);
  window.addEventListener("resize", recalc);

  return () => {
    clearTimeout(timeoutId);
    if (lateTimer) window.clearTimeout(lateTimer);
    try { ro.disconnect(); } catch {}
    window.removeEventListener("resize", recalc);
  };
// Important: don't include A1..A11 here; that forces expensive layout work on every keypress
// and makes typing in the answer/clue inputs feel laggy.
}, [editDraft?.CAT, editDraft?.NOTES, editDraft?.visibleExplanation, editDraft?.SPOTIFY_URL, editDraft?.SOUND_URL, editRowIdx]);





// Re-fit on content changes (typed text, row swap)
useEffect(() => {
  // delay a frame to allow DOM to lay out first
  const id = requestAnimationFrame(fitHeroText);
  return () => cancelAnimationFrame(id);
}, [editDraft?.Q, editRowIdx]);

// Re-fit on resize
useEffect(() => {
  const ro = new ResizeObserver(() => fitHeroText());
  if (heroBoxRef.current) ro.observe(heroBoxRef.current);
  // also observe the editable itself in case line wraps change
  if (questionEditableRef.current) ro.observe(questionEditableRef.current);
  window.addEventListener("resize", fitHeroText);
  return () => {
    try { ro.disconnect(); } catch {}
    window.removeEventListener("resize", fitHeroText);
  };
}, []);

// Removed live sync of editDraft to rows on every keystroke - this was causing performance issues
// Changes are now synced only when closing the editor (in closeEdit function)
// This prevents expensive setRows operations on every keystroke



// Keep the contentEditable text in sync with editDraft.Q WITHOUT resetting the caret
useEffect(() => {
  if (!editDraft || !questionEditableRef.current) return;
  
  const el = questionEditableRef.current;
  const isUserTyping = document.activeElement === el;
  
  // If we're in preview mode, use preview question text
  const currentPreviewQuestion = hashtagGenre && previewQuestions.length > 0 && previewQuestions[previewQuestionIndex] 
    ? previewQuestions[previewQuestionIndex] 
    : null;
  
  const raw = currentPreviewQuestion 
    ? (currentPreviewQuestion.question || "")
    : (editDraft.Q || "");
  const cleaned = cleanQuestionText(raw);

  // If user is typing and the question hasn't changed, don't interfere
  if (isUserTyping && cleaned === lastQuestionRef.current) return;

  // Normalize current text for comparison
  const current = (el.innerText || "").replace(/\r/g, "");

  if (current !== cleaned) {
    el.innerText = cleaned;
    // Move cursor to end only when syncing external updates (user not typing)
    const sel = window.getSelection && window.getSelection();
    if (sel) {
      const r = document.createRange();
      r.selectNodeContents(el);
      r.collapse(false); // end
      sel.removeAllRanges();
      sel.addRange(r);
    }
  }

  // Track the last question we synced so we can detect real changes while typing
  lastQuestionRef.current = cleaned;
}, [editDraft?.Q, editRowIdx, hashtagGenre, previewQuestions, previewQuestionIndex]);

// Fetch questions for hashtag preview (using useCallback to avoid dependency issues)
// When genre is null, query by questionType only and filter by all tags (tags-only mode).
const fetchHashtagQuestions = useCallback(async (genre, category, venue, tags) => {
  console.log("[fetchHashtagQuestions] Called with:", { genre, category, venue, tags });
  if (!category) {
    console.log("[fetchHashtagQuestions] Missing category, returning empty");
    setPreviewQuestions([]);
    return;
  }
  if (!genre && (!tags || (Array.isArray(tags) ? tags.length === 0 : !parseTagsFromString(tags).length))) {
    console.log("[fetchHashtagQuestions] When genre is empty, at least one tag is required");
    setPreviewQuestions([]);
    return;
  }

  setPreviewLoading(true);
  let totalDocsFetched = 0;
  let totalFilteredByVenue = 0;
  let totalFilteredByTag = 0;
  const requestedTags = Array.isArray(tags)
    ? tags.flatMap((entry) => parseTagsFromString(entry))
    : parseTagsFromString(tags);
  const normalizedRequestedTags = [
    ...new Set(requestedTags.map(normalizeTagValue).filter(Boolean)),
  ];
  const useGenreInQuery = Boolean(genre);

  try {
    const BATCH_SIZE = 20;
    // Genre path: bounded batches (indexed query). Tags-only: scan more batches by interestRating —
    // a single unordered limit(100) grabbed arbitrary docs and missed most tag matches vs Library Viewer.
    const MAX_BATCHES_GENRE = 5;
    const MAX_BATCHES_TAGS_ONLY = 15;
    const maxBatches = useGenreInQuery ? MAX_BATCHES_GENRE : MAX_BATCHES_TAGS_ONLY;
    let lastVisibleDoc = null;
    const readyQuestions = [];
    let flaggedCount = 0;

    // Count flagged questions
    try {
      const flaggedQuery = useGenreInQuery
        ? query(
            collection(db, "QuestionLibrary"),
            where("genre", "==", genre),
            where("questionType", "==", category)
          )
        : query(
            collection(db, "QuestionLibrary"),
            where("questionType", "==", category)
          );
      console.log("[fetchHashtagQuestions] Counting flagged with query:", useGenreInQuery ? { genre, category } : { category });
      const flaggedSnap = await getDocs(flaggedQuery);
      flaggedSnap.forEach((doc) => {
        const data = doc.data();
        if (String(data.accuracyFlag || "").trim()) flaggedCount++;
      });
      console.log("[fetchHashtagQuestions] Flagged count:", flaggedCount);
    } catch (err) {
      console.warn("Error counting flagged questions:", err);
    }

    if (useGenreInQuery) {
      // Genre-based query (existing index: genre + questionType + accuracyFlag + interestRating)
      for (let batchNum = 0; batchNum < maxBatches; batchNum++) {
        let libraryQuery = query(
          collection(db, "QuestionLibrary"),
          where("genre", "==", genre),
          where("questionType", "==", category),
          where("accuracyFlag", "==", ""),
          orderBy("interestRating", "desc"),
          limit(BATCH_SIZE)
        );
        if (lastVisibleDoc) libraryQuery = query(libraryQuery, startAfter(lastVisibleDoc));
        const librarySnap = await getDocs(libraryQuery);
        const docsInBatch = librarySnap.docs;
        totalDocsFetched += docsInBatch.length;
        if (docsInBatch.length === 0) break;
        lastVisibleDoc = docsInBatch[docsInBatch.length - 1];

        for (const docSnap of docsInBatch) {
          const candidateData = docSnap.data();
          const candidateId = docSnap.id;
          const usageHistory = candidateData.usageHistory || [];
          const candidateTags = normalizeTagList(candidateData.tags);
          const tagMatches = normalizedRequestedTags.length === 0
            || normalizedRequestedTags.every((r) => candidateTags.includes(r));
          const usedAtVenue = venue && venue !== "Test Venue" && usedAtVenueRecently(usageHistory, venue);
          if (!tagMatches) { totalFilteredByTag++; continue; }
          if (usedAtVenue) { totalFilteredByVenue++; continue; }
          readyQuestions.push({ id: candidateId, ...candidateData });
        }
        if (docsInBatch.length < BATCH_SIZE) break;
      }
    } else {
      // Tags-only: prefer paginating by interestRating (needs composite index). If the index
      // is not deployed yet, Firestore throws failed-precondition — fall back to unordered fetch.
      const pushIfTagsOnlyMatch = (docSnap) => {
        const candidateData = docSnap.data();
        const candidateId = docSnap.id;
        const usageHistory = candidateData.usageHistory || [];
        const candidateTags = normalizeTagList(candidateData.tags);
        const tagMatches = normalizedRequestedTags.length > 0
          && normalizedRequestedTags.every((r) => candidateTags.includes(r));
        const usedAtVenue = venue && venue !== "Test Venue" && usedAtVenueRecently(usageHistory, venue);
        if (!tagMatches) { totalFilteredByTag++; return; }
        if (usedAtVenue) { totalFilteredByVenue++; return; }
        readyQuestions.push({ id: candidateId, ...candidateData });
      };

      try {
        lastVisibleDoc = null;
        for (let batchNum = 0; batchNum < maxBatches; batchNum++) {
          let libraryQuery = query(
            collection(db, "QuestionLibrary"),
            where("questionType", "==", category),
            where("accuracyFlag", "==", ""),
            orderBy("interestRating", "desc"),
            limit(BATCH_SIZE)
          );
          if (lastVisibleDoc) libraryQuery = query(libraryQuery, startAfter(lastVisibleDoc));
          const librarySnap = await getDocs(libraryQuery);
          const docsInBatch = librarySnap.docs;
          totalDocsFetched += docsInBatch.length;
          if (docsInBatch.length === 0) break;
          lastVisibleDoc = docsInBatch[docsInBatch.length - 1];

          docsInBatch.forEach((docSnap) => pushIfTagsOnlyMatch(docSnap));
          if (docsInBatch.length < BATCH_SIZE) break;
        }
      } catch (tagsOnlyErr) {
        if (tagsOnlyErr?.code === "failed-precondition") {
          console.warn(
            "[fetchHashtagQuestions] Tags-only indexed query failed (index may be missing); using legacy unordered fetch.",
            tagsOnlyErr
          );
          const TAGS_ONLY_LEGACY_LIMIT = 500;
          const legacyQuery = query(
            collection(db, "QuestionLibrary"),
            where("questionType", "==", category),
            where("accuracyFlag", "==", ""),
            limit(TAGS_ONLY_LEGACY_LIMIT)
          );
          const legacySnap = await getDocs(legacyQuery);
          totalDocsFetched = legacySnap.docs.length;
          legacySnap.docs.forEach((docSnap) => pushIfTagsOnlyMatch(docSnap));
        } else {
          throw tagsOnlyErr;
        }
      }
    }

    if (readyQuestions.length === 0) {
      const repairKey = useGenreInQuery ? `${genre}::${category}` : `noGenre::${category}`;
      if (!accuracyFlagRepairRef.current.has(repairKey)) {
        accuracyFlagRepairRef.current.add(repairKey);
        try {
          const scanQuery = useGenreInQuery
            ? query(
                collection(db, "QuestionLibrary"),
                where("genre", "==", genre),
                where("questionType", "==", category),
                orderBy("interestRating", "desc"),
                limit(50)
              )
            : query(
                collection(db, "QuestionLibrary"),
                where("questionType", "==", category),
                limit(50)
              );
          const scanSnap = await getDocs(scanQuery);
          if (!scanSnap.empty) {
            const batch = writeBatch(db);
            let updated = 0;
            scanSnap.docs.forEach((docSnap) => {
              const data = docSnap.data();
              const accuracyFlag = data.accuracyFlag;
              const trimmed = typeof accuracyFlag === "string" ? accuracyFlag.trim() : "";
              if (accuracyFlag == null || (typeof accuracyFlag === "string" && trimmed === "")) {
                if (accuracyFlag !== "") {
                  batch.update(docSnap.ref, { accuracyFlag: "" });
                  updated += 1;
                }
              }
            });
            if (updated > 0) {
              await batch.commit();
              await fetchHashtagQuestions(genre, category, venue, tags);
              return;
            }
          }
        } catch (err) {
          console.warn("[fetchHashtagQuestions] Error normalizing accuracyFlag:", err);
        }
      }
    }

    console.log(`[fetchHashtagQuestions] Summary: Fetched ${totalDocsFetched} docs, filtered ${totalFilteredByVenue} by venue, ${totalFilteredByTag} by tag, ${readyQuestions.length} ready`);

    // Sort questions: first by interestRating desc, then by question text for consistent ordering
    readyQuestions.sort((a, b) => {
      const ratingA = Number(a.interestRating || 0);
      const ratingB = Number(b.interestRating || 0);
      if (ratingB !== ratingA) {
        return ratingB - ratingA; // Higher rating first
      }
      // Secondary sort by question text for consistent ordering when ratings are equal
      const questionA = String(a.question || "").trim();
      const questionB = String(b.question || "").trim();
      return questionA.localeCompare(questionB);
    });
    
    console.log("[fetchHashtagQuestions] Loaded", readyQuestions.length, "ready questions. First question:", {
      id: readyQuestions[0]?.id,
      question: readyQuestions[0]?.question?.substring(0, 50),
      rating: readyQuestions[0]?.interestRating,
      flagged: readyQuestions[0]?.accuracyFlag ? "YES" : "NO"
    });
    
    setPreviewQuestions(readyQuestions);
    setPreviewReadyCount(readyQuestions.length);
    setPreviewFlaggedCount(flaggedCount);
    setPreviewQuestionIndex(0);
    
    // Store original data for the first question when loaded (using editDraft format: Q, A1, etc.)
    if (readyQuestions.length > 0 && readyQuestions[0]) {
      originalPreviewDataRef.current = {
        question: readyQuestions[0].question || "",
        answer: readyQuestions[0].answer || "",
        A2: readyQuestions[0].A2 || "",
        A3: readyQuestions[0].A3 || "",
        A4: readyQuestions[0].A4 || "",
        A5: readyQuestions[0].A5 || "",
        A6: readyQuestions[0].A6 || "",
        A7: readyQuestions[0].A7 || "",
        A8: readyQuestions[0].A8 || "",
        A9: readyQuestions[0].A9 || "",
        A10: readyQuestions[0].A10 || "",
        A11: readyQuestions[0].A11 || "",
        notes: readyQuestions[0].notes || "",
        accuracyFlag: readyQuestions[0].accuracyFlag || "",
        interestRating: readyQuestions[0].interestRating || 0
      };
    }
  } catch (error) {
    console.error("[fetchHashtagQuestions] ERROR:", error);
    console.error("[fetchHashtagQuestions] Error code:", error.code);
    console.error("[fetchHashtagQuestions] Error message:", error.message);
    console.error("[fetchHashtagQuestions] Query params were:", { genre, category, venue, tags });
    if (error.code === 'failed-precondition') {
      console.error(
        "[fetchHashtagQuestions] MISSING FIRESTORE INDEX! Needed for hashtag preview: " +
          "(genre + questionType + accuracyFlag + interestRating) and/or " +
          "(questionType + accuracyFlag + interestRating). Deploy `firestore.indexes.json` or use the index URL from the error."
      );
    }
    setPreviewQuestions([]);
    setPreviewReadyCount(0);
    setPreviewFlaggedCount(0);
  } finally {
    setPreviewLoading(false);
  }
}, []);

// Show modal when no questions are found for hashtag (only once per combination)
const noQuestionsShownRef = React.useRef(null);
useEffect(() => {
  if (hashtagGenre && !previewLoading && previewQuestions.length === 0 && editDraft?.CAT) {
    const category = String(editDraft.CAT || "").trim();
    const tagKey = (hashtagTags || []).join(",");
    const key = `${hashtagGenre}-${tagKey}-${category}`;
    // Only show modal if we haven't shown it for this hashtag/category combination yet
    if (noQuestionsShownRef.current !== key) {
      noQuestionsShownRef.current = key;
      const extraTags = (hashtagTags || []).filter(
        (tag) => normalizeTagValue(tag) !== normalizeTagValue(hashtagGenre)
      );
      const tagSuffix = extraTags.length > 0
        ? ` ${extraTags.map((tag) => `#${tag}`).join(" ")}`
        : "";
      openBobAlert(`Warning, no questions available matching this hashtag (#${hashtagGenre}${tagSuffix}) and question type (${category}).`);
    }
  } else if (!hashtagGenre || previewQuestions.length > 0) {
    // Reset the ref when hashtag changes or questions are found
    noQuestionsShownRef.current = null;
  }
}, [hashtagGenre, hashtagTags, previewLoading, previewQuestions.length, editDraft?.CAT, openBobAlert]);

// Close genre dropdown when clicking outside or pressing Escape
useEffect(() => {
  if (!genreDropdown.show) return;

  const handleClickOutside = (e) => {
    const dropdownEl = document.querySelector('[data-genre-dropdown]');
    const inputRef = genreInputRefs.current[genreDropdown.rowIdx];
    const isClickInDropdown = dropdownEl && dropdownEl.contains(e.target);
    const isClickInInput = inputRef && inputRef.contains(e.target);
    
    // Close if click is outside both dropdown and input
    if (!isClickInDropdown && !isClickInInput) {
      setGenreDropdown({ show: false, rowIdx: null, position: null });
    }
  };

  const handleEscape = (e) => {
    if (e.key === "Escape") {
      const inputRef = genreInputRefs.current[genreDropdown.rowIdx];
      setGenreDropdown({ show: false, rowIdx: null, position: null });
      if (inputRef) {
        inputRef.focus();
      }
    }
  };

  // Use a slight delay to avoid closing immediately on # key press
  const timeoutId = setTimeout(() => {
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
  }, 100);

  return () => {
    clearTimeout(timeoutId);
    document.removeEventListener("mousedown", handleClickOutside);
    document.removeEventListener("keydown", handleEscape);
  };
}, [genreDropdown.show, genreDropdown.rowIdx]);

// Detect hashtag and fetch preview questions
useEffect(() => {
  if (!editDraft || !editRowIdx) {
    // Only clear hashtag mode if we don't have preview questions loaded
    if (previewQuestions.length === 0) {
      setHashtagGenre(null);
      setHashtagTags([]);
      setPreviewQuestions([]);
    }
    return;
  }

  const questionText = String(editDraft.Q || "").trim();
  if (isHashtagPattern(questionText)) {
    const hashtags = extractHashtagsFromText(questionText);
    const rawGenre = hashtags[0] || null;
    const useGenreInQuery = isKnownGenre(rawGenre, genreOptions);
    const genre = useGenreInQuery ? resolveCanonicalGenre(rawGenre, genreOptions) : null;
    const tags = useGenreInQuery ? hashtags.slice(1) : hashtags; // when no genre, all hashtags are tags
    // Normalize category (uppercase + alias for CSV abbreviations)
    const catUpper = String(editDraft.CAT || "").trim().toUpperCase();
    const alias = { TRUEFALSE: "TRUE/FALSE", MULTICHOI: "MULTICHOICE" };
    const category = alias[catUpper] || catUpper;
    const canFetch = category && (genre || (tags && tags.length > 0));

    if (canFetch) {
      setHashtagGenre(genre || rawGenre); // display first tag when tags-only
      setHashtagTags(tags);
      fetchHashtagQuestions(genre, category, selectedVenue, tags);
    } else {
      // Only clear if no preview questions are loaded
      if (previewQuestions.length === 0) {
        setHashtagGenre(null);
        setHashtagTags([]);
        setPreviewQuestions([]);
      }
    }
  } else {
    // If question text no longer matches hashtag, but we have preview questions loaded,
    // keep hashtag mode active (question was loaded from preview)
    if (previewQuestions.length === 0) {
      setHashtagGenre(null);
      setHashtagTags([]);
      setPreviewQuestions([]);
    }
    // Otherwise, preserve hashtagGenre to prevent CSV sync
  }
}, [editDraft?.Q, editDraft?.CAT, selectedVenue, fetchHashtagQuestions]);

// Manual save function for dynamic questions
const savePreviewQuestionChanges = useCallback(async () => {
  // Only save if we're in hashtag mode with loaded questions
  if (!hashtagGenre || !previewQuestions.length || previewQuestionIndex < 0 || previewQuestionIndex >= previewQuestions.length) {
    console.log("[Save] Cannot save - missing requirements");
    return;
  }
  
  try {
    const currentQuestion = previewQuestions[previewQuestionIndex];
    if (!currentQuestion?.id) {
      console.log("[Save] Cannot save - no question ID");
      return;
    }
    
    // Validate that we have required data before saving
    const questionText = editDraft?.Q || currentQuestion?.question || "";
    const answerText = editDraft?.A1 || currentQuestion?.answer || "";
    
    if (!questionText.trim() || !answerText.trim()) {
      console.warn("[Save] Cannot save - question or answer is empty:", {
        hasQuestion: !!questionText.trim(),
        hasAnswer: !!answerText.trim(),
        questionId: currentQuestion.id
      });
      return;
    }
    
    console.log("[Save] Starting save for question ID:", currentQuestion.id);
    
    // Build updates from current state
    // IMPORTANT: Use currentQuestion from previewQuestions array as the primary source,
    // NOT editDraft, because editDraft may contain CSV data (like "#Music" hashtag)
    // The user's edits are stored in previewQuestions[previewQuestionIndex]
    const previewQuestionData = {
      accuracyFlag: currentQuestion.accuracyFlag || "",
      interestRating: currentQuestion.interestRating || 0
    };
    
    // For dynamic questions, use currentQuestion as PRIMARY source, editDraft only as override for explicitly edited fields
    // This prevents CSV hashtag data from overwriting the actual question data
    const updates = mapEditDraftToLibraryFields(editDraft, previewQuestionData, currentQuestion);
    
    // Final validation: ensure updates contain required fields
    if (!updates.question || !updates.answer) {
      console.warn("[Save] Cannot save - updates missing required fields:", {
        hasQuestion: !!updates.question,
        hasAnswer: !!updates.answer
      });
      return;
    }
    
    console.log("[Save] Updates to save:", updates);
    console.log("[Save] Data sources (for dynamic questions, only currentQuestion is used):", {
      editDraftQ: editDraft?.Q?.substring(0, 50),
      editDraftA1: editDraft?.A1?.substring(0, 30),
      currentQuestionQuestion: currentQuestion?.question?.substring(0, 50),
      currentQuestionAnswer: currentQuestion?.answer?.substring(0, 30),
      savedQuestion: updates.question?.substring(0, 50),
      savedAnswer: updates.answer?.substring(0, 30),
      warning: "For dynamic questions, editDraft is ignored - only currentQuestion (from previewQuestions) is saved"
    });
    
    // Only save if there are actual changes
    if (Object.keys(updates).length > 0) {
      await savePreviewQuestionToLibrary(currentQuestion.id, updates);
      
      // CRITICAL: Update previewQuestions with the SAVED data from Firestore (updates object),
      // NOT from editDraft which may contain CSV hashtag data ("#Sport")
      // The updates object contains the actual question text, answers, etc. that were just saved
      setPreviewQuestions(prev => {
        const updated = [...prev];
        if (updated[previewQuestionIndex]) {
          updated[previewQuestionIndex] = {
            ...updated[previewQuestionIndex],
            // Use saved data from updates (which came from currentQuestion, not editDraft)
            question: updates.question || updated[previewQuestionIndex].question,
            answer: updates.answer || updated[previewQuestionIndex].answer,
            A2: updates.A2 || updated[previewQuestionIndex].A2,
            A3: updates.A3 || updated[previewQuestionIndex].A3,
            A4: updates.A4 || updated[previewQuestionIndex].A4,
            A5: updates.A5 || updated[previewQuestionIndex].A5,
            A6: updates.A6 || updated[previewQuestionIndex].A6,
            A7: updates.A7 || updated[previewQuestionIndex].A7,
            A8: updates.A8 || updated[previewQuestionIndex].A8,
            A9: updates.A9 || updated[previewQuestionIndex].A9,
            A10: updates.A10 || updated[previewQuestionIndex].A10,
            A11: updates.A11 || updated[previewQuestionIndex].A11,
            notes: updates.notes || updated[previewQuestionIndex].notes,
            accuracyFlag: previewQuestionData.accuracyFlag,
            interestRating: previewQuestionData.interestRating
          };
        }
        return updated;
      });
      
      // Update original data after successful save - use the SAVED data, not editDraft
      // This ensures change detection works correctly after save
      // Update the stored original data for this index with the saved values
      if (originalDataByIndexRef.current[previewQuestionIndex]) {
        originalDataByIndexRef.current[previewQuestionIndex] = {
          question: updates.question || originalDataByIndexRef.current[previewQuestionIndex].question,
          answer: updates.answer || originalDataByIndexRef.current[previewQuestionIndex].answer,
          A2: updates.A2 || originalDataByIndexRef.current[previewQuestionIndex].A2,
          A3: updates.A3 || originalDataByIndexRef.current[previewQuestionIndex].A3,
          A4: updates.A4 || originalDataByIndexRef.current[previewQuestionIndex].A4,
          A5: updates.A5 || originalDataByIndexRef.current[previewQuestionIndex].A5,
          A6: updates.A6 || originalDataByIndexRef.current[previewQuestionIndex].A6,
          A7: updates.A7 || originalDataByIndexRef.current[previewQuestionIndex].A7,
          A8: updates.A8 || originalDataByIndexRef.current[previewQuestionIndex].A8,
          A9: updates.A9 || originalDataByIndexRef.current[previewQuestionIndex].A9,
          A10: updates.A10 || originalDataByIndexRef.current[previewQuestionIndex].A10,
          A11: updates.A11 || originalDataByIndexRef.current[previewQuestionIndex].A11,
          notes: updates.notes || originalDataByIndexRef.current[previewQuestionIndex].notes,
          visibleExplanation: updates.visibleExplanation || originalDataByIndexRef.current[previewQuestionIndex].visibleExplanation,
          accuracyFlag: previewQuestionData.accuracyFlag,
          interestRating: previewQuestionData.interestRating
        };
        // Also update the current reference
        originalPreviewDataRef.current = originalDataByIndexRef.current[previewQuestionIndex];
      }
      
      // Update editDraft to match the saved data (so it displays correctly)
      // This prevents editDraft from containing stale CSV hashtag data
      setEditDraft(prev => ({
        ...prev,
        Q: updates.question || prev?.Q || "",
        A1: updates.answer || prev?.A1 || "",
        A2: updates.A2 || prev?.A2 || "",
        A3: updates.A3 || prev?.A3 || "",
        A4: updates.A4 || prev?.A4 || "",
        A5: updates.A5 || prev?.A5 || "",
        A6: updates.A6 || prev?.A6 || "",
        A7: updates.A7 || prev?.A7 || "",
        A8: updates.A8 || prev?.A8 || "",
        A9: updates.A9 || prev?.A9 || "",
        A10: updates.A10 || prev?.A10 || "",
        A11: updates.A11 || prev?.A11 || "",
        NOTES: updates.notes || prev?.NOTES || "",
        visibleExplanation: updates.visibleExplanation || prev?.visibleExplanation || ""
      }));
      
      console.log("[Save] Save completed successfully");
      
      // Clear changes flag
      setHasChanges(false);
    } else {
      console.log("[Save] No updates to save (empty updates object)");
    }
  } catch (error) {
    console.error("[Save] Error saving preview question:", error);
  }
}, [hashtagGenre, previewQuestions, previewQuestionIndex, editDraft]);

// Check if there are unsaved changes
const hasUnsavedChanges = useCallback(() => {
  if (!hashtagGenre || !previewQuestions.length || previewQuestionIndex < 0 || previewQuestionIndex >= previewQuestions.length) {
    return false;
  }
  
  // Get the stored original for this index (from the ref that doesn't get overwritten)
  const original = originalDataByIndexRef.current[previewQuestionIndex] || originalPreviewDataRef.current;
  if (!original) {
    console.log("[hasUnsavedChanges] No original data stored for index", previewQuestionIndex);
    return false;
  }
  
  const currentQuestion = previewQuestions[previewQuestionIndex];
  
  // Compare current state with original (both in editDraft format: Q, A1, etc.)
  const questionChanged = (editDraft?.Q || "") !== (original.question || "");
  const answerChanged = (editDraft?.A1 || "") !== (original.answer || "");
  const a2Changed = (editDraft?.A2 || "") !== (original.A2 || "");
  const a3Changed = (editDraft?.A3 || "") !== (original.A3 || "");
  const a4Changed = (editDraft?.A4 || "") !== (original.A4 || "");
  const a5Changed = (editDraft?.A5 || "") !== (original.A5 || "");
  const a6Changed = (editDraft?.A6 || "") !== (original.A6 || "");
  const a7Changed = (editDraft?.A7 || "") !== (original.A7 || "");
  const a8Changed = (editDraft?.A8 || "") !== (original.A8 || "");
  const a9Changed = (editDraft?.A9 || "") !== (original.A9 || "");
  const a10Changed = (editDraft?.A10 || "") !== (original.A10 || "");
  const a11Changed = (editDraft?.A11 || "") !== (original.A11 || "");
  const notesChanged = (editDraft?.NOTES || "") !== (original.notes || "");
  const visibleExplanationChanged = (editDraft?.visibleExplanation || "") !== (original.visibleExplanation || "");
  const flagChanged = (currentQuestion?.accuracyFlag || "") !== (original.accuracyFlag || "");
  // Ensure both values are numbers for accurate comparison
  const currentRating = Number(currentQuestion?.interestRating || 0);
  const originalRating = Number(original.interestRating || 0);
  const ratingChanged = currentRating !== originalRating;
  
  const hasChanges = questionChanged || answerChanged || a2Changed || a3Changed || a4Changed || a5Changed || a6Changed || a7Changed || a8Changed || a9Changed || a10Changed || a11Changed || notesChanged || visibleExplanationChanged || flagChanged || ratingChanged;
  
  if (hasChanges) {
    console.log("[hasUnsavedChanges] Changes detected:", {
      questionChanged,
      answerChanged,
      a2Changed,
      notesChanged,
      flagChanged,
      ratingChanged,
      currentFlag: currentQuestion?.accuracyFlag || "",
      originalFlag: original.accuracyFlag || "",
      currentRating: currentQuestion?.interestRating || 0,
      originalRating: original.interestRating || 0,
      editDraftQ: editDraft?.Q?.substring(0, 50),
      originalQ: original.question?.substring(0, 50),
      editDraftA1: editDraft?.A1?.substring(0, 30),
      originalA1: original.answer?.substring(0, 30)
    });
  }
  
  return hasChanges;
}, [hashtagGenre, previewQuestions, previewQuestionIndex, editDraft]);

// Update hasChanges state when relevant values change
useEffect(() => {
  if (hashtagGenre && previewQuestions.length > 0 && previewQuestionIndex >= 0 && previewQuestionIndex < previewQuestions.length) {
    const currentQuestion = previewQuestions[previewQuestionIndex];
    const currentFlag = currentQuestion?.accuracyFlag || "";
    const currentRating = currentQuestion?.interestRating || 0;
    const original = originalDataByIndexRef.current[previewQuestionIndex] || originalPreviewDataRef.current;
    const originalFlag = original?.accuracyFlag || "";
    const originalRating = original?.interestRating || 0;
    
    // Debounce the expensive hasUnsavedChanges check to avoid running on every keystroke
    const timeoutId = setTimeout(() => {
      const changed = hasUnsavedChanges();
      setHasChanges(changed);
    }, 200); // Wait 200ms after last change before checking
    return () => clearTimeout(timeoutId);
  } else {
    setHasChanges(false);
  }
}, [
  hashtagGenre, 
  previewQuestions,  // This array reference changes when flag/rating is updated, triggering the effect
  previewQuestionIndex, 
  editDraft?.Q, 
  editDraft?.A1, 
  editDraft?.A2, 
  editDraft?.A3, 
  editDraft?.A4, 
  editDraft?.A5, 
  editDraft?.A6, 
  editDraft?.A7,
  editDraft?.A8,
  editDraft?.A9,
  editDraft?.A10,
  editDraft?.A11,
  editDraft?.NOTES,
  hasUnsavedChanges
  // Removed nested property access - previewQuestions array reference change is enough
]);

// Check if CSV has unsaved changes
function checkCsvHasChanges() {
  if (!originalRowsRef.current || !rows.length) return false;
  if (originalFilenameRef.current !== filename) return true;
  
  // Compare rows (excluding internal properties like _dndId and isLocked for comparison)
  const normalizeRow = (row) => {
    const { _dndId, isLocked, ...rest } = row;
    return rest;
  };
  
  const originalStr = JSON.stringify(originalRowsRef.current.map(normalizeRow));
  const currentStr = JSON.stringify(rows.map(normalizeRow));
  return originalStr !== currentStr;
}

// Function to manually check for CSV changes (call on blur or after operations)
function checkForCsvChanges() {
  if (originalRowsRef.current) {
    const hasChanges = checkCsvHasChanges();
    setHasCsvChanges(hasChanges);
  }
}

// Handle browser navigation (back button, closing tab, etc.)
useEffect(() => {
  const handleBeforeUnload = (e) => {
    if (hasCsvChanges && !readOnly) {
      e.preventDefault();
      e.returnValue = "You have unsaved changes. Are you sure you want to leave?";
      return e.returnValue;
    }
  };

  window.addEventListener("beforeunload", handleBeforeUnload);
  return () => {
    window.removeEventListener("beforeunload", handleBeforeUnload);
  };
}, [hasCsvChanges, readOnly]);

// Reset preview index when questions change
useEffect(() => {
  setPreviewQuestionIndex(0);
  // Store original data for the first question
  if (previewQuestions.length > 0 && previewQuestions[0]) {
    originalPreviewDataRef.current = {
      question: previewQuestions[0].question || "",
      answer: previewQuestions[0].answer || "",
      A2: previewQuestions[0].A2 || "",
      A3: previewQuestions[0].A3 || "",
      A4: previewQuestions[0].A4 || "",
      A5: previewQuestions[0].A5 || "",
      A6: previewQuestions[0].A6 || "",
      A7: previewQuestions[0].A7 || "",
      A8: previewQuestions[0].A8 || "",
      A9: previewQuestions[0].A9 || "",
      A10: previewQuestions[0].A10 || "",
      A11: previewQuestions[0].A11 || "",
      notes: previewQuestions[0].notes || "",
      visibleExplanation: previewQuestions[0].visibleExplanation || "",
      accuracyFlag: previewQuestions[0].accuracyFlag || "",
      interestRating: previewQuestions[0].interestRating || 0
    };
    // Reset changes flag when questions are loaded
    setHasChanges(false);
  }
}, [previewQuestions.length]);

// Sync editDraft with preview question and store original data when question index changes
// Use a ref to track the last index we synced, so we don't re-sync on every previewQuestions update
const lastSyncedIndexRef = useRef(-1);
const originalDataByIndexRef = useRef({}); // Store original data per index, so it doesn't get overwritten

useEffect(() => {
  // Only sync in dynamic/hashtag mode - don't sync for regular CSV rows
  if (!hashtagGenre || previewQuestions.length === 0) {
    return;
  }
  
  // Only sync when previewQuestionIndex actually changes, not when previewQuestions array changes
  if (previewQuestionIndex === lastSyncedIndexRef.current) {
    return; // Already synced for this index, skip
  }
  
  if (previewQuestionIndex >= 0 && previewQuestionIndex < previewQuestions.length) {
    const currentQuestion = previewQuestions[previewQuestionIndex];
    if (currentQuestion) {
      // Validate that currentQuestion has required data before overwriting editDraft
      // Prevent wiping question/answers if data is missing or invalid
      const hasValidQuestion = currentQuestion.question !== null && currentQuestion.question !== undefined;
      const hasValidAnswer = currentQuestion.answer !== null && currentQuestion.answer !== undefined;
      
      if (!hasValidQuestion || !hasValidAnswer) {
        console.warn("[Sync editDraft] Skipping sync - question data incomplete:", {
          index: previewQuestionIndex,
          hasQuestion: hasValidQuestion,
          hasAnswer: hasValidAnswer,
          questionId: currentQuestion.id
        });
        return; // Don't overwrite with incomplete data
      }
      
      // Check if we already have original data stored for this index
      if (!originalDataByIndexRef.current[previewQuestionIndex]) {
        // Store original data ONLY if we haven't stored it before (first time loading this question)
        originalDataByIndexRef.current[previewQuestionIndex] = {
          question: currentQuestion.question || "",  // Stored as 'question' for comparison with editDraft.Q
          answer: currentQuestion.answer || "",       // Stored as 'answer' for comparison with editDraft.A1
          A2: currentQuestion.A2 || "",
          A3: currentQuestion.A3 || "",
          A4: currentQuestion.A4 || "",
          A5: currentQuestion.A5 || "",
          A6: currentQuestion.A6 || "",
          A7: currentQuestion.A7 || "",
          A8: currentQuestion.A8 || "",
          A9: currentQuestion.A9 || "",
          A10: currentQuestion.A10 || "",
          A11: currentQuestion.A11 || "",
          notes: currentQuestion.notes || "",         // Stored as 'notes' for comparison with editDraft.NOTES
          visibleExplanation: currentQuestion.visibleExplanation || "",  // Stored for comparison with editDraft.visibleExplanation
          accuracyFlag: currentQuestion.accuracyFlag || "",
          interestRating: currentQuestion.interestRating || 0
        };
        console.log("[Sync editDraft] Stored original for index", previewQuestionIndex, ":", originalDataByIndexRef.current[previewQuestionIndex]);
      }
      
      // Always update the current original reference to the stored original for this index
      originalPreviewDataRef.current = originalDataByIndexRef.current[previewQuestionIndex];
      
      // Update editDraft to match the current preview question (in editDraft format: Q, A1, etc.)
      // Use defensive fallbacks: if field is missing in currentQuestion, keep existing editDraft value
      setEditDraft(prev => ({
        ...prev,
        Q: currentQuestion.question ?? prev?.Q ?? "",
        A1: currentQuestion.answer ?? prev?.A1 ?? "",
        A2: currentQuestion.A2 ?? prev?.A2 ?? "",
        A3: currentQuestion.A3 ?? prev?.A3 ?? "",
        A4: currentQuestion.A4 ?? prev?.A4 ?? "",
        A5: currentQuestion.A5 ?? prev?.A5 ?? "",
        A6: currentQuestion.A6 ?? prev?.A6 ?? "",
        A7: currentQuestion.A7 ?? prev?.A7 ?? "",
        A8: currentQuestion.A8 ?? prev?.A8 ?? "",
        A9: currentQuestion.A9 ?? prev?.A9 ?? "",
        A10: currentQuestion.A10 ?? prev?.A10 ?? "",
        A11: currentQuestion.A11 ?? prev?.A11 ?? "",
        NOTES: currentQuestion.notes ?? prev?.NOTES ?? "",
        visibleExplanation: currentQuestion.visibleExplanation ?? prev?.visibleExplanation ?? ""
      }));
      
      // Mark this index as synced
      lastSyncedIndexRef.current = previewQuestionIndex;
      
      // Reset changes flag when switching questions
      setHasChanges(false);
    }
  }
}, [previewQuestionIndex]); // Only depend on previewQuestionIndex, NOT previewQuestions!

// Auto-scroll window while dragging near top/bottom edges
// Auto-scroll while dragging near the top/bottom edges of the scroll container (or window fallback)
useEffect(() => {
  if (!isDragging) return;

  const EDGE = 80;      // px from the container edge to start autoscroll
  const MAX_STEP = 28;  // max px per RAF tick
  const MIN_STEP = 6;   // min px per RAF tick

  const onMove = (e) => {
    const y = e?.clientY ?? e?.touches?.[0]?.clientY ?? null;
    if (y != null) lastMouseYRef.current = y;
  };

  const tick = () => {
    const y = lastMouseYRef.current;
    if (typeof y === "number") {
      const scroller = gridWrapRef.current;
      let dy = 0;

      if (scroller && scroller.scrollHeight > scroller.clientHeight) {
        // Scroll the grid wrapper
        const rect = scroller.getBoundingClientRect();
        const topEdge = rect.top + EDGE;
        const bottomEdge = rect.bottom - EDGE;

        if (y < topEdge) {
          const intensity = (topEdge - y) / EDGE; // 0..1
          dy = -Math.max(MIN_STEP, Math.min(MAX_STEP, Math.round(intensity * MAX_STEP)));
        } else if (y > bottomEdge) {
          const intensity = (y - bottomEdge) / EDGE; // 0..1
          dy = Math.max(MIN_STEP, Math.min(MAX_STEP, Math.round(intensity * MAX_STEP)));
        }

        if (dy !== 0) scroller.scrollTop += dy;
      } else {
        // Fallback: scroll window
        const h = window.innerHeight || document.documentElement.clientHeight;
        const topEdge = EDGE;
        const bottomEdge = h - EDGE;

        if (y < topEdge) {
          const intensity = (topEdge - y) / EDGE;
          dy = -Math.max(MIN_STEP, Math.min(MAX_STEP, Math.round(intensity * MAX_STEP)));
        } else if (y > bottomEdge) {
          const intensity = (y - bottomEdge) / EDGE;
          dy = Math.max(MIN_STEP, Math.min(MAX_STEP, Math.round(intensity * MAX_STEP)));
        }

        if (dy !== 0) window.scrollBy(0, dy);
      }
    }
    scrollRAF.current = requestAnimationFrame(tick);
  };

  window.addEventListener("mousemove", onMove, { passive: true });
  window.addEventListener("touchmove", onMove, { passive: true });

  scrollRAF.current = requestAnimationFrame(tick);

  return () => {
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("touchmove", onMove);
    if (scrollRAF.current) cancelAnimationFrame(scrollRAF.current);
    scrollRAF.current = 0;
    lastMouseYRef.current = null;
  };
}, [isDragging]);





function rowMatches(row, term) {
  const t = String(term || "").trim().toLowerCase();
  if (!t) return false;
  const fields = ["Q","CAT","A1","A2","A3","A4","A5","A6","A7","A8","A9","A10","A11","NOTES","P1","SOUND","BACKGROUND","VIDEO"];
  return fields.some(f => String(row[f] ?? "").toLowerCase().includes(t));
}

function flashRow(i) {
  setHighlightedRow(i);
  const el = rowRefs.current[i];
  if (el && el.scrollIntoView) {
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  }
  setTimeout(() => setHighlightedRow(null), 2400);
}

function findNextMatch() {
  const term = searchTerm.trim();
  if (!term || !rows.length) return;

  const start = (lastFoundIndex + 1) % rows.length;
  for (let off = 0; off < rows.length; off++) {
    const i = (start + off) % rows.length;
    if (rowMatches(rows[i], term)) {
      setLastFoundIndex(i);
      flashRow(i);
      return;
    }
  }
  // no match → reset index so next search starts from the top again
  setLastFoundIndex(-1);
}

// === Helper: compute Estimated Run Time up to END QUIZ only ===
// === Helper: compute Estimated Run Time up to END QUIZ only ===
function computeEstimatedRunTime(rows) {
  // Find the first END QUIZ marker (using same logic as rest of codebase)
  const endIdx = rows.findIndex((row) => {
    const cat = String(row?.CAT || row?.type || row?.cat || "").trim().toUpperCase();
    return cat === "END QUIZ" || cat === "ENDQUIZ";
  });

  // Consider slides only up to (but not including) END QUIZ marker
  // Exclude everything from END QUIZ onwards
  const effectiveRows = endIdx >= 0 ? rows.slice(0, endIdx) : rows;

  // Mapping of question types to overhead times
  const getOverheadForType = (type) => {
    const normalized = canonicalCategoryUpper(
      String(type || "")
        .trim()
        .toUpperCase()
        .replace(/\s+/g, " ")
    );
    
    // 15 seconds: TRUE/FALSE, MULTICHOICE, MULTIANSWER
    if (["TRUE/FALSE", "TRUEFALSE", "MULTICHOICE", "MULTIANSWER"].includes(normalized)) {
      return 15;
    }
    
    // 20 seconds: VANISHINGCHOICE, OUTOFORDER, WHEEL, TYPE, BREADCRUMBS, BUZZER, NEARESTANSWER, BETANSWER, JACKPOT, WHOAMI
    if (["VANISHINGCHOICE", "OUTOFORDER", "WHEEL", "TYPE", "BREADCRUMBS", "BUZZER", "NEARESTANSWER", "BETANSWER", "JACKPOT", "WHOAMI"].includes(normalized)) {
      return 20;
    }
    
    // 25 seconds: ENDROUND (for end of round scores display)
    if (normalized === "ENDROUND") {
      return 25;
    }
    
    // 0 seconds (no overhead): STARTROUND, SHOWROUNDS, JOIN QUIZ, INTRODUCTION, END QUIZ, BREAK TIME, DISPLAY, DEFAULT CATEGORY, and any other display-like slides
    // Default to 0 for unknown types as well
    return 0;
  };

  // Helper to format seconds as H:MM:SS
  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  // Sum declared TIME values (raw time)
  const rawTimeSec = effectiveRows.reduce((acc, row) => {
    const t = parseInt(row.TIME, 10);
    return acc + (isNaN(t) ? 0 : t);
  }, 0);

  // Calculate total overhead based on type for each slide
  const totalOverheadSec = effectiveRows.reduce((acc, row) => {
    const rowType = row?.CAT || row?.type || row?.cat || "";
    return acc + getOverheadForType(rowType);
  }, 0);

  // Estimated time = raw time + overhead
  const estimatedTimeSec = rawTimeSec + totalOverheadSec;

  // Format both times
  const rawTimeDisplay = formatTime(rawTimeSec);
  const estimatedTimeDisplay = formatTime(estimatedTimeSec);

  return { 
    rawTimeDisplay, 
    rawTimeSec, 
    estimatedTimeDisplay, 
    estimatedTimeSec, 
    slideCount: effectiveRows.length 
  };
}




function handleCopyRow(idx) {
  setRows(prev => {
    const src = prev[idx];
    if (!src) return prev;

    // 🚫 Do not allow copying of locked display slides (INTRODUCTION, JOIN QUIZ, END QUIZ)
    if (src.isLocked) {
      try { appAlert("Copy is disabled for this slide type."); } catch {}
      return prev;
    }

    // clone row (drop ephemeral id), give the copy a fresh _dndId
    const { _dndId, ...rest } = src;
    const copy = {
      ...rest,
      _dndId: `row-${Date.now()}-${Math.random().toString(36).slice(2,7)}`,
      flag: rest.flag !== undefined ? rest.flag : false // Ensure flag is initialized
    };

    const next = prev.slice();
    next.splice(idx + 1, 0, copy); // insert directly below the source row
    return next;
  });
}




function startEdit(i) {
  setEditRowIdx(i);
  const rowData = rows[i];
  
  // Check if this is a hashtag question BEFORE setting editDraft
  const questionText = String(rowData?.Q || "").trim();
  const isHashtag = isHashtagPattern(questionText);
  
  // Only set editDraft from CSV row if it's NOT a hashtag question
  // If it's a hashtag, let the hashtag detection effect handle loading the question
  if (!isHashtag) {
    setEditDraft({ ...rowData });
    // Clear dynamic/hashtag mode state when opening a regular CSV row (not a hashtag)
    setHashtagGenre(null);
    setPreviewQuestions([]);
    setPreviewQuestionIndex(0);
    lastSyncedIndexRef.current = -1;
    originalDataByIndexRef.current = {};
  } else {
    // For hashtag questions, preserve existing editDraft or set minimal data
    // The hashtag detection effect will load the actual question from Firestore
    setEditDraft(prev => prev || { ...rowData, Q: questionText, CAT: rowData?.CAT || "" });
  }
}
function closeEdit() {
  // Prevent CSV sync when in hashtag mode - changes should only go to Firestore
  // Check multiple conditions to prevent race conditions
  const isHashtagQuestion = isHashtagPattern(editDraft?.Q || "");
  if (!(hashtagGenre || previewQuestions.length > 0 || isHashtagQuestion)) {
    // Only sync editDraft back to rows if NOT in hashtag/dynamic mode
    if (editRowIdx !== null && editDraft) {
      setRows(prev => prev.map((r, idx) => 
        idx === editRowIdx ? { ...r, ...editDraft } : r
      ));
      // Check for changes after state updates
      setTimeout(() => checkForCsvChanges(), 0);
    }
  } else {
    console.log("[closeEdit] Skipping CSV sync - in hashtag/dynamic mode");
  }
  setEditRowIdx(null);
  setEditDraft(null);
}
function saveEdit() {
  if (editRowIdx == null || !editDraft) return;
  
  // Prevent CSV save when in hashtag mode - changes should only go to Firestore
  // Check multiple conditions to prevent race conditions
  const isHashtagQuestion = isHashtagPattern(editDraft?.Q || "");
  if (hashtagGenre || previewQuestions.length > 0 || isHashtagQuestion) {
    console.log("[saveEdit] Skipping CSV save - in hashtag/dynamic mode");
    return;
  }
  
  // Persist edits into the in-memory rows for the current row
  setRows(prev =>
    prev.map((r, idx) => (idx === editRowIdx ? { ...r, ...editDraft } : r))
  );
  // Do NOT close the preview on save
}

// Hashtag detection helper
function isHashtagPattern(text) {
  return extractHashtagsFromText(text).length > 0;
}

function extractHashtagsFromText(text) {
  if (!text || typeof text !== "string") return [];
  const trimmed = text.trim();
  if (!trimmed.startsWith("#")) return [];
  if (trimmed.includes("\n")) return [];

  // Strip all leading # so "##Pop Culture" or "#Pop Culture" both yield genre "Pop Culture"
  const afterFirstHash = trimmed.replace(/^#+/, "").trim();
  const nextHashIdx = afterFirstHash.indexOf("#");
  const firstSegment =
    nextHashIdx >= 0
      ? afterFirstHash.slice(0, nextHashIdx).trim()
      : afterFirstHash.trim();
  const firstTag = firstSegment.replace(/^#+/, "").trim();
  if (!firstTag) return [];

  // Remaining hashtags (no spaces): parse from second # onward
  const rest = nextHashIdx >= 0 ? afterFirstHash.slice(nextHashIdx) : "";
  const restMatches = rest.match(/#[^\s,]+/g);
  const restTags = restMatches
    ? restMatches.map((tag) => tag.replace(/^#+/, "").trim()).filter(Boolean)
    : [];

  return [firstTag, ...restTags];
}

function normalizeTagValue(raw) {
  if (!raw) return "";
  return String(raw).trim().replace(/^#+/, "").toLowerCase();
}

function normalizeTagList(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.flatMap((entry) => parseTagsFromString(entry));
  }
  if (typeof raw === "string") {
    return parseTagsFromString(raw);
  }
  return [];
}

function parseTagsFromString(raw) {
  if (!raw) return [];
  const text = String(raw);
  const hashtagMatches = text.match(/#[^\s,]+/g);
  if (hashtagMatches && hashtagMatches.length > 0) {
    return hashtagMatches.map(normalizeTagValue).filter(Boolean);
  }
  return text
    .split(/[,\s]+/g)
    .map(normalizeTagValue)
    .filter(Boolean);
}


// Map editDraft fields to QuestionLibrary schema for Firestore updates
// CRITICAL: For dynamic questions, currentPreviewQuestion is the ONLY source of truth
// editDraft contains CSV data (like "#Music" hashtag) which should NEVER be used for dynamic questions
// User edits are stored in previewQuestions array, which is what currentPreviewQuestion comes from
function mapEditDraftToLibraryFields(editDraft, previewQuestionData, currentPreviewQuestion) {
  const updates = {};
  
  // For dynamic questions, ALWAYS use currentPreviewQuestion - never trust editDraft
  // editDraft.Q is the CSV hashtag ("#Music") and should be completely ignored
  // All user edits are stored in previewQuestions array, which currentPreviewQuestion comes from
  
  // Question: ALWAYS use currentPreviewQuestion.question - it contains the actual question text
  updates.question = currentPreviewQuestion?.question || "";
  
  // Answers: ALWAYS use currentPreviewQuestion - user edits are stored here
  updates.answer = currentPreviewQuestion?.answer || "";
  updates.A2 = currentPreviewQuestion?.A2 || "";
  updates.A3 = currentPreviewQuestion?.A3 || "";
  updates.A4 = currentPreviewQuestion?.A4 || "";
  updates.A5 = currentPreviewQuestion?.A5 || "";
  updates.A6 = currentPreviewQuestion?.A6 || "";
  updates.A7 = currentPreviewQuestion?.A7 || "";
  updates.A8 = currentPreviewQuestion?.A8 || "";
  updates.A9 = currentPreviewQuestion?.A9 || "";
  updates.A10 = currentPreviewQuestion?.A10 || "";
  updates.A11 = currentPreviewQuestion?.A11 || "";
  
  // Notes: ALWAYS use currentPreviewQuestion
  updates.notes = currentPreviewQuestion?.notes || "";
  
  // Visible Explanation: ALWAYS use currentPreviewQuestion
  updates.visibleExplanation = currentPreviewQuestion?.visibleExplanation || "";
  
  // Map preview question data fields (flag and rating)
  if (previewQuestionData) {
    updates.accuracyFlag = previewQuestionData.accuracyFlag ?? "";
    if (previewQuestionData.interestRating !== undefined) {
      const rating = parseInt(previewQuestionData.interestRating, 10);
      updates.interestRating = isNaN(rating) ? 0 : rating;
    }
  }
  
  console.log("[mapEditDraftToLibraryFields] Mapped updates:", updates);
  return updates;
}

// Save preview question changes to Firestore
async function savePreviewQuestionToLibrary(questionId, updates) {
  if (!questionId || Object.keys(updates).length === 0) {
    console.log("[savePreviewQuestionToLibrary] Skipping - no questionId or empty updates");
    return;
  }
  
  try {
    const questionRef = doc(db, "QuestionLibrary", questionId);
    console.log("[savePreviewQuestionToLibrary] Updating document:", questionId, "with:", updates);
    await updateDoc(questionRef, updates);
    console.log("[savePreviewQuestionToLibrary] Successfully saved question changes to Firestore");
  } catch (error) {
    console.error("[savePreviewQuestionToLibrary] Error saving preview question to Firestore:", error);
    throw error; // Re-throw so the caller can handle it
  }
}

// Update local preview question state
function updatePreviewQuestionLocal(previewQuestions, previewQuestionIndex, updates) {
  if (!previewQuestions || previewQuestionIndex < 0 || previewQuestionIndex >= previewQuestions.length) {
    return previewQuestions;
  }
  
  const updated = [...previewQuestions];
  updated[previewQuestionIndex] = {
    ...updated[previewQuestionIndex],
    ...updates
  };
  return updated;
}

// BREADCRUMBS edit modal: A1 (fixed) + drag-reorder A2..A11 with handle + numbers 1..10
function BreadcrumbsEditDnD({
  pillHeight,
  displayData,
  editDraft,
  setEditDraft,
  answers,
  previewBreadcrumbsAreaRef,
  hashtagGenre,
  previewQuestions,
  previewQuestionIndex,
  setPreviewQuestions,
  checkForCsvChanges,
  limitAnswer,
  warnAndBlockIfOver
}) {
  const bcGap = Math.max(4, Math.min(8, Math.round(pillHeight * 0.2)));
  const activeFieldRef = useRef(null);

  const buildLocalValues = useCallback(() => {
    const out = { A1: "" };
    const a1FromDisplay = (displayData && Object.prototype.hasOwnProperty.call(displayData, "A1")) ? displayData.A1 : undefined;
    out.A1 = String(a1FromDisplay ?? editDraft?.A1 ?? answers?.[0] ?? "");
    BREADCRUMBS_CLUE_KEYS.forEach((k, i) => {
      const answerIdx = i + 1;
      const fromDisplay = (displayData && Object.prototype.hasOwnProperty.call(displayData, k)) ? displayData[k] : undefined;
      out[k] = String(fromDisplay ?? editDraft?.[k] ?? answers?.[answerIdx] ?? "");
    });
    return out;
  }, [displayData, editDraft, answers]);

  const [localValues, setLocalValues] = useState(() => buildLocalValues());

  useEffect(() => {
    // Do not clobber the local typing buffer while an input is focused.
    // Parent rerenders can happen during debounced commits; this preserves focus.
    if (activeFieldRef.current) return;
    const next = buildLocalValues();
    setLocalValues((prev) => {
      if (prev.A1 !== next.A1) return next;
      for (const k of BREADCRUMBS_CLUE_KEYS) {
        if (prev[k] !== next[k]) return next;
      }
      return prev;
    });
  }, [buildLocalValues, previewQuestionIndex]);

  const commitValues = useCallback((nextValues) => {
    setEditDraft((d) => ({ ...(d || {}), ...nextValues }));
    if (hashtagGenre && previewQuestions.length > 0 && previewQuestionIndex >= 0) {
      const patch = { ...nextValues };
      if (Object.prototype.hasOwnProperty.call(patch, "A1")) {
        patch.answer = patch.A1;
        delete patch.A1;
      }
      setPreviewQuestions((prev) => updatePreviewQuestionLocal(prev, previewQuestionIndex, patch));
    }
  }, [setEditDraft, hashtagGenre, previewQuestions, previewQuestionIndex, setPreviewQuestions]);

  const flushCommit = useCallback((nextValues) => {
    commitValues(nextValues);
  }, [commitValues]);

  const onBreadcrumbClueDragEnd = (result) => {
    if (!result.destination) return;
    const s = result.source.index;
    const t = result.destination.index;
    if (s === t) return;
    const prevVals = BREADCRUMBS_CLUE_KEYS.map((k) => (localValues[k] == null ? "" : String(localValues[k])));
    const nextVals = reorderListImmutable(prevVals, s, t);
    const nextPatch = {};
    BREADCRUMBS_CLUE_KEYS.forEach((k, i) => { nextPatch[k] = nextVals[i]; });
    const nextLocal = { ...localValues, ...nextPatch };
    setLocalValues(nextLocal);
    flushCommit(nextLocal);
    if (checkForCsvChanges) {
      setTimeout(() => { try { checkForCsvChanges(); } catch (e) { /* no-op */ } }, 0);
    }
  };

  const keyA1 = "A1";
  const rawA1 = String(localValues.A1 ?? "");
  const answerFontA1 = computeBreadcrumbsPreviewFontSize(rawA1, pillHeight);
  const bcBadgeSize = `${Math.max(20, Math.min(30, Math.round(pillHeight * 0.82)))}px`;
  const bcBadgeFontSize = `${Math.max(0.62, Math.min(0.95, pillHeight * 0.028)).toFixed(2)}rem`;

  const basePill = {
    position: "relative",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    fontSize: "inherit",
    fontFamily: "'Arial Black', Impact, Arial, sans-serif",
    color: "#fff",
    textShadow: "-2px -2px 0 #111, 2px -2px 0 #111, -2px 2px 0 #111, 2px 2px 0 #111",
    pointerEvents: "auto",
    userSelect: "text",
    height: `${pillHeight}px`,
    minHeight: `${pillHeight}px`,
    maxHeight: `${pillHeight}px`,
    boxSizing: "border-box",
    lineHeight: 1.1,
    padding: "0 0.45em"
  };

  return (
    <DragDropContext onDragEnd={onBreadcrumbClueDragEnd}>
      <div
        ref={previewBreadcrumbsAreaRef}
        className="answer-list host-answer-list-layout"
        style={{
          height: "auto",
          maxHeight: "none",
          marginBlock: 0,
          minHeight: 0,
          minWidth: 0,
          width: "100%",
          alignSelf: "stretch",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: bcGap,
          listStyle: "none",
          boxSizing: "border-box"
        }}
      >
        <ol
          className="answer-list host-answer-list-layout"
          type="A"
          style={{
            height: "auto",
            maxHeight: "none",
            margin: 0,
            padding: 0,
            listStyle: "none",
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            width: "100%",
            minWidth: 0,
            gap: 0
          }}
        >
          <li
            className="host-answer-item"
            key="breadcrumbs-a1"
            style={{ width: "100%", minWidth: 0, maxWidth: "100%", boxSizing: "border-box" }}
          >
            <div
              className="answer-option option-a host-answer-pill"
              style={basePill}
            >
              <span
                aria-hidden="true"
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: bcBadgeSize,
                  height: bcBadgeSize,
                  borderRadius: "999px",
                  background: "rgba(33,166,74,.92)",
                  border: "2px solid #52d788",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.45)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: bcBadgeFontSize,
                  fontWeight: 900,
                  lineHeight: 1,
                  pointerEvents: "none"
                }}
                title="Correct"
              >
                ✓
              </span>
              <input
                type="text"
                value={rawA1}
                onBeforeInput={(e) => { warnAndBlockIfOver(e, rawA1 ?? "", keyA1); }}
                onPaste={(e) => {
                  const paste = e.clipboardData?.getData?.("text") ?? "";
                  warnAndBlockIfOver(e, rawA1 ?? "", keyA1, paste);
                }}
                onChange={(e) => {
                  const [limited] = limitAnswer(e.target.value);
                  setLocalValues((prev) => ({ ...prev, A1: limited }));
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    flushCommit({ ...localValues });
                  }
                }}
                onFocus={() => { activeFieldRef.current = "A1"; }}
                onBlur={() => {
                  activeFieldRef.current = null;
                  flushCommit({ ...localValues });
                }}
                style={{
                  flex: 1,
                  minWidth: 0,
                  width: "100%",
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  textAlign: "center",
                  color: "#fff",
                  fontFamily: "'Arial Black', Impact, Arial, sans-serif",
                  fontWeight: 900,
                  letterSpacing: ".02em",
                  fontSize: answerFontA1,
                  textShadow: "-2px -2px 0 #111, 2px -2px 0 #111, -2px 2px 0 #111, 2px 2px 0 #111",
                  cursor: "text",
                  padding: "0 32px 0 8px"
                }}
                placeholder={getBreadcrumbsPillPlaceholder(0)}
              />
            </div>
          </li>
        </ol>

        <Droppable droppableId="breadcrumb-clue-slots">
          {(provided) => (
            <ol
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="answer-list host-answer-list-layout"
              type="A"
              style={{
                height: "auto",
                maxHeight: "none",
                margin: 0,
                padding: 0,
                listStyle: "none",
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: bcGap,
                width: "100%",
                minWidth: 0,
                boxSizing: "border-box"
              }}
            >
              {BREADCRUMBS_CLUE_KEYS.map((key, slotIndex) => {
                const answerIdx = slotIndex + 1;
                const rawAnswer = String(localValues[key] ?? "");
                const displayIdx = answerIdx;
                const answerFont = computeBreadcrumbsPreviewFontSize(rawAnswer, pillHeight);
                const optionLetter = String.fromCharCode(97 + (displayIdx % 6));
                return (
                  <Draggable key={key} draggableId={`bc-clue-${slotIndex}`} index={slotIndex}>
                    {(dragProvided) => (
                      <li
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        className="host-answer-item"
                        style={{
                          listStyle: "none",
                          width: "100%",
                          minWidth: 0,
                          maxWidth: "100%",
                          boxSizing: "border-box",
                          ...dragProvided.draggableProps.style
                        }}
                      >
                        <div
                          className={`answer-option option-${optionLetter} host-answer-pill`}
                          style={{
                            ...basePill,
                            fontSize: answerFont
                          }}
                        >
                          <span
                            aria-hidden="true"
                            style={{
                              position: "absolute",
                              right: "8px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              width: bcBadgeSize,
                              height: bcBadgeSize,
                              borderRadius: "999px",
                              background: "rgba(214,57,57,.92)",
                              border: "2px solid #ff7a7a",
                              boxShadow: "0 4px 16px rgba(0,0,0,0.45)",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: bcBadgeFontSize,
                              fontWeight: 900,
                              lineHeight: 1,
                              pointerEvents: "none"
                            }}
                            title="Clue (not the correct answer)"
                          >
                            ✗
                          </span>
                          <span
                            {...dragProvided.dragHandleProps}
                            style={{
                              cursor: "grab",
                              color: "#aaa",
                              fontWeight: 900,
                              fontSize: "1.15em",
                              lineHeight: 1,
                              userSelect: "none",
                              flexShrink: 0,
                              padding: "0 2px 0 0",
                              textAlign: "center"
                            }}
                            title="Drag to reorder (top = first clue revealed, A2)"
                            aria-label="Drag to reorder this clue in the reveal list"
                          >
                            ☰
                          </span>
                          <span
                            style={{
                              flexShrink: 0,
                              minWidth: "1.4em",
                              textAlign: "center",
                              fontSize: "0.82em",
                              fontWeight: 800,
                              opacity: 0.9,
                              userSelect: "none",
                              color: "rgba(255,255,255,0.95)"
                            }}
                            title="Clue order in reveal (1 = first, 10 = last before 11th slot)"
                          >
                            {slotIndex + 1}
                          </span>
                          <input
                            type="text"
                            value={rawAnswer}
                            onBeforeInput={(e) => { warnAndBlockIfOver(e, rawAnswer ?? "", key); }}
                            onPaste={(e) => {
                              const paste = e.clipboardData?.getData?.("text") ?? "";
                              warnAndBlockIfOver(e, rawAnswer ?? "", key, paste);
                            }}
                            onChange={(e) => {
                              const [limited] = limitAnswer(e.target.value);
                              setLocalValues((prev) => ({ ...prev, [key]: limited }));
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                flushCommit({ ...localValues });
                              }
                            }}
                            onFocus={() => { activeFieldRef.current = key; }}
                            onBlur={() => {
                              activeFieldRef.current = null;
                              flushCommit({ ...localValues });
                            }}
                            style={{
                              flex: 1,
                              minWidth: 0,
                              background: "transparent",
                              border: "none",
                              outline: "none",
                              textAlign: "center",
                              color: "#fff",
                              fontFamily: "'Arial Black', Impact, Arial, sans-serif",
                              fontWeight: 900,
                              letterSpacing: ".02em",
                              fontSize: "inherit",
                              textShadow: "-2px -2px 0 #111, 2px -2px 0 #111, -2px 2px 0 #111, 2px 2px 0 #111",
                              cursor: "text",
                              padding: "0 32px 0 2px"
                            }}
                            placeholder={getBreadcrumbsPillPlaceholder(displayIdx)}
                          />
                        </div>
                      </li>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </ol>
          )}
        </Droppable>
      </div>
    </DragDropContext>
  );
}

/** Out-of-order preview in Trove/CSV editor: numbers + handle on the colored pill (Breadcrumbs-style). */
const OOO_PREVIEW_KEYS = ["A1", "A2", "A3", "A4", "A5", "A6"];

function OutOfOrderEditDnD({
  pillHeight,
  displayData,
  editDraft,
  setEditDraft,
  answers,
  hashtagGenre,
  previewQuestions,
  previewQuestionIndex,
  setPreviewQuestions,
  limitAnswer,
  warnAndBlockIfOver,
  checkForCsvChanges
}) {
  const getVal = (key) => {
    const fromDisplay =
      displayData && Object.prototype.hasOwnProperty.call(displayData, key)
        ? displayData[key]
        : undefined;
    const idx = parseInt(String(key).replace("A", ""), 10) - 1;
    return fromDisplay ?? editDraft?.[key] ?? (answers && answers[idx] != null ? answers[idx] : "");
  };

  const activeKeys = OOO_PREVIEW_KEYS.filter((k) => {
    const v = getVal(k);
    return v != null && String(v).trim() !== "";
  });

  const onOooDragEnd = (result) => {
    if (!result.destination) return;
    const s = result.source.index;
    const t = result.destination.index;
    if (s === t) return;
    if (activeKeys.length < 2) return;
    const vals = activeKeys.map((k) => getVal(k));
    const newVals = reorderListImmutable(vals, s, t);
    const patch = {};
    activeKeys.forEach((k, i) => {
      patch[k] = newVals[i];
    });
    setEditDraft((d) => ({ ...(d || {}), ...patch }));
    if (hashtagGenre && previewQuestions.length > 0 && previewQuestionIndex >= 0) {
      const previewPatch = {};
      activeKeys.forEach((k, i) => {
        const fk = k === "A1" ? "answer" : k;
        previewPatch[fk] = newVals[i];
      });
      setPreviewQuestions((prev) =>
        updatePreviewQuestionLocal(prev, previewQuestionIndex, previewPatch)
      );
    }
    if (checkForCsvChanges) {
      setTimeout(() => {
        try {
          checkForCsvChanges();
        } catch (e) {
          /* no-op */
        }
      }, 0);
    }
  };

  const oooGap = Math.max(4, Math.min(8, Math.round(pillHeight * 0.2)));
  const bcBadgeSize = `${Math.max(20, Math.min(30, Math.round(pillHeight * 0.82)))}px`;
  const bcBadgeFontSize = `${Math.max(0.62, Math.min(0.95, pillHeight * 0.028)).toFixed(2)}rem`;
  const badgeRightPad = Math.max(32, Math.round(pillHeight * 0.38) + 8);

  const basePill = {
    position: "relative",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    fontSize: "inherit",
    fontFamily: "'Arial Black', Impact, Arial, sans-serif",
    color: "#fff",
    textShadow: "-2px -2px 0 #111, 2px -2px 0 #111, -2px 2px 0 #111, 2px 2px 0 #111",
    pointerEvents: "auto",
    userSelect: "text",
    height: `${pillHeight}px`,
    minHeight: `${pillHeight}px`,
    maxHeight: `${pillHeight}px`,
    boxSizing: "border-box",
    lineHeight: 1.1,
    padding: "0 0.45em"
  };

  if (activeKeys.length === 0) {
    return (
      <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.9rem" }}>
        Add text in A1–A6 in the table for each option in the correct order.
      </div>
    );
  }

  return (
    <DragDropContext onDragEnd={onOooDragEnd}>
      <Droppable droppableId="ooo-csv-preview-slots">
        {(dropProvided) => (
          <ol
            ref={dropProvided.innerRef}
            {...dropProvided.droppableProps}
            className="answer-list host-answer-list-layout"
            type="A"
            style={{
              margin: 0,
              padding: 0,
              listStyle: "none",
              width: "100%",
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: oooGap
            }}
          >
            {activeKeys.map((key, index) => {
              const rawAnswer = getVal(key);
              const displayIdx = index;
              const isCorrect = displayIdx === 0;
              const answerFont = computeBreadcrumbsPreviewFontSize(String(rawAnswer), pillHeight);
              const badgeBg = isCorrect ? "rgba(33,166,74,.92)" : "rgba(214,57,57,.92)";
              const badgeBrdr = isCorrect ? "#52d788" : "#ff7a7a";
              const badgeGlyph = isCorrect ? "✓" : "✗";
              const optionLetter = String.fromCharCode(97 + (displayIdx % 6));

              return (
                <Draggable key={key} draggableId={`ooo-${key}`} index={index}>
                  {(dragProvided) => (
                    <li
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      className="host-answer-item"
                      style={{
                        listStyle: "none",
                        boxSizing: "border-box",
                        ...dragProvided.draggableProps.style
                      }}
                    >
                      <div
                        className={`answer-option option-${optionLetter} host-answer-pill`}
                        style={{ ...basePill, fontSize: answerFont }}
                      >
                        <span
                          aria-hidden="true"
                          style={{
                            position: "absolute",
                            right: "8px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: bcBadgeSize,
                            height: bcBadgeSize,
                            borderRadius: "999px",
                            background: badgeBg,
                            border: `2px solid ${badgeBrdr}`,
                            boxShadow: "0 4px 16px rgba(0,0,0,0.45)",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: bcBadgeFontSize,
                            fontWeight: 900,
                            lineHeight: 1,
                            pointerEvents: "none"
                          }}
                          title={isCorrect ? "First in correct order" : "Other positions"}
                        >
                          {badgeGlyph}
                        </span>
                        <span
                          {...dragProvided.dragHandleProps}
                          style={{
                            cursor: "grab",
                            color: "#aaa",
                            fontWeight: 900,
                            fontSize: "1.15em",
                            lineHeight: 1,
                            userSelect: "none",
                            flexShrink: 0,
                            padding: "0 2px 0 0",
                            textAlign: "center"
                          }}
                          title="Drag to reorder (1 = first in correct order)"
                          aria-label="Drag to reorder this answer in the list"
                        >
                          ☰
                        </span>
                        <span
                          style={{
                            flexShrink: 0,
                            minWidth: "1.4em",
                            textAlign: "center",
                            fontSize: "0.82em",
                            fontWeight: 800,
                            opacity: 0.9,
                            userSelect: "none",
                            color: "rgba(255,255,255,0.95)"
                          }}
                          title="Rank in the correct order (1 = first)"
                        >
                          {index + 1}
                        </span>
                        <input
                          type="text"
                          value={String(rawAnswer)}
                          onBeforeInput={(e) => {
                            warnAndBlockIfOver(e, rawAnswer ?? "", key);
                          }}
                          onPaste={(e) => {
                            const paste = e.clipboardData?.getData?.("text") ?? "";
                            warnAndBlockIfOver(e, rawAnswer ?? "", key, paste);
                          }}
                          onChange={(e) => {
                            const [limited] = limitAnswer(e.target.value);
                            setEditDraft((d) => ({ ...(d || {}), [key]: limited }));
                            if (
                              hashtagGenre &&
                              previewQuestions.length > 0 &&
                              previewQuestionIndex >= 0
                            ) {
                              setPreviewQuestions((prev) => {
                                const patchKey = key === "A1" ? "answer" : key;
                                return updatePreviewQuestionLocal(prev, previewQuestionIndex, {
                                  [patchKey]: limited
                                });
                              });
                            }
                          }}
                          style={{
                            flex: 1,
                            minWidth: 0,
                            width: "100%",
                            background: "transparent",
                            border: "none",
                            outline: "none",
                            textAlign: "center",
                            color: "#fff",
                            fontFamily: "'Arial Black', Impact, Arial, sans-serif",
                            fontWeight: 900,
                            letterSpacing: ".02em",
                            fontSize: "inherit",
                            textShadow: "-2px -2px 0 #111, 2px -2px 0 #111, -2px 2px 0 #111, 2px 2px 0 #111",
                            cursor: "text",
                            padding: `0 ${badgeRightPad}px 0 2px`
                          }}
                          placeholder={`Rank ${index + 1}`}
                        />
                      </div>
                    </li>
                  )}
                </Draggable>
              );
            })}
            {dropProvided.placeholder}
          </ol>
        )}
      </Droppable>
    </DragDropContext>
  );
}

function orderHeaders(fields) {
  let arr = [...fields];

  // Keep POINTS immediately after TIME
  const ptsIdx = arr.indexOf("POINTS");
  if (ptsIdx !== -1) arr.splice(ptsIdx, 1);
  const timeIdx = arr.indexOf("TIME");
  if (timeIdx !== -1) {
    arr.splice(timeIdx + 1, 0, "POINTS");
  } else {
    arr.push("POINTS");
  }

  // CAT to the front
  const idx = arr.indexOf("CAT");
  if (idx > 0) {
    arr.splice(idx, 1);
    arr.unshift("CAT");
  }

  // Make sure URL columns appear at the end (now includes VIDEO_URL)
  ["P1_URL", "SOUND_URL", "BACKGROUND_URL", "VIDEO_URL"].forEach(col => {
    const i = arr.indexOf(col);
    if (i !== -1) {
      arr.splice(i, 1);
      arr.push(col);
    }
  });

  return arr;
}

/** Default pixel widths for CSV editor grid columns (user can drag-resize). */
function defaultCsvColumnWidth(headerKey) {
  return headerKey === "Q" ? 460 : 180;
}






useEffect(() => {
  getDownloadURL(ref(storage, "media/Brainbackground.png")).then(setBgUrl);
}, []);


// Show Bob warning ONLY after ownership has been resolved
useEffect(() => {
  if (ownershipChecked && readOnly && !readOnlyNoticeShown) {
    setReadOnlyNoticeShown(true);
    setLimitModal({
      open: true,
      message: "This Quiz is not owned by you so you may not edit (Save changes will be disabled)"
    });
  }
}, [ownershipChecked, readOnly, readOnlyNoticeShown]);




useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const file = params.get("file");

  // Helper to create a default row for a specific CAT
  const createDefaultRow = (cat, headers) => {
    const defaults = CAT_DEFAULTS[cat.toUpperCase()] || {};
    const row = Object.fromEntries(headers.map(h => {
      if (h === "TIME" || h === "POINTS") return [h, defaults[h] ?? "0"];
      return [h, defaults[h] ?? ""];
    }));
    row.CAT = cat;
    row._dndId = `row-${cat.toLowerCase()}-${Date.now()}`;
    row.flag = false; // Initialize flag as false (red) by default
    if (cat === 'JOIN QUIZ' || cat === 'INTRODUCTION' || cat === 'END QUIZ') {
      row.isLocked = true;
    }
    return row;
  };

  async function resolveOwnershipAndLoad() {
    setOwnershipChecked(false);

    const defaultHeaders = [
      "CAT","Q","A1","A2","A3","A4","A5","A6","A7","A8","A9","A10","A11",
      "P1","NOTES","visibleExplanation","SOUND","VIDEO","BACKGROUND","JIGSAW",
      "TIME","POINTS",
      "P1_URL","SOUND_URL","BACKGROUND_URL","VIDEO_URL","SPOTIFY_URL",
      "flag"
    ];
    const orderedHeaders = orderHeaders(defaultHeaders);
    setHeaders(orderedHeaders);

    if (!file) {
      setOwnerUid(currentUid || null);
      setReadOnly(false);
      setOwnershipChecked(true);
      setFilename("Untitled Quiz");

      // Create a new quiz with default locked rows
      const newRows = [
        createDefaultRow("JOIN QUIZ", orderedHeaders),
        createDefaultRow("INTRODUCTION", orderedHeaders),
        createDefaultRow("END QUIZ", orderedHeaders),
      ];
      setRows(newRows);
      originalRowsRef.current = JSON.parse(JSON.stringify(newRows));
      originalFilenameRef.current = "Untitled Quiz";
      setLoading(false);
      return;
    }

    setFilename(file.replace(/\.csv$/i, ""));
    const fileRef = ref(storage, `quizzes/${file}`);

    let detectedOwner = null;
    let sharedWith = [];
    try {
      const snap = await getDoc(doc(db, "quizzes", file));
      if (snap.exists()) {
        const d = snap.data() || {};
        detectedOwner = d.ownerUid || d.ownerUID || d.owner || null;
        sharedWith = Array.isArray(d.sharedWithUids) ? d.sharedWithUids : [];
      }
    } catch {}

    if (!detectedOwner) {
      try {
        const meta = await getMetadata(fileRef);
        detectedOwner = meta?.customMetadata?.ownerUid || meta?.customMetadata?.owner || null;
      } catch {}
    }

    setOwnerUid(detectedOwner);

    let ro = true;
    if (currentUid) {
      if (detectedOwner) ro = currentUid !== detectedOwner;
      else if (sharedWith.includes(currentUid)) ro = true;
      else ro = true;
    }
    setReadOnly(ro);
    setOwnershipChecked(true);

    getDownloadURL(fileRef)
      .then(url => fetch(url))
      .then(res => res.text())
      .then(async (csv) => {
        const headerRegex = /(^|\r?\n)\s*(?:"?Q"?\s*,|"?CAT"?\s*,)/i;
        const noBom = csv.replace(/^\uFEFF/, "");
        const match = headerRegex.exec(noBom);
        if (!match) { setMessage("Could not find header row in CSV!"); setLoading(false); return; }

        const tableCsv = noBom.slice(match.index + (match[1] ? match[1].length : 0));
        const parsed = Papa.parse(tableCsv, { header: true, skipEmptyLines: false });
        const fields = parsed.meta.fields || [];
        // Ensure visibleExplanation is in the headers even if not in CSV
        if (!fields.includes("visibleExplanation")) {
          fields.push("visibleExplanation");
        }
        // Ensure flag is in the headers even if not in CSV
        if (!fields.includes("flag")) {
          fields.push("flag");
        }
        const allHeaders = orderHeaders([...new Set([...fields, ...orderedHeaders])]);
        setHeaders(allHeaders);

        let rowsFromCsv = (parsed.data || [])
          .filter(row => Object.values(row).some(v => (v ?? "").toString().trim() !== ""))
          .map((row, i) => {
            const padded = {};
            allHeaders.forEach(h => {
              if (h === "TIME" || h === "POINTS") {
                padded[h] = (row[h] !== undefined && row[h] !== "") ? row[h] : "0";
              } else if (h === "flag") {
                // Handle flag: convert string "true"/"false" to boolean, default to false
                const flagValue = row[h];
                padded[h] = flagValue === "true" || flagValue === true || flagValue === "1";
              } else {
                padded[h] = (h in row && row[h] !== undefined) ? row[h] : "";
              }
            });
            return { ...padded, _dndId: `row-${i}` };
          });

          // Ensure default rows exist (respect original order for all three)
          let finalRows = [...rowsFromCsv];

          // Do we already have these special slides?
          const hasJoin  = finalRows.some(r => r.CAT === 'JOIN QUIZ');
          const hasIntro = finalRows.some(r => r.CAT === 'INTRODUCTION');
          const hasEnd   = finalRows.some(r => r.CAT === 'END QUIZ');

          // If missing, insert NEW rows in sensible places without moving existing ones.
          // - JOIN QUIZ: insert at index 0
          // - INTRODUCTION: insert at index 1 (or after JOIN QUIZ if present)
          // - END QUIZ: append at the very end IF it doesn’t exist (do NOT move an existing one)
          if (!hasJoin) {
            finalRows.splice(0, 0, createDefaultRow("JOIN QUIZ", allHeaders));
          }

          if (!hasIntro) {
            // if we just inserted JOIN QUIZ, INTRODUCTION should go next; otherwise still index 1 is safe
            const joinIdxNow = finalRows.findIndex(r => r.CAT === 'JOIN QUIZ');
            const introTarget = Math.min(
              Math.max(1, joinIdxNow + 1),
              finalRows.length
            );
            finalRows.splice(introTarget, 0, createDefaultRow("INTRODUCTION", allHeaders));
          }

          if (!hasEnd) {
            // Only add END QUIZ if missing — do NOT reposition if it exists
            finalRows.push(createDefaultRow("END QUIZ", allHeaders));
          }

          // Mark locked rows (don't change their position) and initialize flags
          finalRows = finalRows.map(r => {
            const updated = ['JOIN QUIZ', 'INTRODUCTION', 'END QUIZ'].includes(r.CAT)
              ? { ...r, isLocked: true }
              : r;
            // Initialize flag if not present
            if (updated.flag === undefined) {
              updated.flag = false;
            }
            return updated;
          });

          // Note: Warnings for questions placed after END QUIZ are handled
          // in the drag handler (handleDragEnd), not during file loading.

          setRows(finalRows);
          originalRowsRef.current = JSON.parse(JSON.stringify(finalRows));
          originalFilenameRef.current = file.replace(/\.csv$/i, "");
          setLoading(false);


        setTimeout(() => {
          ensureAllMediaUrls(finalRows, setRows).catch(err => {
            console.error("Auto-mapping failed:", err);
            setMessage("Auto-mapping failed: " + err.message);
          });
        }, 0);
      })
      .catch(err => { setMessage("Failed to load CSV: " + err.message); setLoading(false); });
  }

  resolveOwnershipAndLoad();
}, [currentUid]);









function handleDeleteMedia() {
  if (!previewImg || previewImg.rowIdx == null || !previewImg.col) return;
  const { rowIdx, col } = previewImg;
  const filenameCol = col.replace("_URL", "");
  setRows(oldRows =>
    oldRows.map((row, i) =>
      i === rowIdx
        ? { ...row, [col]: "", [filenameCol]: "" }
        : row
    )
  );
  setPreviewImg(null);
}

function applyBackgroundToAll(url) {
  if (!url) return;
  const guessedName = fileNameFromUrl(url);
  setRows(old =>
    old.map(r => ({
      ...r,
      BACKGROUND_URL: url,
      // Only fill BACKGROUND filename if currently empty; otherwise leave user’s filename
      BACKGROUND: r.BACKGROUND ? r.BACKGROUND : guessedName
    }))
  );
}





function handleFieldChange(rowIdx, col, value) {
  // A1: always 42 chars
  if (col === "A1") {
    const [limited] = limitAnswer(value);
    value = limited;
  }
  // A7–A11 (BREADCRUMBS clues): limit to 100 chars per clue
  if (/^A(?:7|8|9|10|11)$/.test(col)) {
    if (typeof value === "string" && value.length > 100) value = value.slice(0, 100);
  }

  setRows(oldRows => {
    const row = oldRows[rowIdx];
    const isBreadcrumbs = isBreadcrumbsCategory(row?.CAT);
    let finalValue = value;
    // A2–A6: 100 chars for BREADCRUMBS clues, 42 otherwise
    if (/^A[2-6]$/.test(col) && typeof value === "string") {
      const maxLen = isBreadcrumbs ? 100 : 42;
      if (value.length > maxLen) {
        finalValue = value.slice(0, maxLen);
        if (!isBreadcrumbs) openBobAlert(`The answer limit is ${maxLen} characters.`);
      }
    }
    return oldRows.map((r, i) =>
      i === rowIdx ? { ...r, [col]: finalValue } : r
    );
  });
}




// Treat only Firebase Storage links as "already mapped"
function isStorageUrl(u) {
  return /^https:\/\/firebasestorage\.googleapis\.com\//.test(String(u || ""));
}


async function ensureAllMediaUrls(rows, setRows) {
  const mediaFields = [
    ["P1", "P1_URL"],
    ["SOUND", "SOUND_URL"],
    ["BACKGROUND", "BACKGROUND_URL"],
    ["VIDEO", "VIDEO_URL"],
  ];

  const updatedRows = [...rows];

  // Count only things that truly need mapping
  const needsMapping = [];
  for (let i = 0; i < updatedRows.length; i++) {
    for (const [field, urlField] of mediaFields) {
      const v = updatedRows[i][field];
      const u = updatedRows[i][urlField];
      if (v && (!u || !isStorageUrl(u))) {
        needsMapping.push({ i, field, urlField, name: v });
      }
    }
  }

  const totalToMap = needsMapping.length;
  setMediaMappingMsg(totalToMap ? `Mapping ${totalToMap} media files...` : "");
  setMediaMappingCount(0);

  // Resolve in small batches to avoid thrashing memory; accumulate all changes, then apply once
  const BATCH = 8;
  let done = 0;
  const allChanges = [];

  const normaliseNames = (raw) => normaliseMediaName(raw); // reuse your helper

  for (let p = 0; p < needsMapping.length; p += BATCH) {
    const slice = needsMapping.slice(p, p + BATCH);

    await Promise.all(
      slice.map(async (item) => {
        const candidates = normaliseNames(item.name);
        for (const candidate of candidates) {
          try {
            const fileRef = ref(storage, `media/${candidate}`);
            const url = await getDownloadURL(fileRef);
            updatedRows[item.i][item.urlField] = url;
            allChanges.push({ i: item.i, urlField: item.urlField, url });
            break;
          } catch {
            // try next candidate
          }
        }
        done++;
      })
    );

    setMediaMappingCount(done);
    setMediaMappingMsg(`Please Wait Whilst Mapping Media: ${done}/${totalToMap}`);

    // yield to the browser between batches
    await new Promise((r) => setTimeout(r, 0));
  }

  // Single state update with all URL changes (avoids React batching overwriting previous updates)
  if (allChanges.length) {
    setRows((prev) => {
      const next = [...prev];
      for (const c of allChanges) {
        if (!next[c.i]) continue;
        next[c.i] = { ...next[c.i], [c.urlField]: c.url };
      }
      return next;
    });
  }

  setMediaMappingMsg("");
  return updatedRows;
}





async function handleSave() {
  setSaving(true);
  setMessage("");

  try {
    // Normalise filename with .csv
    const quizFileName =
      (filename?.endsWith(".csv") ? filename : `${filename}.csv`) || "Untitled Quiz.csv";

    // Safety: if we computed readOnly, respect it (button should already be disabled)
    if (readOnly) {
      setSaving(false);
      return;
    }

    // Map any media filenames to URLs before saving
    const mappedRows = await ensureAllMediaUrls(rows, setRows);
    const saveRows = mappedRows.map(({ _dndId, isLocked, ...row }) => {
      const cleanRow = { ...row };
      // Convert flag boolean to string for CSV
      if (cleanRow.flag !== undefined) {
        cleanRow.flag = cleanRow.flag ? "true" : "false";
      }
      return cleanRow;
    });
    const csv = Papa.unparse(saveRows, { columns: headers });

    // Upload CSV
    const fileRef = ref(storage, `quizzes/${quizFileName}`);
    await uploadString(fileRef, csv);

    // Ensure Storage metadata has ownerUid
    const currentUser = auth.currentUser;
    if (currentUser?.uid) {
      const existing = (await getMetadata(fileRef))?.customMetadata || {};
      await updateMetadata(fileRef, { customMetadata: { ...existing, ownerUid: currentUser.uid } });
    }

    // 🔧 Ensure/merge the Firestore index doc for this quiz
    const quizDocRef = doc(db, "quizzes", quizFileName);
    const snap = await getDoc(quizDocRef);
    if (snap.exists()) {
      await setDoc(
        quizDocRef,
        {
          ownerUid: currentUser?.uid || null,
          storagePath: `quizzes/${quizFileName}`,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } else {
      await setDoc(quizDocRef, {
        ownerUid: currentUser?.uid || null,
        storagePath: `quizzes/${quizFileName}`,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }

    setMessage("Quiz saved successfully!");
    // Reset CSV changes flag after successful save
    setHasCsvChanges(false);
    originalRowsRef.current = JSON.parse(JSON.stringify(rows));
    originalFilenameRef.current = filename;
  } catch (err) {
    setMessage("Error saving: " + err.message);
  }

  setSaving(false);
}




function handleAddRow() {
  const defaultRow = Object.fromEntries(headers.map(h => {
    if (h === "TIME" || h === "POINTS") return [h, "0"];
    return [h, ""];
  }));
  defaultRow._dndId = `row-${Date.now()}`;
  setRows(r => {
      const endQuizIndex = r.findIndex(row => row.CAT === 'END QUIZ');
      if (endQuizIndex !== -1) {
          const newRows = [...r];
          newRows.splice(endQuizIndex, 0, defaultRow);
          return newRows;
      }
      return [...r, defaultRow];
  });
  // Check for changes after state updates
  setTimeout(() => checkForCsvChanges(), 0);
}


function handleRemoveRow(idx) {
    setRows(r => r.filter((row, i) => i !== idx));
    // Check for changes after state updates
    setTimeout(() => checkForCsvChanges(), 0);
  }

// Toggle flag for a single row
function handleToggleFlag(idx) {
  setRows(r => r.map((row, i) => 
    i === idx ? { ...row, flag: row.flag === true ? false : true } : row
  ));
  setTimeout(() => checkForCsvChanges(), 0);
}

// Toggle all flags (with confirmation)
function handleToggleAllFlags() {
  setShowToggleAllFlagsModal(true);
}

function confirmToggleAllFlags() {
  setRows(r => {
    const nonQuestionCatsList = ["DEFAULT CATEGORY", "STARTROUND", "ENDROUND", "SHOWROUNDS", "JOIN QUIZ", "INTRODUCTION", "END QUIZ", "BREAK TIME"];
    const endQuizIdx = r.findIndex(row => {
      const cat = (row.CAT || "").trim().toUpperCase();
      return cat === "END QUIZ" || cat === "ENDQUIZ";
    });
    const isQuestionRow = (row, index) => {
      if (endQuizIdx !== -1 && index > endQuizIdx) return false;
      const cat = (row.CAT || "").trim().toUpperCase();
      if (nonQuestionCatsList.includes(cat)) return false;
      return row.Q && String(row.Q).trim() !== "";
    };
    const questionRows = r.filter((row, i) => isQuestionRow(row, i));
    const allQuestionRowsGreen = questionRows.length > 0 && questionRows.every(row => row.flag === true);
    const targetFlag = !allQuestionRowsGreen;
    return r.map((row, i) =>
      isQuestionRow(row, i) ? { ...row, flag: targetFlag } : row
    );
  });
  setTimeout(() => checkForCsvChanges(), 0);
  setShowToggleAllFlagsModal(false);
}

  // Drag and drop handler
// Drag and drop handler
// Drag and drop handler
function handleDragEnd(result) {
  setIsDragging(false);
  if (!result.destination) return;

  let reordered = Array.from(rows);
  const sourceIndex = result.source.index;
  const destinationIndex = result.destination.index;
  const draggedRow = reordered[sourceIndex];
  let movedItem = draggedRow;

  if (draggedRow && String(draggedRow.CAT || "").toUpperCase() === 'STARTROUND') {
    // Correctly find the matching ENDROUND using a depth counter
    let endRoundIndex = -1;
    let roundDepth = 1;
    for (let i = sourceIndex + 1; i < reordered.length; i++) {
      const cat = String(reordered[i].CAT || "").toUpperCase();
      if (cat === 'STARTROUND') {
        roundDepth++;
      } else if (cat === 'ENDROUND') {
        roundDepth--;
      }
      if (roundDepth === 0) {
        endRoundIndex = i;
        break;
      }
    }

    if (endRoundIndex !== -1) {
      const roundLength = endRoundIndex - sourceIndex + 1;
      const roundToMove = reordered.splice(sourceIndex, roundLength);

      // --- FINAL FIX: Use the destination index directly from the library ---
      // The library pre-adjusts the index, so no manual calculation is needed.
      reordered.splice(destinationIndex, 0, ...roundToMove);
      movedItem = roundToMove[0];

    } else {
      // Fallback for an incomplete round
      const [moved] = reordered.splice(sourceIndex, 1);
      reordered.splice(destinationIndex, 0, moved);
    }
  } else {
    // Logic for all other single rows
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(destinationIndex, 0, moved);
  }

  // Quizmaster Bob warning logic
  const finalEndQuizIndex = reordered.findIndex(r => String(r.CAT || "").toUpperCase() === 'END QUIZ');
  if (finalEndQuizIndex !== -1 && result.destination.index > finalEndQuizIndex) {
    if (movedItem && String(movedItem.CAT || "").toUpperCase() !== 'END QUIZ') {
      // Only show warning once per session
      if (!sessionStorage.getItem('endQuizWarningShown')) {
        openBobAlert("Caution: your slide will not display if it is placed after an 'END QUIZ' slide");
        sessionStorage.setItem('endQuizWarningShown', 'true');
      }
    }
  }

  setRows(reordered);
  // Check for changes after state updates
  setTimeout(() => checkForCsvChanges(), 0);
}


  // Navigation
  function gotoHome() {
    if (hasCsvChanges && !readOnly) {
      setPendingNavigation(() => () => {
        window.location.href = "/host";
      });
      setShowUnsavedChangesModal(true);
    } else {
      window.location.href = "/host";
    }
  }
  function gotoFindQuiz() {
    if (hasCsvChanges && !readOnly) {
      setPendingNavigation(() => () => {
        window.location.href = "/host";
      });
      setShowUnsavedChangesModal(true);
    } else {
      window.location.href = "/host";
    }
  }

  if (loading) return <div style={{ color: "#fff" }}>Loading...</div>;
  if (!filename) return <div style={{ color: "#fff" }}>{message}</div>;

  // Brain background
const brainBgStyle = {
  minHeight: "100vh",
  width: "100vw",
  background: bgUrl
    ? `linear-gradient(rgba(34,34,64,0.10), rgba(44,16,50,0.13)), url(${bgUrl}) center center / cover no-repeat`
    : "#223b4c",
  backgroundAttachment: "fixed"
};

// Count the number of rows with a question (Q is not blank)
// Count the number of rows with a question (Q is not blank)
const nonQuestionCats = ["DEFAULT CATEGORY", "STARTROUND", "ENDROUND", "SHOWROUNDS", "JOIN QUIZ", "INTRODUCTION", "END QUIZ", "BREAK TIME"];
// Find the index of END QUIZ to exclude questions after it
const endQuizIndex = rows.findIndex(row => {
  const cat = (row.CAT || "").trim().toUpperCase();
  return cat === "END QUIZ" || cat === "ENDQUIZ";
});

const totalQuestions = rows.filter((row, index) => {
  // Exclude rows after END QUIZ
  if (endQuizIndex !== -1 && index > endQuizIndex) return false;
  
  const cat = (row.CAT || "").trim().toUpperCase();
  if (nonQuestionCats.includes(cat)) return false;
  return row.Q && String(row.Q).trim() !== "";
}).length;

// For "Toggle all flags" header icon: true when all numbered-question rows are green
const allQuestionRowsGreen = (() => {
  const questionRows = rows.filter((row, index) => {
    if (endQuizIndex !== -1 && index > endQuizIndex) return false;
    const cat = (row.CAT || "").trim().toUpperCase();
    if (nonQuestionCats.includes(cat)) return false;
    return row.Q && String(row.Q).trim() !== "";
  });
  return questionRows.length > 0 && questionRows.every(row => row.flag === true);
})();

// Count red flags (unflagged records) before END QUIZ
const flaggedCountBeforeEndQuiz = rows.filter((row, index) => {
  // Exclude rows after END QUIZ
  if (endQuizIndex !== -1 && index > endQuizIndex) return false;
  // Count only unflagged records (red flags - row.flag !== true)
  return row.flag !== true;
}).length;


// === Estimated Run Time (up to END QUIZ only) ===
const { rawTimeDisplay, estimatedTimeDisplay } = computeEstimatedRunTime(rows);




const visibleHeaders = headers.filter(h => !HIDDEN_COLUMNS.includes(h));

const getCsvColWidth = (h) => csvColumnWidths[h] ?? defaultCsvColumnWidth(h);
/** Width hint for `<th>` only — body cells stay un-sized so the grid matches the pre-resize look. */
const csvHeaderColStyle = (h) => ({ width: getCsvColWidth(h) });

// Build display-only numbering that skips non-question rows
// Build display-only numbering that skips non-question rows
const questionNumberByIndex = (() => {
  let n = 0;
  // Find the index of END QUIZ to stop numbering there
  const endQuizIndexForNumbering = rows.findIndex(row => {
    const cat = (row.CAT || "").trim().toUpperCase();
    return cat === "END QUIZ" || cat === "ENDQUIZ";
  });
  
  return rows.map((row, index) => {
    // Don't number rows after END QUIZ
    if (endQuizIndexForNumbering !== -1 && index > endQuizIndexForNumbering) {
      return null;
    }
    
    const cat = (row.CAT || "").trim().toUpperCase();
    const isQuestion =
      !nonQuestionCats.includes(cat) &&
      row.Q &&
      String(row.Q).trim() !== "";
    return isQuestion ? ++n : null; // null renders as blank
  });
})();



  return (
    <div style={brainBgStyle}>
    {/* NAVBAR: Editing: ... | Save Changes | Home | Find Quiz */}
<div
  style={{
    width: "100vw",
    padding: "24px 0 14px 0",
    background: "rgba(20,15,44,0.55)",
    display: "flex",
    alignItems: "center",
    gap: "28px",
    position: "sticky",
    top: 0,
    zIndex: 999,
    fontFamily: "'Montserrat','Open Sans',Arial,sans-serif",
    boxShadow: "0 2px 12px 0 #0006",
  }}
>
  {/* Editing Filename label */}
  <span style={{
    color: "#fff",
    fontWeight: 800,
    fontSize: "1.13em",
    marginLeft: 18,
    marginRight: 8,
    letterSpacing: 0.3,
    textShadow: "0 2px 8px #233, 0 1px 10px #36d, 0 1px 0px #fff8",
    whiteSpace: "nowrap",
    fontFamily: "'Montserrat','Open Sans',Arial,sans-serif"
  }}>
    <input
      value={filename}
      onChange={e => setFilename(e.target.value)}
      onBlur={checkForCsvChanges}
      style={{
        color: "#fff",
        background: "rgba(18,28,44,0.48)",
        border: "1.5px solid #49e",
        borderRadius: 8,
        fontWeight: 800,
        fontSize: "1.13em",
        width: Math.max(10, filename.length + 2) + "ch",
        padding: "3px 11px",
        outline: "none",
        fontFamily: "'Montserrat','Open Sans',Arial,sans-serif",
        marginLeft: 2
      }}
      spellCheck={false}
    />
  </span>

  {/* Save Changes button */}
  <button
  style={{
    background: readOnly 
      ? "rgba(255,255,255,0.12)" 
      : hasCsvChanges 
        ? "rgba(255, 80, 80, 0.35)" // Red hue when there are changes
        : "rgba(58,128,255,0.22)",
    color: readOnly ? "#bbb" : "#fff",
    fontWeight: 900,
    fontSize: "1.13em",
    fontFamily: "'Montserrat','Open Sans',Arial,sans-serif",
    border: hasCsvChanges && !readOnly ? "2px solid rgba(255, 100, 100, 0.6)" : "none",
    borderRadius: "18px",
    padding: "8px 26px",
    marginRight: 12,
    marginLeft: 0,
    boxShadow: readOnly 
      ? "none" 
      : hasCsvChanges 
        ? "0 2px 12px rgba(255, 80, 80, 0.4), 0 0 0 2px rgba(255, 100, 100, 0.3)"
        : "0 2px 12px #48f8, 0 0 0 2px #20a2ff55",
    textShadow: "0 2px 8px #233, 0 1px 10px #36d, 0 1px 0px #fff8",
    transition: "background 0.17s, color 0.11s, border 0.17s",
    cursor: readOnly ? "not-allowed" : (saving ? "not-allowed" : "pointer"),
    opacity: saving || readOnly ? 0.65 : 1
  }}
  onClick={() => { if (!readOnly) handleSave(); }}
  disabled={saving || readOnly}
>
  {readOnly ? "Save Disabled" : (saving ? "Saving..." : "Save Changes")}
</button>


  {/* Home button */}
  <button
    style={{
      background: "rgba(58,128,255,0.19)",
      color: "#fff",
      fontWeight: 800,
      fontSize: "1.13em",
      border: "none",
      borderRadius: "18px",
      padding: "8px 28px",
      boxShadow: "0 2px 12px #48f8, 0 0 0 2px #20a2ff55",
      textShadow: "0 2px 8px #233, 0 1px 10px #36d, 0 1px 0px #fff8",
      fontFamily: "'Montserrat','Open Sans',Arial,sans-serif",
      transition: "background 0.17s, color 0.11s",
      cursor: "pointer",
    }}
    onClick={gotoHome}
  >
    Home
  </button>

  {/* Totals (kept left so they fit on one line) */}
  <span style={{
    color: "#fff",
    fontWeight: 700,
    fontSize: "1.06em",
    marginRight: 24,
    marginLeft: 16,
    letterSpacing: 0.2,
    whiteSpace: "nowrap"
  }}>
    Total Questions: {totalQuestions}
  </span>
  <span style={{
    color: "#fff",
    fontWeight: 700,
    fontSize: "1.06em",
    marginRight: 24,
    letterSpacing: 0.2,
    whiteSpace: "nowrap"
  }}>
    Flagged: {flaggedCountBeforeEndQuiz}
  </span>
<span style={{
  color: "#fff",
  fontWeight: 700,
  fontSize: "1.06em",
  marginRight: 16,
  letterSpacing: 0.2,
  whiteSpace: "nowrap"
}}>
  Raw time: {rawTimeDisplay}
</span>
<span style={{
  color: "#fff",
  fontWeight: 700,
  fontSize: "1.06em",
  marginRight: 16,
  letterSpacing: 0.2,
  whiteSpace: "nowrap"
}}>
  Estimated Run Time: {estimatedTimeDisplay}
</span>


  {/* Flexible spacer pushes search to the far right */}
  <div style={{ flex: 1 }} />

  {/* Search (moved out of the Total Running Time span, pinned right) */}
  <div style={{ display: "flex", alignItems: "center", gap: 8, marginRight: 16 }}>
    <input
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      onKeyDown={(e) => { if (e.key === "Enter") findNextMatch(); }}
      placeholder="Search questions/answers…"
      style={{
        color: "#fff",
        background: "rgba(18,28,44,0.48)",
        border: "1.5px solid #49e",
        borderRadius: 8,
        fontWeight: 700,
        fontSize: "1em",
        padding: "6px 10px",
        outline: "none",
        minWidth: "22ch"
      }}
      spellCheck={false}
    />
    <button
      onClick={findNextMatch}
      style={{
        background: "rgba(58,128,255,0.22)",
        color: "#fff",
        fontWeight: 800,
        border: "none",
        borderRadius: 12,
        padding: "6px 14px",
        boxShadow: "0 2px 12px #48f8, 0 0 0 2px #20a2ff55",
        cursor: "pointer"
      }}
    >
      Find Next
    </button>
  </div>

  {/* Save message (still at far right) */}
  {message && (
    <span style={{
      color: "#fff",
      fontWeight: 700,
      fontSize: "1em",
      marginRight: 36,
      textShadow: "0 2px 8px #233, 0 1px 10px #36d, 0 1px 0px #fff8",
      whiteSpace: "nowrap"
    }}>
      {message}
    </span>
  )}
</div>


{mediaMappingMsg && (
  <div style={{ color: "#f9e82f", fontWeight: 700, margin: "12px 0", fontSize: "1.13em" }}>
    {mediaMappingMsg}
  </div>
)}





      {/* Main content box */}
      <div style={{
        width: "99vw",
        minHeight: "86vh",
        padding: "2em 1vw",
       // background: "rgba(34,34,50,0.83)"
      }}>
        {/* No h2 here now! */}
<div
  ref={gridWrapRef}
  style={{
    overflowX: "auto",
    overflowY: "auto",
    maxHeight: "70vh",
    WebkitOverflowScrolling: "touch",
    background: "none",
    borderRadius: 12,
    padding: "2em 1em 1em 1em",
    marginBottom: 28,
    boxShadow: "none",
  }}
>



          <DragDropContext
  onDragStart={() => setIsDragging(true)}
  onDragEnd={handleDragEnd}
>

            <Droppable droppableId="quiz-table">


              
              {(provided) => (
<table
  {...provided.droppableProps}
  ref={provided.innerRef}
  style={{
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0
  }}
>

  
<thead>
  <tr>

     {/* COPY COLUMN (far left) */}
    <th style={{
      position: "sticky",
      top: 0,
      zIndex: 12,
      width: 36,
      background: "rgba(22,16,48,0.98)",
      color: "#fff",
      boxShadow: "0 2px 8px rgba(0,0,0,0.45)"
    }}></th>

{/* PREVIEW COLUMN (new, far left) */}
<th style={{
  position: "sticky",
  top: 0,
  zIndex: 12,
  width: 36,
  background: "rgba(22,16,48,0.98)",
  color: "#fff",
  boxShadow: "0 2px 8px rgba(0,0,0,0.45)"
}}></th>

    {/* FLAG COLUMN - before DELETE */}
    <th style={{
      position: "sticky",
      top: 0,
      zIndex: 12,
      width: 40,
      background: "rgba(22,16,48,0.98)",
      color: "#fff",
      textAlign: "center",
      boxShadow: "0 2px 8px rgba(0,0,0,0.45)"
    }}>
      <span
        onClick={handleToggleAllFlags}
        title="Toggle all flags"
        style={{
          cursor: "pointer",
          fontSize: "1.2em",
          display: "inline-block",
          lineHeight: 1,
          filter: allQuestionRowsGreen
            ? "hue-rotate(0deg) saturate(1.2)" // All question rows green → next click toggles to red
            : "hue-rotate(120deg) saturate(1.3)" // Question rows red/mixed → next click toggles to green
        }}
      >
        🚩
      </span>
    </th>

    {/* DELETE COLUMN - far left */}
    <th style={{
      position: "sticky",
      top: 0,
      zIndex: 12,
      width: 32,
      background: "rgba(22,16,48,0.98)",
      color: "#fff",
      boxShadow: "0 2px 8px rgba(0,0,0,0.45)"
    }}></th>
    {/* DRAG HANDLE COLUMN */}
    <th style={{
      position: "sticky",
      top: 0,
      zIndex: 12,
      width: 32,
      background: "rgba(22,16,48,0.98)",
      color: "#fff",
      boxShadow: "0 2px 8px rgba(0,0,0,0.45)"
    }}></th>

    {/* NEW: Question number display-only column */}
    <th
      style={{
        position: "sticky",
        top: 0,
        zIndex: 12,
        width: 64,
        background: "rgba(22,16,48,0.98)",
        color: "#fff",
        fontWeight: 800,
        border: "1px solid #ccc",
        padding: "6px",
        textAlign: "center",
        boxShadow: "0 2px 8px rgba(0,0,0,0.45)"
      }}
    >
      Q#
    </th>

    {visibleHeaders.map(h => (
      <th
        key={h}
        style={{
          position: "sticky",
          top: 0,
          zIndex: 11,
          background: "rgba(22,16,48,0.98)",
          color: "#fff",
          fontWeight: 800,
          border: "1px solid #ccc",
          padding: "6px",
          ...csvHeaderColStyle(h),
          boxShadow: "0 2px 8px rgba(0,0,0,0.45)"
        }}
      >
        {h === "A1"
          ? "CORRECT"
          : h === "Q"
          ? "QUESTION/TITLE"
          : h === "BACKGROUND_URL"
          ? "BGROUND"
          : h === "SOUND_URL"
          ? "AUDIO"
          : h === "P1_URL"
          ? "MEDIA"
          : h}
        <span
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize ${h} column`}
          title="Drag to resize column"
          onMouseDown={(e) => onCsvColResizeMouseDown(h, e)}
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: 10,
            cursor: "col-resize",
            zIndex: 14,
            touchAction: "none",
            background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.06) 50%, transparent 100%)"
          }}
        />
      </th>
    ))}

    {/* Stats columns removed */}
  </tr>
</thead>


  <tbody>
    {rows.map((row, i) =>
      <Draggable key={row._dndId} draggableId={row._dndId} index={i} isDragDisabled={row.isLocked}>
        {(provided, snapshot) => (
<tr
  ref={(el) => { provided.innerRef(el); rowRefs.current[i] = el; }}
  {...provided.draggableProps}
  style={{
    ...provided.draggableProps.style,
    background: highlightedRow === i
      ? "linear-gradient(90deg, rgba(46,168,98,0.65), rgba(46,168,98,0.35))"
      : (snapshot.isDragging ? "#eef6e2" : ""),
    cursor: row.isLocked ? "not-allowed" : "grab",
    height: "48px",
    minHeight: "48px"
  }}>


             {/* COPY button (far left) */}
<td style={{ textAlign: "center", padding: 4 }}>
  <button
    title={row.isLocked ? "Copy disabled for this slide type" : "Copy this question into the next row"}
    onClick={(e) => { 
      e.preventDefault(); 
      if (!row.isLocked) handleCopyRow(i); 
    }}
    disabled={row.isLocked}
    style={{
      cursor: row.isLocked ? "not-allowed" : "pointer",
      fontWeight: 800,
      color: row.isLocked ? "#ddd" : "#fff",
      background: row.isLocked
        ? "linear-gradient(135deg,#555,#444)"
        : "linear-gradient(135deg,#2c7,#174)",
      border: "none",
      borderRadius: 8,
      padding: "4px 8px",
      boxShadow: row.isLocked ? "none" : "0 2px 8px #0006",
      opacity: row.isLocked ? 0.5 : 1
    }}
  >
    Copy
  </button>
</td>



{/* PREVIEW/EDIT icon button (opens the current preview/edit modal) */}
<td style={{ textAlign: "center", padding: 4 }}>
  <button
    title="Preview / Edit"
    aria-label="Preview / Edit"
    onClick={(e) => { e.preventDefault(); e.stopPropagation(); startEdit(i); }}
    style={{
      background: "linear-gradient(135deg,#4ea1ff,#2151b8)",
      border: "none",
      borderRadius: 8,
      padding: 6,
      cursor: "pointer",
      boxShadow: "0 2px 8px #0006",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 28,
      height: 28
    }}
  >
    {/* inline SVG eye icon */}
    <svg viewBox="0 0 24 24" width="18" height="18" fill="#fff" aria-hidden="true">
      <path d="M12 5C7 5 2.73 8.11 1 12c1.73 3.89 6 7 11 7s9.27-3.11 11-7c-1.73-3.89-6-7-11-7zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-2.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z"/>
    </svg>
  </button>
</td>





            {/* FLAG ICON - before DELETE */}
            <td style={{ textAlign: "center", padding: 4 }}>
              <span
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleToggleFlag(i);
                }}
                title={row.flag === true ? "Flagged (green) - Click to flag" : "Unflagged (red) - Click to unflag"}
                style={{
                  cursor: "pointer",
                  fontSize: "1.2em",
                  display: "inline-block",
                  lineHeight: 1,
                  filter: row.flag === true 
                    ? "hue-rotate(120deg) saturate(1.3)" // Green
                    : "hue-rotate(0deg) saturate(1.2)" // Red
                }}
              >
                🚩
              </span>
            </td>

            {/* DELETE BUTTON - far left */}
            <td>
              <button
                onClick={() => handleRemoveRow(i)}
                style={{
                  background: "#fdc82f",
                  color: "#900",
                  fontWeight: 900,
                  border: "none",
                  borderRadius: 8,
                  padding: "2px 11px",
                  margin: 2,
                  cursor: row.isLocked ? "not-allowed" : "pointer",
                  opacity: row.isLocked ? 0.3 : 1
                }}
                disabled={row.isLocked}
                >X</button>
            </td>
            {/* DRAG HANDLE */}
            <td {...provided.dragHandleProps} style={{ cursor: row.isLocked ? "not-allowed" : "grab", color: row.isLocked ? "#444" : "#aaa", fontWeight: 900, fontSize: "1.3em", userSelect: "none" }}>☰</td>
            {/* DISPLAY-ONLY QUESTION NUMBER (skips default/start/end) */}
<td
  style={{
    border: "1px solid #ddd",
    padding: 0,
    background: "rgba(10,14,32,0.55)",
    color: "#fff",
    fontFamily: "'Open Sans', Arial, sans-serif",
    fontWeight: 800,
    fontSize: "1.02em",
    textAlign: "center",
    verticalAlign: "middle",
    height: "48px",
    minHeight: "48px",
    width: 64
  }}
>
  {questionNumberByIndex[i] ?? ""}
</td>

            {visibleHeaders.map((col, colIdx) => {
              const catRaw = row.CAT || "";
              const category = catRaw.toUpperCase();
              const lockA2A6Cats = ["NEARESTANSWER", "BUZZER", "WHEEL"];
              const lockA1A6Cats = ["ENDROUND", "STARTROUND", "SHOWROUNDS", "DEFAULT CATEGORY", "JOIN QUIZ", "INTRODUCTION", "END QUIZ", "BREAK TIME"];
              
              // JACKPOT: disable all cells
              const isJackpot = category === "JACKPOT";

              const isTrueFalse = category === "TRUE/FALSE";
              const isLockA3A6TrueFalse = isTrueFalse && ["A3", "A4", "A5", "A6"].includes(col);

              const isLockA2A6 = lockA2A6Cats.includes(category) && ["A2", "A3", "A4", "A5", "A6"].includes(col);
              const isLockA1A6 = lockA1A6Cats.includes(category) && /^A[1-6]$/.test(col);

              const isStartEndOrDefault = lockA1A6Cats.includes(category);
              
              const isLockTime = isStartEndOrDefault && !['BREAK TIME', 'DEFAULT CATEGORY'].includes(category) && col === "TIME";
              const isLockPoints = isStartEndOrDefault && col === "POINTS"
              const isLockTimePoints = isLockTime || isLockPoints;
              
              const isBetAnswerPoints = category === "BETANSWER" && col === "POINTS";
              // JACKPOT: lock all cells except CAT (category selector itself)
              const isLockedAnswer = isJackpot && col !== "CAT" ? true : (isLockA2A6 || isLockA1A6 || isLockA3A6TrueFalse || isLockTimePoints || isBetAnswerPoints);

const isMediaCell = [
  "SOUND", "BACKGROUND", "P1", "VIDEO",
  "SOUND_URL", "BACKGROUND_URL", "P1_URL", "VIDEO_URL"
].includes(col);


              // ----- CAT COLUMN: DROPDOWN -----
// ----- CAT COLUMN: DROPDOWN -----
// ----- CAT COLUMN: DROPDOWN -----
// ----- CAT COLUMN: DROPDOWN -----
// ----- CAT COLUMN: DROPDOWN -----
if (col === "CAT") {
  const pal = getCatPalette(row[col] || row.CAT);
  return (
    <td
      key={col}
      style={{
        border: "1px solid #ddd",
        padding: 0,
        background: pal.bg,
        color: pal.fg,
        fontWeight: 800,
        fontSize: "1.13em",
        letterSpacing: "0.045em",
        fontFamily: "'Open Sans', Arial, sans-serif",
        textAlign: "center",
        verticalAlign: "middle",
        height: "48px",
        minHeight: "48px"
      }}
    >
      <select
        value={row[col] || ""}
        onChange={(e) => {
          const newCat = e.target.value;
          const newCatUpper = String(newCat).toUpperCase();

          if (DISABLED_CAT_SET.has(newCatUpper)) {
            e.target.value = row[col] || row.CAT || "";
            return;
          }

          const prevCat = row.CAT;
          const prevDef = CAT_DEFAULTS[prevCat] || {};
          const newDef  = CAT_DEFAULTS[newCatUpper] || {};

          const isEmpty = (v) =>
            v === undefined || v === null || v === "" || v === "0" || v === 0;

          const timeLooksAuto =
            isEmpty(row.TIME) ||
            (prevDef.TIME !== undefined && String(row.TIME) === String(prevDef.TIME));
          const pointsLooksAuto =
            isEmpty(row.POINTS) ||
            (prevDef.POINTS !== undefined && String(row.POINTS) === String(prevDef.POINTS));
          const qLooksAuto =
            !row.Q || (prevDef.Q && String(row.Q) === String(prevDef.Q));
          const notesLooksAuto =
            !row.NOTES || (prevDef.NOTES && String(row.NOTES) === String(prevDef.NOTES));

          const updates = { [col]: newCat };

          if (newDef.TIME !== undefined && timeLooksAuto)   updates.TIME   = String(newDef.TIME);
          if (newDef.POINTS !== undefined && pointsLooksAuto) updates.POINTS = String(newDef.POINTS);
          if (newDef.Q && qLooksAuto)                       updates.Q      = newDef.Q;
          if (newDef.NOTES && notesLooksAuto)               updates.NOTES  = newDef.NOTES;

          // NEW: when switching to TRUE/FALSE, initialize A1/A2 as TRUE/FALSE (once)
          if (newCatUpper === "TRUE/FALSE") {
            const a1 = String(row.A1 || "").toUpperCase();
            const a2 = String(row.A2 || "").toUpperCase();
            const isValidPair =
              (a1 === "TRUE" && a2 === "FALSE") || (a1 === "FALSE" && a2 === "TRUE");

            if (!isValidPair) {
              updates.A1 = "TRUE";
              updates.A2 = "FALSE";
            }
            // optional cleanup: clear any extra answers (A3..A6) for clarity
            updates.A3 = "";
            updates.A4 = "";
            updates.A5 = "";
            updates.A6 = "";
          }

          if (newCatUpper === "SHOWROUNDS") {
            updates.TIME   = "0";
            updates.POINTS = "0";
            updates.Q      = "Upcoming Rounds";
            const msg = "This slide will display all the rounds you have set up for your trivia";
            updates.NOTES = msg;
            try { openBobAlert(msg); } catch {}
          }

          if (newCatUpper === "JACKPOT") {
            updates.TIME   = "60";
            updates.POINTS = "0";
            updates.Q      = "Jackpot question - no points, scored manually";
            const msg = "JACKPOT questions have no points and are scored manually by the host";
            updates.NOTES = msg;
            try { openBobAlert(msg); } catch {}
          }

          if (newCatUpper === "BREADCRUMBS" || newCatUpper === "BRE" || newCatUpper === "OUTOFORDER") {
            updates.P1 = "";
            updates.P1_URL = "";
          }

          setRows((old) =>
            old.map((r, idx2) => (idx2 === i ? { ...r, ...updates } : r))
          );
        }}
        style={{
          width: "100%",
          border: "none",
          background: pal.bg,
          color: pal.fg,
          fontWeight: 800,
          fontSize: "1em",
          letterSpacing: "0.025em",
          fontFamily: "'Open Sans', Arial, sans-serif",
          textAlign: "center",
          padding: "4px",
          appearance: "none",
          WebkitAppearance: "none",
          MozAppearance: "none",
          cursor: row.isLocked ? "not-allowed" : "pointer"
        }}
        title="Choose a question type"
        disabled={row.isLocked}
      >
        {row.isLocked ? (
          <option value={row.CAT}>{renderCatLabel(row.CAT)}</option>
        ) : (
          <>
            <option value="" style={{ color: "#222", background: "#fff" }}>
              -- Select --
            </option>

            {catOptions.map((opt) => {
              const upper = String(opt).toUpperCase();
              const disabled = DISABLED_CAT_SET.has(upper);
              const palOpt = getCatPalette(opt);
              return (
                <option
                  key={opt}
                  value={opt}
                  disabled={disabled}
                  style={{
                    color: disabled ? "#888" : palOpt.fg,
                    background: palOpt.opt,
                    fontWeight: 800,
                    opacity: disabled ? 0.45 : 1,
                    cursor: disabled ? "not-allowed" : "pointer"
                  }}
                  title={
                    disabled ? "This type is not available in the editor" : undefined
                  }
                >
                  {renderCatLabel(opt)}
                </option>
              );
            })}
          </>
        )}
      </select>
    </td>
  );
}

              // ----- MEDIA/AUDIO/BGROUND CELLS (no text input) -----
// ----- MEDIA/AUDIO/BGROUND CELLS (no text input) -----
// ----- MEDIA/AUDIO/BGROUND CELLS (no text input) -----
// ----- MEDIA/AUDIO/BGROUND CELLS (no text input) -----
// ----- MEDIA/AUDIO/BGROUND CELLS (no text input) -----
// ----- MEDIA/AUDIO/BGROUND CELLS (no text input) -----
if (isMediaCell) {
  // Black out P1 + P1_URL for WHEEL, BREADCRUMBS, OUT OF ORDER (no image/media; CSV may use "BRE")
  const rowCatU = canonicalCategoryUpper((row?.CAT ?? category) || "");
  const disableP1ByQuestionType =
    isP1MediaBlackoutType(row?.CAT ?? category) && (col === "P1" || col === "P1_URL");
  const disableSlideRowMedia =
    isTroveSlideMediaBlackoutCat(category) && (col === "P1" || col === "P1_URL" || col === "VIDEO_URL");


return (
  <td
    key={col}
    style={{
      border: "1px solid #ddd",
      padding: 0,
      background: disableP1ByQuestionType || disableSlideRowMedia
        ? "rgba(10,14,32,0.93)"
        : (isLockedAnswer
            ? (isLockA3A6TrueFalse ? "#000" : "rgba(10,14,32,0.93)")
            : "none"),
      color: "#fff",
      fontFamily: "'Open Sans', Arial, sans-serif",
      fontWeight: 600,
      fontSize: "1em",
      textAlign: "center",
      verticalAlign: "middle",
      height: "48px",
      minHeight: "48px",
      cursor: disableP1ByQuestionType || disableSlideRowMedia ? "not-allowed" : "pointer"
    }}
    onClick={(e) => {
      if (disableP1ByQuestionType || disableSlideRowMedia) return;

      let accept = "";
      if (col.startsWith("SOUND")) {
        accept = ".mp3,.wav,.ogg,.aac,.m4a,.flac";
      } else if (col.startsWith("P1")) {
        accept = ".jpg,.jpeg,.png,.gif,.bmp,.webp,.svg,.mp4,.webm,.mov,.m4v,.avi,.wmv,.mkv";
      } else if (col.startsWith("BACKGROUND")) {
        accept = ".jpg,.jpeg,.png,.gif,.bmp,.webp,.svg";
      } else if (col.startsWith("VIDEO")) {
        accept = ".mp4,.webm,.mov,.m4v,.avi,.wmv,.mkv";
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
        if (accept) fileInputRef.current.setAttribute("accept", accept);
      }
      setMediaTarget({ row: i, col });
      fileInputRef.current?.click();
    }}
  >
    {/* MEDIA preview (images or videos) */}
    {!disableP1ByQuestionType && !disableSlideRowMedia && col === "P1_URL" && row[col] && row[col].startsWith("http") && (
      <LazyMedia
        kind={urlLooksVideo(row[col]) ? "video" : "image"}
        src={row[col]}
        onClick={() => setPreviewImg({ url: row[col], rowIdx: i, col })}
      />
    )}

    {/* BACKGROUND preview (image only) */}
    {!disableP1ByQuestionType && !disableSlideRowMedia && col === "BACKGROUND_URL" && row[col] && row[col].startsWith("http") && (
      <LazyMedia
        kind="image"
        src={row[col]}
        onClick={() => setPreviewImg({ url: row[col], rowIdx: i, col })}
      />
    )}

{/* AUDIO preview — icon triggers the preview overlay */}
{!disableP1ByQuestionType && !disableSlideRowMedia && col === "SOUND_URL" && row[col] && row[col].startsWith("http") && (
  <div
    title="Preview audio"
    role="button"
    aria-label="Preview audio"
    onClick={(e) => { e.stopPropagation(); setPreviewImg({ url: row[col], rowIdx: i, col }); }}
    style={{
      width: "100%",
      height: "48px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "zoom-in"
    }}
  >
    {/* Speaker with sound waves icon */}
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block", filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.4))" }}
    >
      <path d="M3 10v4h3l4 4V6L6 10H3z" fill="#ffffff"/>
      <circle cx="16.5" cy="12" r="3.5" fill="none" stroke="#ffffff" strokeWidth="2"/>
      <path d="M19.5 6.5a7 7 0 010 11" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  </div>
)}



    {/* VIDEO preview (tiny posterless tile) */}
    {!disableP1ByQuestionType && !disableSlideRowMedia && col === "VIDEO_URL" && row[col] && row[col].startsWith("http") && (
      <LazyMedia
        kind="video"
        src={row[col]}
        onClick={() => setPreviewImg({ url: row[col], rowIdx: i, col })}
      />
    )}

    {/* Loading or filename (only when not a *_URL cell) */}
    {!disableP1ByQuestionType && !disableSlideRowMedia && (
      uploadingMedia.row === i && uploadingMedia.col === col ? (
        <span style={{ color: "#0ff", fontWeight: 700, fontSize: "0.95em" }}>Loading...</span>
      ) : (
        !col.endsWith("_URL") && row[col] && !row[col].startsWith("http") && (
          <span style={{ color: "#ffe066", fontSize: "0.95em", wordBreak: "break-all" }}>
            {row[col]}
          </span>
        )
      )
    )}

    {disableP1ByQuestionType && (
      <span style={{ color: "#aaa", fontWeight: 700, fontSize: "0.95em" }}>
        {mediaDisabledLabelForCategory(rowCatU)}
      </span>
    )}
  </td>
);
}



// ----- ALL OTHER COLUMNS -----
return (
  <td
    key={col}
    style={{
      border: "1px solid #ddd",
      padding: 0,
      background:
        isLockedAnswer
          ? (isLockA3A6TrueFalse ? "#000" : "rgba(10,14,32,0.93)")
          : "none",
      color: "#fff",
      fontFamily: "'Open Sans', Arial, sans-serif",
      fontWeight: 600,
      fontSize: "1em",
      textAlign: "center",
      verticalAlign: "middle",
      height: "48px",
      minHeight: "48px",
      cursor: isLockedAnswer ? "not-allowed" : "auto",
      position: "relative"
    }}
  >
    {/* TRUE/FALSE CAT: A1 or A2: SPECIAL DROPDOWN */}
    {isTrueFalse && (col === "A1" || col === "A2") ? (
      <select
        value={row[col] || "TRUE"}
        onChange={e => {
          const val = e.target.value;
          setRows(oldRows =>
            oldRows.map((r, j) => {
              if (j !== i) return r;
              const update = { ...r };
              if (col === "A1") {
                update.A1 = val;
                update.A2 = val === "TRUE" ? "FALSE" : "TRUE";
              } else {
                update.A2 = val;
                update.A1 = val === "TRUE" ? "FALSE" : "TRUE";
              }
              return update;
            })
          );
        }}
        style={{
          width: "100%",
          border: "none",
          background: "#fff",
          color: "#111",
          fontFamily: "'Open Sans', Arial, sans-serif",
          fontWeight: 600,
          fontSize: "1em",
          letterSpacing: "0.03em",
          textAlign: "center",
          borderRadius: "6px"
        }}
        disabled={isLockedAnswer}
      >
        <option value="TRUE">TRUE</option>
        <option value="FALSE">FALSE</option>
      </select>
    ) : (
      // INPUT FIELD: default for everything else (including NOTES)
      (() => {
        const isNearestA1 = category === "NEARESTANSWER" && col === "A1";
        return (
          <>
            <input
              ref={col === "Q" ? (el) => {
                // Store ref for genre dropdown (no state updates to avoid infinite loops)
                if (el) {
                  genreInputRefs.current[i] = el;
                } else {
                  delete genreInputRefs.current[i];
                }
              } : undefined}
              type="text"
              value={row[col] ?? ""}

              onMouseDownCapture={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onKeyDownCapture={(e) => {
                e.stopPropagation();
                // Show genre dropdown when # is typed in Q column
                if (col === "Q" && e.key === "#" && !isLockedAnswer) {
                  const inputEl = e.target;
                  const rect = inputEl.getBoundingClientRect();
                  genreInputRefs.current[i] = inputEl; // Store ref
                  setGenreDropdown({
                    show: true,
                    rowIdx: i,
                    position: { top: rect.bottom + 2, left: rect.left }
                  });
                }
              }}
              onKeyUpCapture={(e) => e.stopPropagation()}

              // Show popup the instant the user would exceed the limit
              onBeforeInput={(e) => warnAndBlockIfOver(e, row[col] ?? "", col)}
              onPaste={(e) => {
                const paste = e.clipboardData?.getData?.("text") ?? "";
                warnAndBlockIfOver(e, row[col] ?? "", col, paste);
              }}

              // NEARESTANSWER A1 numeric guard
              inputMode={isNearestA1 ? "decimal" : undefined}
              pattern={isNearestA1 ? "-?[0-9]*\\.?[0-9]*" : undefined}

              onChange={(e) => {
                let val = e.target.value;

                if (col === "TIME" || col === "POINTS") {
                  if (/^\d*$/.test(val)) handleFieldChange(i, col, val);
                  return;
                }
                if (isNearestA1 && !/^-?\d*\.?\d*$/.test(val) && val !== "") return;

               if (/^A[1-6]$/.test(col)) {
                const [limited] = limitAnswer(val);
                val = limited;
              }

              // --- THIS IS THE NEW LOGIC ---
              if (col === 'Q' && val.length > MAX_QUESTION_CHARS) {
                openBobAlert(`The question limit is ${MAX_QUESTION_CHARS} characters.`);
                val = val.slice(0, MAX_QUESTION_CHARS);
              }
              // --- END OF NEW LOGIC ---

              if (!isLockedAnswer || isNearestA1) {
                handleFieldChange(i, col, val);
              }
            }}
            onBlur={checkForCsvChanges}
              disabled={isLockedAnswer && !isNearestA1}
              style={{
                width: "100%",
                border: "none",
                background: "transparent",
                color: isLockedAnswer && !isNearestA1 ? "#777" : "#fff",
                fontFamily: "'Open Sans', Arial, sans-serif",
                fontWeight: 600,
                fontSize: "1em",
                padding: "4px",
                cursor: isLockedAnswer && !isNearestA1 ? "not-allowed" : "auto",
                opacity: isLockedAnswer && !isNearestA1 ? 0.89 : 1,
                letterSpacing: "0.03em",
                textAlign: col === "TIME" || col === "POINTS" ? "center" : "left"
              }}
            />
            {/* Genre Dropdown - shown when # is typed in Q column */}
            {col === "Q" && genreDropdown.show && genreDropdown.rowIdx === i && (
              <div
                data-genre-dropdown
                style={{
                  position: "fixed",
                  top: genreDropdown.position?.top || 0,
                  left: genreDropdown.position?.left || 0,
                  zIndex: 10000,
                  background: "rgba(20,20,30,0.95)",
                  border: "1px solid #888",
                  borderRadius: 8,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
                  maxHeight: "300px",
                  overflowY: "auto",
                  minWidth: "200px"
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {genreOptions.map((genre) => (
                  <div
                    key={genre}
                    onClick={() => {
                      const currentValue = row[col] ?? "";
                      const newValue = currentValue + "#" + genre;
                      handleFieldChange(i, col, newValue);
                      const inputRef = genreInputRefs.current[i];
                      setGenreDropdown({ show: false, rowIdx: null, position: null });
                      // Focus back on input after selection
                      setTimeout(() => {
                        if (inputRef) {
                          inputRef.focus();
                        }
                      }, 0);
                    }}
                    style={{
                      padding: "8px 12px",
                      color: "#fff",
                      cursor: "pointer",
                      fontFamily: "'Open Sans', Arial, sans-serif",
                      fontSize: "0.9em",
                      borderBottom: "1px solid rgba(255,255,255,0.1)"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(97,218,251,0.3)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    {genre}
                  </div>
                ))}
              </div>
            )}
            {isJackpot && col !== "CAT" && (
              <span style={{ color: "#aaa", fontWeight: 700, fontSize: "0.85em", position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", pointerEvents: "none" }}>
                JACKPOT (disabled)
              </span>
            )}
          </>
        );
      })()
    )}
  </td>
);



            })}
            {/* Stats columns removed */}
          </tr>
        )}
      </Draggable>
    )}
    {provided.placeholder}
  </tbody>
</table>






              )}
            </Droppable>
          </DragDropContext>
<button
  onClick={handleAddRow}



  
  style={{
    margin: "16px 0 0 0",
    background: "rgba(58,128,255,0.19)",
    color: "#fff",
    fontWeight: 800,
    fontSize: "1.13em",
    fontFamily: "'Montserrat','Open Sans',Arial,sans-serif",
    border: "none",
    borderRadius: "18px",
    padding: "8px 28px",
    boxShadow: "0 2px 12px #48f8, 0 0 0 2px #20a2ff55",
    textShadow: "0 2px 8px #233, 0 1px 10px #36d, 0 1px 0px #fff8",
    transition: "background 0.17s, color 0.11s",
    cursor: "pointer"
  }}>
  + Add Row
</button>





        </div>
      </div>


{previewImg?.url && (
  <div
    onClick={() => setPreviewImg(null)}
    style={{
      position: "fixed",
      top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.8)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 2000,
      cursor: "zoom-out"
    }}
  >
    <div
      style={{ position: "relative", display: "inline-block" }}
      onClick={(e) => e.stopPropagation()} // keep clicks on controls from closing the overlay
    >
      {/* Close Icon */}
      <button
        aria-label="Close"
        onClick={(e) => { e.stopPropagation(); setPreviewImg(null); }}
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          background: "rgba(0,0,0,0.58)",
          color: "#fff",
          border: "none",
          borderRadius: "50%",
          width: 36,
          height: 36,
          fontSize: 23,
          fontWeight: 900,
          cursor: "pointer",
          zIndex: 21,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 1px 8px #000a"
        }}
      >
        ×
      </button>

      {/* Delete button (label switches for audio) */}
      <button
        style={{
          position: "absolute",
          top: 8,
          right: 52,
          background: "#e24",
          color: "#fff",
          fontWeight: 900,
          fontSize: "1.01em",
          padding: "6px 16px",
          border: "none",
          borderRadius: "14px",
          boxShadow: "0 2px 12px #0007",
          cursor: "pointer",
          zIndex: 20
        }}
        onClick={(e) => { e.stopPropagation(); handleDeleteMedia(); }}
      >
        {previewImg?.col === "SOUND_URL" ? "Delete Audio" : "Delete Media"}
      </button>

{previewImg?.col === "BACKGROUND_URL" && (
  <button
              style={{
            position: "absolute",
            top: 8,
            left: 8,
            background: "#47e373",
            color: "#111",
            fontWeight: 900,
            fontSize: "1.01em",
            padding: "6px 16px",
            border: "none",
            borderRadius: "14px",
            boxShadow: "0 2px 12px #0007",
            cursor: "pointer",
            zIndex: 20
          }}
    onClick={(e) => {
      e.stopPropagation();
      openBobConfirm(
        "This will replace the background image for EVERY record in this quiz. Continue?",
        () => applyBackgroundToAll(previewImg.url)
      );
    }}
    title="Use this image as the background for every row"
  >
    Use this image for every Question
  </button>
)}




      {/* Media body */}
      {previewImg?.col === "SOUND_URL" ? (
        <div
          style={{
            width: "760px",
            maxWidth: "90vw",
            background: "rgba(15,18,28,0.96)",
            borderRadius: 14,
            boxShadow: "0 12px 40px rgba(0,0,0,0.6)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column"
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 8,
              padding: "10px 12px",
              background: "rgba(255,255,255,0.06)"
            }}
          >
            <button
              style={{
                background: "#e24",
                color: "#fff",
                fontWeight: 900,
                fontSize: "0.95rem",
                padding: "6px 14px",
                border: "none",
                borderRadius: 12,
                boxShadow: "0 2px 10px rgba(0,0,0,0.5)",
                cursor: "pointer"
              }}
              onClick={(e) => { e.stopPropagation(); handleDeleteMedia(); }}
            >
              Delete Audio
            </button>
            <button
              aria-label="Close"
              onClick={(e) => { e.stopPropagation(); setPreviewImg(null); }}
              style={{
                background: "rgba(0,0,0,0.45)",
                color: "#fff",
                border: "none",
                borderRadius: 12,
                width: 36,
                height: 36,
                fontSize: 20,
                fontWeight: 900,
                cursor: "pointer"
              }}
            >
              ×
            </button>
          </div>

          <div style={{ padding: 16 }}>
            <audio
              src={previewImg.url}
              controls
              autoPlay
              style={{
                width: "100%",
                height: 40,
                display: "block",
                borderRadius: 8,
                background: "transparent"
              }}
            />
          </div>
        </div>
      ) : urlLooksVideo(previewImg.url) ? (
        <video
          src={previewImg.url}
          controls
          autoPlay
          style={{
            maxWidth: "85vw",
            maxHeight: "85vh",
            borderRadius: 12,
            boxShadow: "0 0 44px #000a",
            display: "block"
          }}
        />
      ) : (
        <img
          src={previewImg.url}
          alt=""
          style={{
            maxWidth: "85vw",
            maxHeight: "85vh",
            borderRadius: 12,
            boxShadow: "0 0 44px #000a",
            display: "block"
          }}
        />
      )}
    </div>
  </div>
)}





{editRowIdx !== null && editDraft && (() => {
  // Check if we're showing a preview question from hashtag
  const currentPreviewQuestion = hashtagGenre && previewQuestions.length > 0 && previewQuestions[previewQuestionIndex] 
    ? previewQuestions[previewQuestionIndex] 
    : null;
  
  // Use preview question data if available, otherwise use editDraft
  const displayData = currentPreviewQuestion ? {
    ...editDraft,
    Q: currentPreviewQuestion.question || "",
    CAT: currentPreviewQuestion.questionType || editDraft.CAT,
    A1: currentPreviewQuestion.answer || "",
    A2: currentPreviewQuestion.A2 || "",
    A3: currentPreviewQuestion.A3 || "",
    A4: currentPreviewQuestion.A4 || "",
    A5: currentPreviewQuestion.A5 || "",
    A6: currentPreviewQuestion.A6 || "",
    A7: currentPreviewQuestion.A7 || "",
    A8: currentPreviewQuestion.A8 || "",
    A9: currentPreviewQuestion.A9 || "",
    A10: currentPreviewQuestion.A10 || "",
    A11: currentPreviewQuestion.A11 || "",
    P1_URL: currentPreviewQuestion.Medialink || editDraft.P1_URL || "",
    SOUND_URL: currentPreviewQuestion.Audiolink || editDraft.SOUND_URL || "",
    SPOTIFY_URL: currentPreviewQuestion.SpotifyUrl || editDraft.SPOTIFY_URL || "",
    BACKGROUND_URL: currentPreviewQuestion.backgroundUrl || editDraft.BACKGROUND_URL || "",
    NOTES: currentPreviewQuestion.notes || editDraft.NOTES || "",
    visibleExplanation: currentPreviewQuestion.visibleExplanation || editDraft.visibleExplanation || ""
  } : editDraft;

  const q = rowToQ(displayData);
  const isDisplay = ["DISPLAY","DEFAULT","SLIDE","DEFAULT CATEGORY", "JOIN QUIZ", "INTRODUCTION", "END QUIZ", "BREAK TIME"].includes(String(displayData.CAT||"").toUpperCase()) || ((Number.parseInt(displayData.TIME,10)||0)===0 && [displayData.A1,displayData.A2,displayData.A3,displayData.A4,displayData.A5,displayData.A6,displayData.A7,displayData.A8,displayData.A9,displayData.A10,displayData.A11].every(a => !String(a||"").trim()));
  // CSV may store Breadcrumbs as "BRE" or "BREADCRUMBS" — must match for layout, P1, and DnD
  const typeUpper = canonicalCategoryUpper(displayData.CAT || q.type || "");
  // WHEEL / BREADCRUMBS / OUT OF ORDER: no P1 / side image in this editor (same as live)
  const p1Url = isP1MediaBlackoutType(typeUpper) ? "" : (q.P1_URL || "");
  const answers = q.answers;
  const answersOrder = q.answersOrder;
  const timerNum = Number.parseInt(displayData.TIME, 10) || 0;
  const isPreviewMode = !!currentPreviewQuestion;
  const isBreadcrumbEditModal = isBreadcrumbsCategory(typeUpper);

  const applyFromLibrary = (libItem) => {
    // Map into the active draft
    setEditDraft(prev => mapLibraryItemToRow(prev || {}, libItem));
    // Mirror into the table row immediately (your file already mirrors editDraft => rows in another effect, but we also force it here)
    setRows(prev => prev.map((r, idx) => (idx === editRowIdx ? mapLibraryItemToRow(r, libItem) : r)));
    // Check for changes after state updates
    setTimeout(() => checkForCsvChanges(), 0);
    setShowLib(false);
  };

  return (
    <div
      onClick={closeEdit}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.72)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 4000
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        ref={previewWrapRef}
        className="quiz-bg"
        style={{
          width: "min(92vw, 96vw)",
          maxHeight: "calc(95vh - 2px)",
          overflowX: "hidden",
          overflowY: String((displayData?.CAT || editDraft?.CAT || "")).toUpperCase() === "SHOWROUNDS"
            ? "hidden"
            : (isBreadcrumbEditModal ? "hidden" : "auto"),
          boxSizing: "border-box",
          background: editDraft.BACKGROUND_URL
            ? `linear-gradient(rgba(0,0,0,0.45),rgba(0,0,0,0.45)),url(${editDraft.BACKGROUND_URL}) center center / cover no-repeat`
            : "#0e1222",
          border: "2px solid #2b64d8",
          borderRadius: 16,
          boxShadow: "0 10px 50px #000a",
          padding: "12px 16px 28px 16px",
          color: "#fff",
          fontFamily: "'Open Sans', Arial, sans-serif",
          position: "relative",
          ...(isBreadcrumbEditModal ? {
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            // Keep the popup footprint consistent with other question types.
            // Breadcrumbs should use the same near-full-screen modal height, not shrink to content.
            height: "calc(95vh - 2px)"
          } : {})
        }}
      >
        <div
          ref={isBreadcrumbEditModal ? previewBreadcrumbsScrollRef : undefined}
          style={isBreadcrumbEditModal ? {
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            position: "relative",
            width: "100%"
          } : { display: "contents" }}
        >

        {/* Type ribbon (unchanged) */}
        {(() => {
          const catRaw = String(displayData?.CAT || editDraft?.CAT || "");
          const label  = renderCatLabel(catRaw);
          const pal    = getCatPalette(catRaw);
          return (
            <div
              style={{
                position: "absolute",
                top: 12,
                right: 16,
                background: pal.bg,
                color: pal.fg,
                borderRadius: 12,
                padding: "6px 14px",
                fontWeight: 900,
                letterSpacing: ".03em",
                textTransform: "uppercase",
                boxShadow: "0 2px 10px #0007",
                zIndex: 40,
                whiteSpace: "nowrap"
              }}
            >
              {label}
            </div>
          );
        })()}

        {/* TIME/POINTS editor - Hidden in dynamic/hashtag mode */}
        {(() => {
          const category = String(displayData?.CAT || editDraft?.CAT || "").toUpperCase();
          const lockA1A6Cats = [
            "ENDROUND","STARTROUND","SHOWROUNDS",
            "DEFAULT CATEGORY","JOIN QUIZ","INTRODUCTION","END QUIZ","BREAK TIME"
          ];
          const isStartEndOrDefault = lockA1A6Cats.includes(category);
          // Disable TIME and POINTS in hashtag/dynamic question mode (they come from template, not database)
          const isDynamicMode = hashtagGenre || previewQuestions.length > 0;
          const disableTime   = isDynamicMode || (isStartEndOrDefault && !['BREAK TIME', 'DEFAULT CATEGORY'].includes(category));
          const disablePoints = isDynamicMode || (isStartEndOrDefault && category !== "BREAK TIME") || category === "BETANSWER";
          
          // Hide entire TIME/POINTS section in dynamic mode
          if (isDynamicMode) {
            return null;
          }
          
          return (
            <div
              style={{
                position: "absolute",
                top: 12,
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                gap: 10,
                alignItems: "center",
                zIndex: 35
              }}
            >
              <label style={{ fontWeight: 800, opacity: 0.9 }}>TIME</label>
              <input
                value={editDraft.TIME ?? ""}
                onChange={(e) => {
                  if (disableTime) return;
                  if (/^\d*$/.test(e.target.value)) {
                    setEditDraft((d) => ({ ...d, TIME: e.target.value }));
                  }
                }}
                onBlur={checkForCsvChanges}
                disabled={disableTime}
                style={{
                  width: 80,
                  borderRadius: 8,
                  border: "1px solid #3b4a6a",
                  padding: "6px 8px",
                  background: disableTime ? "rgba(255,255,255,0.10)" : "rgba(255,255,255,0.12)",
                  color: disableTime ? "#888" : "#fff",
                  fontWeight: 800,
                  cursor: disableTime ? "not-allowed" : "text",
                  opacity: disableTime ? 0.7 : 1
                }}
                inputMode="numeric"
                pattern="\d*"
                placeholder="secs"
                title={disableTime ? "Disabled for this type" : "Time for this question (seconds)"}
              />

              <label style={{ fontWeight: 800, opacity: 0.9 }}>POINTS</label>
              <input
                value={editDraft.POINTS ?? ""}
                onChange={(e) => {
                  if (disablePoints) return;
                  if (/^\d*$/.test(e.target.value)) {
                    setEditDraft((d) => ({ ...d, POINTS: e.target.value }));
                  }
                }}
                onBlur={checkForCsvChanges}
                disabled={disablePoints}
                style={{
                  width: 80,
                  borderRadius: 8,
                  border: "1px solid #3b4a6a",
                  padding: "6px 8px",
                  background: disablePoints ? "rgba(255,255,255,0.10)" : "rgba(255,255,255,0.12)",
                  color: disablePoints ? "#888" : "#fff",
                  fontWeight: 800,
                  cursor: disablePoints ? "not-allowed" : "text",
                  opacity: disablePoints ? 0.7 : 1
                }}
                inputMode="numeric"
                pattern="\d*"
                placeholder="pts"
                title={disablePoints ? (String(editDraft?.CAT || "").toUpperCase() === "BETANSWER" ? "Points fixed for BETANSWER" : "Disabled for this type") : "Points for this question"}
              />

              {/* JIGSAW column: only when the row has its own picture. Off by default. */}
              {(String(editDraft.P1_URL || "").trim() || String(editDraft.P1 || "").trim()) && !isP1MediaBlackoutType(editDraft.CAT) && (
                <label
                  style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 800, opacity: 0.9, cursor: readOnly ? "default" : "pointer" }}
                  title="Turn on only when the picture is a clue: it starts covered and uncovers as the clock runs."
                >
                  <input
                    type="checkbox"
                    checked={isJigsawOn(editDraft)}
                    disabled={readOnly}
                    onChange={(e) => {
                      const val = e.target.checked ? "Yes" : "";
                      setEditDraft((d) => ({ ...(d || {}), JIGSAW: val }));
                      setRows((prev) => prev.map((r, idx) => (idx === editRowIdx ? { ...r, JIGSAW: val } : r)));
                      setTimeout(() => checkForCsvChanges(), 0);
                    }}
                  />
                  Jigsaw reveal
                </label>
              )}
            </div>
          );
        })()}

        {/* Prev/Next/Close - Hide Prev/Next in hashtag mode */}
        <div style={{ display: "flex", gap: 10, position: "absolute", left: 16, top: 12, alignItems: "center" }}>
          {!hashtagGenre && (
            <>
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); gotoPrevRow(); }}
                title="Previous question in template"
                style={{
                  background: "rgba(255,255,255,0.10)",
                  color: "#fff",
                  border: "1px solid #888",
                  borderRadius: 10,
                  padding: "8px 12px",
                  fontWeight: 800,
                  cursor: "pointer"
                }}
              >
                ◀ Prev
              </button>

              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); gotoNextRow(); }}
                title="Next question in template"
                style={{
                  background: "rgba(255,255,255,0.10)",
                  color: "#fff",
                  border: "1px solid #888",
                  borderRadius: 10,
                  padding: "8px 12px",
                  fontWeight: 800,
                  cursor: "pointer"
                }}
              >
                Next ▶
              </button>
            </>
          )}

          {/* Edit in Library Viewer and Venue Usage - Show in top-left when in hashtag mode */}
          {hashtagGenre && previewQuestions.length > 0 && previewQuestions[previewQuestionIndex] && (() => {
            const previewQ = previewQuestions[previewQuestionIndex];
            return (
              <>
                {previewQ.id && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Open library viewer with questionId - LibraryViewer will handle navigation
                      window.open(`/library-viewer?questionId=${previewQ.id}`, '_blank');
                    }}
                    style={{
                      background: "rgba(97,218,251,0.85)",
                      color: "#111",
                      border: "none",
                      borderRadius: 10,
                      padding: "8px 14px",
                      fontWeight: 800,
                      cursor: "pointer",
                      fontSize: "0.9rem",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
                    }}
                    title="Open in Library Viewer (new tab)"
                  >
                    Edit in Library Viewer
                  </button>
                )}

                {/* Venue Usage History Dropdown */}
                {previewQ.usageHistory && (
                  <select
                    style={{
                      background: "rgba(20,20,30,0.85)",
                      color: "#fff",
                      border: "1px solid #888",
                      borderRadius: 8,
                      padding: "6px 12px",
                      fontWeight: 700,
                      cursor: "pointer",
                      fontSize: "0.85rem"
                    }}
                    title="Venues where this question has been used"
                    defaultValue=""
                    onChange={() => {}} // Read-only dropdown, prevent React warning
                  >
                    <option value="">
                      {previewQ.usageHistory.length > 0 
                        ? "Used at venues..." 
                        : "Not used at any venue yet"}
                    </option>
                    {previewQ.usageHistory.map((entry, idx) => {
                      const venueName = entry.location?.venueId || 
                                       (typeof entry.location === 'string' ? entry.location : 'Unknown Venue');
                      const usedAt = entry.usedAt?.toDate ? entry.usedAt.toDate() : new Date(entry.usedAt || 0);
                      const dateStr = usedAt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                      return (
                        <option key={idx} value={venueName}>
                          {venueName} - {dateStr}
                        </option>
                      );
                    })}
                  </select>
                )}
              </>
            );
          })()}

          {/* Save button - only show in hashtag mode */}
          {hashtagGenre && previewQuestions.length > 0 && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                savePreviewQuestionChanges();
              }}
              disabled={!hasChanges}
              style={{
                background: hasChanges ? "rgba(71,227,115,0.85)" : "rgba(255,255,255,0.1)",
                color: hasChanges ? "#111" : "#888",
                border: "1px solid #888",
                borderRadius: 10,
                padding: "8px 16px",
                fontWeight: 800,
                cursor: hasChanges ? "pointer" : "not-allowed",
                opacity: hasChanges ? 1 : 0.5
              }}
            >
              Save
            </button>
          )}

          <button
            onClick={closeEdit}
            style={{
              background: "rgba(255,255,255,0.1)",
              color: "#fff",
              border: "1px solid #888",
              borderRadius: 10,
              padding: "8px 16px",
              fontWeight: 800,
              cursor: "pointer"
            }}
          >
            Close
          </button>
        </div>

        {/* Interest Rating Badge - Immediately to the right of POINTS input */}
        {hashtagGenre && previewQuestions.length > 0 && previewQuestions[previewQuestionIndex]?.interestRating !== undefined && (
          <>
            <div style={{
              position: "absolute",
              top: 12,
              left: "calc(50% + 105px)", // 50% center + half of TIME/POINTS section width (~210px total) + gap (10px)
              display: "flex",
              alignItems: "center",
              gap: 4,
              zIndex: 37 // Higher than flag field so it appears on top
            }}>
              <label style={{
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.85rem"
              }}>
                ⭐
              </label>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowRatingSlider(true);
                }}
                style={{
                  background: "rgba(255,193,7,0.85)",
                  color: "#111",
                  border: "1px solid #888",
                  borderRadius: 8,
                  padding: "4px 12px",
                  fontWeight: 800,
                  fontSize: "0.85rem",
                  width: "60px",
                  textAlign: "center",
                  cursor: "pointer"
                }}
              >
                {previewQuestions[previewQuestionIndex].interestRating || 0}
              </button>
            </div>
            
            {/* Rating Slider Popup */}
            {showRatingSlider && (
              <div
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setShowRatingSlider(false);
                  }
                }}
                style={{
                  position: "fixed",
                  inset: 0,
                  background: "rgba(0,0,0,0.7)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10000,
                  backdropFilter: "blur(2px)"
                }}
              >
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    background: "linear-gradient(180deg, rgba(28,34,52,0.95), rgba(18,20,34,0.95))",
                    border: "1px solid rgba(255,255,255,0.18)",
                    borderRadius: 14,
                    padding: "24px 32px",
                    boxShadow: "0 22px 60px rgba(0,0,0,0.6)",
                    minWidth: "300px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 16
                  }}
                >
                  <div style={{ color: "#fff", fontWeight: 800, fontSize: "1.2rem", textAlign: "center" }}>
                    Interest Rating
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <input
                      type="range"
                      min="1"
                      max="100"
                      value={previewQuestions[previewQuestionIndex]?.interestRating || 0}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10) || 0;
                        const clamped = Math.max(1, Math.min(100, val));
                        setPreviewQuestions(prev => {
                          const updated = updatePreviewQuestionLocal(prev, previewQuestionIndex, { interestRating: clamped });
                          return updated;
                        });
                      }}
                      style={{
                        width: "100%",
                        height: "8px",
                        borderRadius: 4,
                        background: "#333",
                        outline: "none",
                        cursor: "pointer"
                      }}
                    />
                    <div style={{ display: "flex", justifyContent: "space-between", color: "#fff", fontSize: "0.9rem" }}>
                      <span>1</span>
                      <span style={{ fontWeight: 800, fontSize: "1.5rem" }}>
                        {previewQuestions[previewQuestionIndex]?.interestRating || 0}
                      </span>
                      <span>100</span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowRatingSlider(false);
                    }}
                    style={{
                      background: "rgba(71,227,115,0.85)",
                      color: "#111",
                      border: "none",
                      borderRadius: 10,
                      padding: "10px 20px",
                      fontWeight: 800,
                      cursor: "pointer",
                      fontSize: "1rem"
                    }}
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Flag Field - Top Right, with gap from question type badge */}
        {hashtagGenre && previewQuestions.length > 0 && previewQuestions[previewQuestionIndex] && (
          <div style={{
            position: "absolute",
            top: 12,
            right: 260, // Positioned with gap from question type badge (which is at right: 16, so gap = 244px)
            display: "flex",
            alignItems: "center",
            gap: 8,
            zIndex: 36 // Below rating badge so rating doesn't hide behind
          }}>
            <label style={{
              color: "#fff",
              fontWeight: 700,
              fontSize: "0.85rem",
              marginRight: 4
            }}>
              Flag:
            </label>
            <input
              type="text"
              value={previewQuestions[previewQuestionIndex].accuracyFlag || ""}
              onChange={(e) => {
                const val = e.target.value;
                setPreviewQuestions(prev => {
                  const updated = updatePreviewQuestionLocal(prev, previewQuestionIndex, { accuracyFlag: val });
                  return updated;
                });
              }}
              placeholder="No flag"
              style={{
                background: "rgba(20,20,30,0.85)",
                color: "#fff",
                border: "1px solid #888",
                borderRadius: 8,
                padding: "6px 12px",
                fontWeight: 700,
                fontSize: "0.85rem",
                width: "180px"
              }}
            />
          </div>
        )}

        {/* Hashtag Preview Controls */}
        {hashtagGenre && (
          <div style={{
            position: "absolute",
            top: 50,
            left: 16,
            right: 16,
            display: "flex",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
            zIndex: 35,
            background: "rgba(0,0,0,0.3)",
            borderRadius: 8,
            padding: "8px 12px"
          }}>
            {/* Venue Selector */}
            <select
              value={selectedVenue || ""}
              onChange={(e) => {
                const newVenue = e.target.value;
                setSelectedVenue(newVenue);
                if (newVenue) {
                  localStorage.setItem("opt_venue", newVenue);
                } else {
                  localStorage.removeItem("opt_venue");
                }
                // The useEffect will automatically refetch when selectedVenue changes
              }}
              style={{
                background: "rgba(20,20,30,0.85)",
                color: "#fff",
                border: "1px solid #888",
                borderRadius: 8,
                padding: "6px 12px",
                fontWeight: 700,
                cursor: "pointer",
                fontSize: "0.9rem"
              }}
            >
              <option value="">Show next question at...</option>
              {venueOptions.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>

            {/* Hashtag Badge */}
            {hashtagGenre && (
              <div style={{
                background: "rgba(97,218,251,0.85)",
                color: "#111",
                borderRadius: 12,
                padding: "4px 12px",
                fontWeight: 800,
                fontSize: "0.85rem",
                boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
              }}>
                #{hashtagGenre}
              </div>
            )}
            {hashtagTags && hashtagTags.length > 0 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {hashtagTags.map((tag) => (
                  <div
                    key={tag}
                    style={{
                      background: "rgba(97,218,251,0.25)",
                      color: "#eaf2ff",
                      border: "1px solid rgba(97,218,251,0.6)",
                      borderRadius: 12,
                      padding: "4px 10px",
                      fontWeight: 800,
                      fontSize: "0.8rem",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.25)"
                    }}
                  >
                    #{tag}
                  </div>
                ))}
              </div>
            )}

            {/* Question Type Badge */}
            {editDraft?.CAT && (
              (() => {
                const catRaw = String(editDraft.CAT || "");
                const pal = getCatPalette(catRaw);
                return (
                  <div style={{
                    background: pal.bg,
                    color: pal.fg,
                    borderRadius: 12,
                    padding: "4px 12px",
                    fontWeight: 800,
                    fontSize: "0.85rem",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
                  }}>
                    {renderCatLabel(catRaw)}
                  </div>
                );
              })()
            )}

            {/* Statistics Badges */}
            <div style={{
              background: "rgba(33,166,74,0.85)",
              color: "#fff",
              borderRadius: 12,
              padding: "4px 12px",
              fontWeight: 800,
              fontSize: "0.85rem",
              boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
            }}>
              {previewReadyCount} Ready
            </div>
            <div style={{
              background: "rgba(214,57,57,0.85)",
              color: "#fff",
              borderRadius: 12,
              padding: "4px 12px",
              fontWeight: 800,
              fontSize: "0.85rem",
              boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
            }}>
              {previewFlaggedCount} Flagged
            </div>

            {/* Question Counter */}
            {previewQuestions.length > 0 && (
              <div style={{
                color: "rgba(255,255,255,0.8)",
                fontWeight: 700,
                fontSize: "0.9rem"
              }}>
                Question {previewQuestionIndex + 1} of {previewQuestions.length}
              </div>
            )}

            {/* Preview Navigation */}
            {previewQuestions.length > 0 && (
              <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setPreviewQuestionIndex(prev => Math.max(0, prev - 1));
                  }}
                  disabled={previewQuestionIndex === 0}
                  title="Previous available question"
                  style={{
                    background: previewQuestionIndex === 0 ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.15)",
                    color: "#fff",
                    border: "1px solid #888",
                    borderRadius: 10,
                    padding: "6px 12px",
                    fontWeight: 800,
                    cursor: previewQuestionIndex === 0 ? "not-allowed" : "pointer",
                    fontSize: "0.85rem",
                    opacity: previewQuestionIndex === 0 ? 0.5 : 1
                  }}
                >
                  ◀ Previous Question
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setPreviewQuestionIndex(prev => Math.min(previewQuestions.length - 1, prev + 1));
                  }}
                  disabled={previewQuestionIndex >= previewQuestions.length - 1}
                  title="Next available question"
                  style={{
                    background: previewQuestionIndex >= previewQuestions.length - 1 ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.15)",
                    color: "#fff",
                    border: "1px solid #888",
                    borderRadius: 10,
                    padding: "6px 12px",
                    fontWeight: 800,
                    cursor: previewQuestionIndex >= previewQuestions.length - 1 ? "not-allowed" : "pointer",
                    fontSize: "0.85rem",
                    opacity: previewQuestionIndex >= previewQuestions.length - 1 ? 0.5 : 1
                  }}
                >
                  Next Question ▶
                </button>
              </div>
            )}
          </div>
        )}

        {/* Hero/question editor - Lower position when in hashtag mode */}
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            marginTop: hashtagGenre ? "80px" : "0"
          }}
        >
          <div
            ref={heroBoxRef}
            style={{
              width: "85%",
              maxWidth: "90%",
              height: "22vh",
              maxHeight: "18vh",
              marginTop: 6,
              marginBottom: 6,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              wordBreak: "break-word"
            }}
          >
            <div
              ref={questionEditableRef}
              contentEditable={true}
              suppressContentEditableWarning
              onInput={(e) => {
                let txt = e.currentTarget?.innerText ?? "";
                if (txt.length > MAX_QUESTION_CHARS) {
                  const selection = window.getSelection();
                  const range = document.createRange();
                  txt = txt.slice(0, MAX_QUESTION_CHARS);
                  e.currentTarget.innerText = txt;
                  range.selectNodeContents(e.currentTarget);
                  range.collapse(false);
                  selection.removeAllRanges();
                  selection.addRange(range);
                  openBobAlert(`Question limit is ${MAX_QUESTION_CHARS} characters.`);
                }
                setEditDraft((prev) => ({ ...(prev || {}), Q: txt }));
                // Update local preview state if in hashtag mode
                if (hashtagGenre && previewQuestions.length > 0 && previewQuestionIndex >= 0) {
                  setPreviewQuestions(prev => {
                    const updated = updatePreviewQuestionLocal(prev, previewQuestionIndex, { question: txt });
                    return updated;
                  });
                }
              }}
              spellCheck={false}
              style={{
                padding: "0.5em 0.9em",
                borderRadius: 20,
                background: "rgba(0,0,0,0.38)",
                boxShadow: "0 12px 50px rgba(0,0,0,0.35)",
                color: "#fff",
                textAlign: "center",
                lineHeight: 1.12,
                whiteSpace: "pre-wrap",
                fontFamily: "'Arial Black', Impact, Arial, sans-serif",
                fontWeight: 900,
                letterSpacing: ".01em",
                textShadow:
                  "-2px -2px 0 #111, 2px -2px 0 #111, -2px 2px 0 #111, 2px 2px 0 #111",
                fontSize: heroFontSize + "px",
                cursor: isPreviewMode ? "default" : "text",
                userSelect: "text",
                outline: "none",
                maxWidth: "100%",
                maxHeight: "100%",
                opacity: isPreviewMode ? 0.95 : 1
              }}
            />
          </div>

          <div style={{ opacity: 0.7, fontWeight: 700, marginTop: 4 }}>
            {getMultiAnswerSuffixLikeLive(displayData)}
          </div>


          {/* Loading indicator */}
          {hashtagGenre && previewLoading && (
            <div style={{
              marginTop: 12,
              color: "rgba(255,255,255,0.8)",
              fontSize: "0.9rem",
              fontStyle: "italic"
            }}>
              Loading questions...
            </div>
          )}

          {/* No questions available */}
          {hashtagGenre && !previewLoading && previewQuestions.length === 0 && (
            <div style={{
              marginTop: 12,
              color: "rgba(255,255,255,0.7)",
              fontSize: "0.9rem",
              fontStyle: "italic"
            }}>
              No questions available matching this hashtag and question type
            </div>
          )}
        </div>

        {/* Answers + media area (unchanged core) */}
        {typeUpper === "SHOWROUNDS" ? (
          <div style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            width: "100%",
            marginTop: 22
          }}>
            <ShowRoundsPreview rows={rows} />
          </div>
        ) : typeUpper === "BREAK TIME" ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginTop: 22 }}>
            <div style={{
              fontFamily: "'Arial Black', Impact, Arial, sans-serif",
              fontSize: "min(12vw, 18vh)",
              fontWeight: 900,
              color: '#fff',
              textShadow: "0 0 10px #36d, 0 0 30px #36d, 0 0 60px #36d"
            }}>
              {`${Math.floor(timerNum / 60).toString().padStart(2, '0')}:${(timerNum % 60).toString().padStart(2, '0')}`}
            </div>
          </div>
        ) : (
          !isDisplay &&  (
            <div style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              width: "100%",
              marginTop: 2,
              gap: p1Url ? 40 : 0
            }}>
              {/* Answers column (unchanged logic) */}
              <div
                style={{
                flex: 1,
                minWidth: typeUpper === "BREADCRUMBS" ? 0 : undefined,
                display: "flex",
                flexDirection: "column",
                alignItems: typeUpper === "BREADCRUMBS" ? "stretch" : (p1Url ? "flex-end" : "center"),
                justifyContent: typeUpper === "BREADCRUMBS" ? "flex-start" : "center",
                minHeight: typeUpper === "BREADCRUMBS" ? 0 : undefined,
                height: "100%",
                paddingBottom: typeUpper === "BREADCRUMBS" ? 6 : 0,
                boxSizing: "border-box"
              }}
              >
                {(() => {
                  // === TRUE/FALSE preview with badges ===
                  if (typeUpper === "TRUE/FALSE") {
                    const firstIdx   = answersOrder[0] ?? 0;
                    const firstLabel = String(answers[firstIdx] || "").trim().toUpperCase() === "FALSE" ? "FALSE" : "TRUE";
                    const secondLabel = firstLabel === "TRUE" ? "FALSE" : "TRUE";
                    const tfPills = [
                      { key: firstLabel,  label: firstLabel,  correct: true },
                      { key: secondLabel, label: secondLabel, correct: false },
                    ];
                    const pillBg = (label) => (label === "TRUE" ? "#2fb36a" : "#ff4a4a");
                    const pillBorder = "rgba(0,0,0,.18)";

                    return (
                      <ol className="answer-list host-answer-list-layout" type="A">
                        {tfPills.map((p, idx) => {
                          const badgeBg   = p.correct ? "rgba(33,166,74,.92)" : "rgba(214,57,57,.92)";
                          const badgeBrdr = p.correct ? "#52d788"            : "#ff7a7a";
                          const badgeGlyph= p.correct ? "✓"                  : "✗";

                          return (
                            <li key={p.key} className="host-answer-item">
                              <div
                                className={`answer-option option-${String.fromCharCode(97 + idx)} host-answer-pill`}
                                style={{
                                  position: "relative",
                                  fontSize: computeAnswerFontSize(p.label),
                                  fontFamily: "'Arial Black', Impact, Arial, sans-serif",
                                  color: "#fff",
                                  textShadow: "-2px -2px 0 #111, 2px -2px 0 #111, -2px 2px 0 #111, 2px 2px 0 #111",
                                  userSelect: "none",
                                  pointerEvents: "none",
                                  background: pillBg(p.label),
                                  border: `1px solid ${pillBorder}`,
                                  boxShadow: "0 6px 18px rgba(0,0,0,.25)",
                                  borderRadius: "999px",
                                }}
                                title={p.label}
                              >
                                <span
                                  aria-hidden="true"
                                  style={{
                                    position: "absolute",
                                    right: "14px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "999px",
                                    background: badgeBg,
                                    border: `2px solid ${badgeBrdr}`,
                                    boxShadow: "0 4px 16px rgba(0,0,0,0.45)",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "1.4rem",
                                    fontWeight: 900,
                                    lineHeight: 1,
                                    pointerEvents: "none"
                                  }}
                                  title={p.correct ? "Correct" : "Incorrect"}
                                >
                                  {badgeGlyph}
                                </span>
                                {p.label}
                              </div>
                            </li>
                          );
                        })}
                      </ol>
                    );
                  }

                  // === Single-option modes (TYPE allows A1–A6 alternative answers; only first shown on reveal) ===
                  const singleOptionMode = ["NEARESTANSWER", "WHEEL"].includes(typeUpper);
                  if (singleOptionMode) {
                    const answerIdx = answersOrder[0] ?? 0;
                    const rawAnswer = answers[answerIdx] ?? "";
                    const key = `A${(answerIdx ?? 0) + 1}`;

                    // For dynamic questions, get the current answer from previewQuestions
                    // For regular CSV questions, use rawAnswer from displayData
                    const currentAnswer = (hashtagGenre && previewQuestions.length > 0 && previewQuestionIndex >= 0) 
                      ? (previewQuestions[previewQuestionIndex]?.[key === "A1" ? "answer" : key] || rawAnswer)
                      : rawAnswer;

                    return (
                      <ol className="answer-list host-answer-list-layout" type="A">
                        <li key={0} className="host-answer-item" style={{ position: "relative", width: "100%" }}>
                          <div
                            className="answer-option option-a host-answer-pill"
                            style={{
                              position: "relative",
                              fontSize: computeAnswerFontSize(currentAnswer),
                              fontFamily: "'Arial Black', Impact, Arial, sans-serif",
                              color: "#fff",
                              textShadow: "-2px -2px 0 #111, 2px -2px 0 #111, -2px 2px 0 #111, 2px 2px 0 #111",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              cursor: "text" // Show text cursor on the pill itself
                            }}
                            onClick={(e) => {
                              // When clicking anywhere on the pill, focus the input
                              e.preventDefault();
                              e.stopPropagation();
                              const input = e.currentTarget.querySelector('input');
                              if (input) {
                                input.focus();
                                input.select();
                              }
                            }}
                          >
                            <span
                              aria-hidden="true"
                              style={{
                                position: "absolute",
                                right: "14px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                width: "40px",
                                height: "40px",
                                borderRadius: "999px",
                                background: "rgba(33, 166, 74, 0.92)",
                                border: "2px solid #52d788",
                                boxShadow: "0 4px 16px rgba(0,0,0,0.45)",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "1.4rem",
                                fontWeight: 900,
                                lineHeight: 1,
                                pointerEvents: "none", // Don't block clicks - let them pass through to input
                                zIndex: 1 // Behind the input
                              }}
                              title="Correct"
                            >
                              ✓
                            </span>

                            <input
                              type="text"
                              value={currentAnswer}
                              readOnly={false}
                              disabled={false}
                              onBeforeInput={(e) => {
                                warnAndBlockIfOver(e, currentAnswer ?? "", key);
                              }}
                              onPaste={(e) => {
                                const paste = e.clipboardData?.getData?.("text") ?? "";
                                warnAndBlockIfOver(e, currentAnswer ?? "", key, paste);
                              }}
                              onChange={(e) => {
                                let val = e.target.value;
                                const isNearestA1 = typeUpper === "NEARESTANSWER" && key === "A1";
                                if (isNearestA1 && !/^-?\d*\.?\d*$/.test(val) && val !== "") return;
                                const [limited] = limitAnswer(val);
                                val = limited;
                                setEditDraft(d => ({ ...(d || {}), [key]: val }));
                                // Update local preview state if in hashtag mode
                                if (hashtagGenre && previewQuestions.length > 0 && previewQuestionIndex >= 0) {
                                  setPreviewQuestions(prev => {
                                    const updated = updatePreviewQuestionLocal(prev, previewQuestionIndex, { [key === "A1" ? "answer" : key]: val });
                                    return updated;
                                  });
                                }
                              }}
                              onFocus={(e) => {
                                // Ensure input is focused and selectable
                                e.stopPropagation();
                              }}
                              onClick={(e) => {
                                // Stop propagation so clicking input doesn't trigger parent onClick
                                e.stopPropagation();
                              }}
                              onMouseDown={(e) => {
                                // Prevent parent from handling the click
                                e.stopPropagation();
                              }}
                              style={{
                                position: "relative",
                                width: "100%",
                                background: "transparent",
                                border: "none",
                                outline: "none",
                                textAlign: "center",
                                color: "#fff",
                                fontFamily: "'Arial Black', Impact, Arial, sans-serif",
                                fontWeight: 900,
                                letterSpacing: ".02em",
                                fontSize: "inherit",
                                textShadow: "-2px -2px 0 #111, 2px -2px 0 #111, -2px 2px 0 #111, 2px 2px 0 #111",
                                cursor: "text",
                                zIndex: 100, // Ensure input is above all other elements
                                pointerEvents: "auto", // Explicitly enable pointer events
                                padding: "0 50px 0 20px", // Add padding to avoid overlap with checkmark
                                minHeight: "100%"
                              }}
                              placeholder="Answer A"
                            />
                          </div>
                        </li>
                      </ol>
                    );
                  }

                  // === Default multi-option list - using same CSS classes as quizscreen.js ===
                  const isBreadcrumbsPreview = typeUpper === "BREADCRUMBS";
                  if (isBreadcrumbsPreview) {
                    return (
                      <BreadcrumbsEditDnD
                        pillHeight={pillHeight}
                        displayData={displayData}
                        editDraft={editDraft}
                        setEditDraft={setEditDraft}
                        answers={answers}
                        previewBreadcrumbsAreaRef={previewBreadcrumbsAreaRef}
                        hashtagGenre={hashtagGenre}
                        previewQuestions={previewQuestions}
                        previewQuestionIndex={previewQuestionIndex}
                        setPreviewQuestions={setPreviewQuestions}
                        checkForCsvChanges={checkForCsvChanges}
                        limitAnswer={limitAnswer}
                        warnAndBlockIfOver={warnAndBlockIfOver}
                      />
                    );
                  }
                  if (typeUpper === "OUTOFORDER") {
                    return (
                      <OutOfOrderEditDnD
                        pillHeight={pillHeight}
                        displayData={displayData}
                        editDraft={editDraft}
                        setEditDraft={setEditDraft}
                        answers={answers}
                        hashtagGenre={hashtagGenre}
                        previewQuestions={previewQuestions}
                        previewQuestionIndex={previewQuestionIndex}
                        setPreviewQuestions={setPreviewQuestions}
                        limitAnswer={limitAnswer}
                        warnAndBlockIfOver={warnAndBlockIfOver}
                        checkForCsvChanges={checkForCsvChanges}
                      />
                    );
                  }
                  return (
                    <ol
                      className="answer-list host-answer-list-layout"
                      type="A"
                    >
                      {answersOrder.map((answerIdx, displayIdx) => {
                        const key = `A${answerIdx + 1}`;
                        const draftVal = (displayData && Object.prototype.hasOwnProperty.call(displayData, key))
                          ? displayData[key]
                          : undefined;
                        const rawAnswer = draftVal ?? (answers[answerIdx] ?? "");

                        const isMultiLocal = typeUpper === "MULTIANSWER";
                        const isCorrectMulti = isMultiLocal && isBracketedCorrect(rawAnswer);
                        const isCorrectSingle = !isMultiLocal && displayIdx === 0;
                        const isCorrect = isMultiLocal ? isCorrectMulti : isCorrectSingle;

                        const displayText = isMultiLocal ? stripBrackets(rawAnswer) : rawAnswer;
                        const answerFont = isBreadcrumbsPreview
                          ? computeBreadcrumbsPreviewFontSize(rawAnswer, pillHeight)
                          : computeAnswerFontSize(rawAnswer);
                        const bcBadgeSize = isBreadcrumbsPreview
                          ? `${Math.max(20, Math.min(30, Math.round(pillHeight * 0.82)))}px`
                          : "40px";
                        const bcBadgeFontSize = isBreadcrumbsPreview
                          ? `${Math.max(0.62, Math.min(0.95, pillHeight * 0.028)).toFixed(2)}rem`
                          : "1.4rem";

                        const badgeBg    = isCorrect ? "rgba(33,166,74,.92)" : "rgba(214,57,57,.92)";
                        const badgeBrdr  = isCorrect ? "#52d788"            : "#ff7a7a";
                        const badgeGlyph = isCorrect ? "✓"                  : "✗";

                        const handleToggle = () => {
                          if (!isMultiLocal) return;
                          setEditDraft(d => {
                            const next = { ...(d || {}) };
                            const current = (next[key] ?? rawAnswer) || "";
                            next[key] = setBracketed(current, !isCorrect);
                            return next;
                          });
                          // Update local preview state if in hashtag mode
                          if (hashtagGenre && previewQuestions.length > 0 && previewQuestionIndex >= 0) {
                            setPreviewQuestions(prev => {
                              const current = (editDraft?.[key] ?? rawAnswer) || "";
                              const updated = updatePreviewQuestionLocal(prev, previewQuestionIndex, { [key === "A1" ? "answer" : key]: setBracketed(current, !isCorrect) });
                              return updated;
                            });
                          }
                        };

                        return (
                          <li
                            key={displayIdx}
                            className="host-answer-item"
                          >
                            <div
                              className={`answer-option option-${String.fromCharCode(97 + (displayIdx % 6))} host-answer-pill`}
                              style={{
                                position: "relative",
                                fontSize: answerFont,
                                fontFamily: "'Arial Black', Impact, Arial, sans-serif",
                                color: "#fff",
                                textShadow: "-2px -2px 0 #111, 2px -2px 0 #111, -2px 2px 0 #111, 2px 2px 0 #111",
                                pointerEvents: "auto",
                                userSelect: "text",
                                ...(isBreadcrumbsPreview
                                  ? {
                                    height: `${pillHeight}px`,
                                    minHeight: `${pillHeight}px`,
                                    maxHeight: `${pillHeight}px`,
                                    boxSizing: "border-box",
                                    lineHeight: 1.1,
                                    padding: "0 0.65em"
                                  }
                                  : {})
                              }}
                            >
                              <span
                                aria-hidden="true"
                                onClick={(e) => { e.stopPropagation(); handleToggle(); }}
                                style={{
                                  position: "absolute",
                                  right: isBreadcrumbsPreview ? "8px" : "14px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  width: bcBadgeSize,
                                  height: bcBadgeSize,
                                  borderRadius: "999px",
                                  background: badgeBg,
                                  border: `2px solid ${badgeBrdr}`,
                                  boxShadow: "0 4px 16px rgba(0,0,0,0.45)",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: bcBadgeFontSize,
                                  fontWeight: 900,
                                  lineHeight: 1,
                                  cursor: isMultiLocal ? "pointer" : "default",
                                  pointerEvents: "auto"
                                }}
                                title={isCorrect ? "Correct" : "Incorrect"}
                              >
                                {badgeGlyph}
                              </span>

                              <input
                                type="text"
                                value={rawAnswer}
                                onBeforeInput={(e) => {
                                  warnAndBlockIfOver(e, rawAnswer ?? "", key);
                                }}
                                onPaste={(e) => {
                                  const paste = e.clipboardData?.getData?.("text") ?? "";
                                  warnAndBlockIfOver(e, rawAnswer ?? "", key, paste);
                                }}
                                onChange={(e) => {
                                  let val = e.target.value;
                                  if (typeUpper === "NEARESTANSWER" && key === "A1") {
                                    if (!/^-?\d*\.?\d*$/.test(val) && val !== "") return;
                                  }
                                  const [limited] = limitAnswer(val);
                                  val = limited;
                                  setEditDraft(d => ({ ...(d || {}), [key]: val }));
                                  // Update local preview state if in hashtag mode
                                  if (hashtagGenre && previewQuestions.length > 0 && previewQuestionIndex >= 0) {
                                    setPreviewQuestions(prev => {
                                      const updated = updatePreviewQuestionLocal(prev, previewQuestionIndex, { [key === "A1" ? "answer" : key]: val });
                                      return updated;
                                    });
                                  }
                                }}
                                style={{
                                  width: "100%",
                                  background: "transparent",
                                  border: "none",
                                  outline: "none",
                                  textAlign: "center",
                                  color: "#fff",
                                  fontFamily: "'Arial Black', Impact, Arial, sans-serif",
                                  fontWeight: 900,
                                  letterSpacing: ".02em",
                                  fontSize: "inherit",
                                  textShadow: "-2px -2px 0 #111, 2px -2px 0 #111, -2px 2px 0 #111, 2px 2px 0 #111",
                                  cursor: "text",
                                  padding: isBreadcrumbsPreview ? "0 32px 0 8px" : undefined
                                }}
                                placeholder={isBreadcrumbsPreview
                                  ? getBreadcrumbsPillPlaceholder(displayIdx)
                                  : `Answer ${String.fromCharCode(65 + displayIdx)}`}
                              />
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  );
                })()}
              </div>

              {/* Image column (unchanged) */}
              {p1Url && (
                !isJigsawOn(displayData) ? (
                  <img
                    src={p1Url}
                    alt="Quiz Visual"
                    style={{
                      minWidth: "480px",
                      maxWidth: "70vw",
                      width: "clamp(320px, 60vw, 1000px)",
                      height: "auto",
                      maxHeight: "58vh",
                      marginTop: "3vh",
                      marginRight: "2vw",
                      alignSelf: "flex-start",
                      borderRadius: "14px",
                      background: "#222",
                      border: "3px solid #111",
                      boxShadow: "0 6px 24px rgba(0,0,0,0.55)"
                    }}
                  />
                ) : (
                  <JigsawImageReveal
                    src={p1Url}
                    timer={timerNum}
                    totalTime={timerNum || 60}
                    rows={6}
                    cols={8}
                    style={{
                      minWidth: "480px",
                      maxWidth: "70vw",
                      width: "clamp(320px, 60vw, 1000px)",
                      height: "auto",
                      maxHeight: "58vh",
                      marginTop: "3vh",
                      marginRight: "2vw",
                      alignSelf: "flex-start",
                      borderRadius: "14px",
                      background: "#222",
                      border: "3px solid #111",
                      boxShadow: "0 6px 24px rgba(0,0,0,0.55)"
                    }}
                  />
                )
              )}
            </div>
          )
        )}

        </div>

        {/* Visible Explanation and NOTES footer with Music previews to the right */}
        <div ref={previewFooterRef} style={{
          ...(isBreadcrumbEditModal ? {
            position: "relative",
            left: 0,
            right: 0,
            transform: "none",
            bottom: "auto",
            margin: "6px auto 0",
            flexShrink: 0,
            zIndex: 2
          } : {
            position: "absolute",
            bottom: 12,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 50
          }),
          width: "86%",
          maxWidth: "86%",
          display: "flex",
          flexDirection: "column",
          gap: 12,
          alignItems: "stretch"
        }}>
          {/* Visible Explanation Field */}
          <input
            type="text"
            value={(() => {
              // In preview mode, use preview question visibleExplanation, otherwise use editDraft
              if (hashtagGenre && previewQuestions.length > 0 && previewQuestions[previewQuestionIndex]) {
                return previewQuestions[previewQuestionIndex].visibleExplanation || "";
              }
              return editDraft?.visibleExplanation ?? "";
            })()}
            onChange={(e) => {
              const val = e.target.value;
              setEditDraft((d) => ({ ...(d || {}), visibleExplanation: val }));
              // Update local preview state if in hashtag mode
              if (hashtagGenre && previewQuestions.length > 0 && previewQuestionIndex >= 0) {
                setPreviewQuestions(prev => {
                  const updated = updatePreviewQuestionLocal(prev, previewQuestionIndex, { visibleExplanation: val });
                  return updated;
                });
              }
            }}
            placeholder="Visible Explanation (optional)..."
            maxLength={130}
            style={{
              width: "100%",
              height: "40px",
              background: "#ffeb3b",
              color: "#333",
              border: "1px solid #fbc02d",
              borderRadius: "8px",
              padding: "10px 15px",
              fontSize: "1rem",
              boxSizing: "border-box",
              fontFamily: "sans-serif",
              outline: "none"
            }}
          />
          
          <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
          <textarea
            value={(() => {
              // In preview mode, use preview question notes, otherwise use editDraft
              if (hashtagGenre && previewQuestions.length > 0 && previewQuestions[previewQuestionIndex]) {
                return previewQuestions[previewQuestionIndex].notes || "";
              }
              return editDraft?.NOTES ?? "";
            })()}
            onChange={(e) => {
              const val = e.target.value;
              setEditDraft((d) => ({ ...(d || {}), NOTES: val }));
              // Update local preview state if in hashtag mode
              if (hashtagGenre && previewQuestions.length > 0 && previewQuestionIndex >= 0) {
                setPreviewQuestions(prev => {
                  const updated = updatePreviewQuestionLocal(prev, previewQuestionIndex, { notes: val });
                  return updated;
                });
              }
            }}
            disabled={readOnly}
            placeholder="Notes…"
            rows={4}
            style={{
              flex: "1 1 auto",
              minHeight: "6em",
              maxHeight: "12vh",
              overflowY: "auto",
              background: "rgba(0,0,0,0.55)",
              borderRadius: 14,
              border: "2px solid rgba(255,255,255,0.45)",
              boxShadow: "inset 0 2px 8px rgba(0,0,0,0.55), 0 0 0 3px rgba(0,0,0,0.25)",
              padding: "10px 14px",
              color: "#fff",
              fontWeight: 800,
              fontSize: "0.95rem",
              lineHeight: 1.35,
              textAlign: "center",
              textShadow: "0 2px 6px rgba(0,0,0,0.6)",
              outline: "none",
              resize: "vertical",
              cursor: readOnly ? "not-allowed" : "text",
              opacity: readOnly ? 0.85 : 1
            }}
          />
          
          {/* Tiny Music Management Section - to the right of notes */}
          {!isDisplay && (
            <div style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              flexShrink: 0
            }}>
              {/* MP3 Audio Section - Tiny */}
              <div style={{
                background: "rgba(0,0,0,0.55)",
                borderRadius: 8,
                border: "2px solid rgba(255,255,255,0.45)",
                padding: "8px",
                width: "180px"
              }}>
                <div style={{ fontWeight: 800, fontSize: "0.75rem", marginBottom: 6, color: "#fff" }}>
                  MP3
                </div>
                {displayData.SOUND_URL ? (
                  <div>
                    <audio
                      src={displayData.SOUND_URL}
                      controls
                      style={{
                        width: "100%",
                        height: "32px",
                        borderRadius: 4,
                        fontSize: "0.7rem"
                      }}
                    />
                    <button
                      onClick={() => {
                        setEditDraft((d) => ({ ...(d || {}), SOUND_URL: "", SOUND: "" }));
                        if (hashtagGenre && previewQuestions.length > 0 && previewQuestionIndex >= 0) {
                          setPreviewQuestions(prev => {
                            const updated = updatePreviewQuestionLocal(prev, previewQuestionIndex, { Audiolink: null });
                            return updated;
                          });
                        }
                      }}
                      disabled={readOnly}
                      style={{
                        background: "#dc3545",
                        color: "#fff",
                        border: "none",
                        borderRadius: 4,
                        padding: "4px 8px",
                        fontWeight: 700,
                        cursor: readOnly ? "not-allowed" : "pointer",
                        fontSize: "0.7rem",
                        opacity: readOnly ? 0.5 : 1,
                        width: "100%",
                        marginTop: 4
                      }}
                    >
                      Delete
                    </button>
                  </div>
                ) : (
                  <div>
                    <input
                      type="file"
                      ref={mp3UploadRef}
                      accept=".mp3,.wav,.ogg,.aac,.m4a,.flac"
                      style={{ display: "none" }}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (!isAudioFile(file)) {
                          appAlert("This only accepts audio files (MP3/WAV/OGG/AAC/M4A/FLAC).");
                          e.target.value = "";
                          return;
                        }
                        try {
                          const fileRef = ref(storage, `media/${file.name.replace(/\s+/g, "_")}`);
                          await uploadBytes(fileRef, file);
                          const downloadURL = await getDownloadURL(fileRef);
                          setEditDraft((d) => ({ ...(d || {}), SOUND_URL: downloadURL, SOUND: file.name }));
                          if (hashtagGenre && previewQuestions.length > 0 && previewQuestionIndex >= 0) {
                            setPreviewQuestions(prev => {
                              const updated = updatePreviewQuestionLocal(prev, previewQuestionIndex, { Audiolink: downloadURL });
                              return updated;
                            });
                          }
                        } catch (err) {
                          appAlert("Failed to upload audio: " + err.message);
                        }
                        e.target.value = "";
                      }}
                    />
                    <button
                      onClick={() => {
                        mp3UploadRef.current?.click();
                      }}
                      disabled={readOnly}
                      style={{
                        background: "#28a745",
                        color: "#fff",
                        border: "none",
                        borderRadius: 4,
                        padding: "6px 12px",
                        fontWeight: 700,
                        cursor: readOnly ? "not-allowed" : "pointer",
                        fontSize: "0.75rem",
                        opacity: readOnly ? 0.5 : 1,
                        width: "100%"
                      }}
                    >
                      Upload
                    </button>
                  </div>
                )}
              </div>

              {/* Spotify Section - Tiny */}
              <div style={{
                background: "rgba(0,0,0,0.55)",
                borderRadius: 8,
                border: "2px solid rgba(255,255,255,0.45)",
                padding: "8px",
                width: "180px"
              }}>
                <div style={{ fontWeight: 800, fontSize: "0.75rem", marginBottom: 6, color: "#fff" }}>
                  Spotify
                </div>
                {displayData.SPOTIFY_URL ? (
                  <div>
                    <iframe
                      src={displayData.SPOTIFY_URL}
                      width="100%"
                      height="80"
                      frameBorder="0"
                      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                      loading="lazy"
                      style={{
                        borderRadius: 6,
                        maxWidth: "100%",
                        minHeight: "80px"
                      }}
                    />
                    <button
                      onClick={() => {
                        setEditDraft((d) => ({ ...(d || {}), SPOTIFY_URL: "" }));
                        if (hashtagGenre && previewQuestions.length > 0 && previewQuestionIndex >= 0) {
                          setPreviewQuestions(prev => {
                            const updated = updatePreviewQuestionLocal(prev, previewQuestionIndex, { SpotifyUrl: null });
                            return updated;
                          });
                        }
                      }}
                      disabled={readOnly}
                      style={{
                        background: "#dc3545",
                        color: "#fff",
                        border: "none",
                        borderRadius: 4,
                        padding: "4px 8px",
                        fontWeight: 700,
                        cursor: readOnly ? "not-allowed" : "pointer",
                        fontSize: "0.7rem",
                        opacity: readOnly ? 0.5 : 1,
                        width: "100%",
                        marginTop: 4
                      }}
                    >
                      Delete
                    </button>
                  </div>
                ) : (
                  <div>
                    <input
                      type="text"
                      placeholder="Paste Spotify URL..."
                      value=""
                      onPaste={(e) => {
                        e.preventDefault();
                        const pasted = e.clipboardData.getData("text");
                        const spotifyUrl = extractSpotifyUrl(pasted);
                        if (spotifyUrl) {
                          setEditDraft((d) => ({ ...(d || {}), SPOTIFY_URL: spotifyUrl }));
                          if (hashtagGenre && previewQuestions.length > 0 && previewQuestionIndex >= 0) {
                            setPreviewQuestions(prev => {
                              const updated = updatePreviewQuestionLocal(prev, previewQuestionIndex, { SpotifyUrl: spotifyUrl });
                              return updated;
                            });
                          }
                        } else {
                          appAlert("Could not extract Spotify URL from pasted content. Please paste the full iframe code or Spotify embed URL.");
                        }
                      }}
                      onChange={(e) => {
                        const value = e.target.value;
                        const spotifyUrl = extractSpotifyUrl(value);
                        if (spotifyUrl && value.includes('open.spotify.com/embed')) {
                          setEditDraft((d) => ({ ...(d || {}), SPOTIFY_URL: spotifyUrl }));
                          if (hashtagGenre && previewQuestions.length > 0 && previewQuestionIndex >= 0) {
                            setPreviewQuestions(prev => {
                              const updated = updatePreviewQuestionLocal(prev, previewQuestionIndex, { SpotifyUrl: spotifyUrl });
                              return updated;
                            });
                          }
                        }
                      }}
                      disabled={readOnly}
                      style={{
                        width: "100%",
                        background: "rgba(255,255,255,0.1)",
                        border: "1px solid rgba(255,255,255,0.3)",
                        borderRadius: 4,
                        padding: "6px",
                        color: "#fff",
                        fontSize: "0.7rem",
                        marginBottom: 4,
                        opacity: readOnly ? 0.5 : 1
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
          </div>
        </div>

        {/* ===== NEW: QuestionLibrary overlay (auto if Q is blank) ===== */}
        {showLib && (
          <QuestionLibraryPicker
            items={qlFiltered}
            loading={qlLoading}
            search={qlSearch}
            setSearch={setQlSearch}
            onChoose={applyFromLibrary}
            onCancel={() => setShowLib(false)}
          />
        )}
      </div>
    </div>
  );
})()}




{/* Global Bob modals (mounted at top level) */}
<BobModal
  open={limitModal.open}
  onClose={closeBobAlert}
  message={limitModal.message}
  confirmText="OK"
/>

<BobModal
  open={confirmModal.open}
  onClose={closeBobConfirm}
  message={confirmModal.message}
  cancelText="Cancel"
  confirmText="Yes, replace all"
  onConfirm={confirmModal.onConfirm}
/>



<input
  type="file"
  ref={fileInputRef}
  style={{ display: "none" }}
  onChange={async (e) => {
    const mediaToUrlCol = {
      SOUND: "SOUND_URL", BACKGROUND: "BACKGROUND_URL", P1: "P1_URL", VIDEO: "VIDEO_URL",
      SOUND_URL: "SOUND", BACKGROUND_URL: "BACKGROUND", P1_URL: "P1", VIDEO_URL: "VIDEO"
    };

    const file = e.target.files?.[0];
    if (!file || mediaTarget.row === null || !mediaTarget.col) return;

    setUploadingMedia({ row: mediaTarget.row, col: mediaTarget.col });

    try {
      // 1) Work out storage path + upload (unchanged from your current flow)
      const cleanName = sanitiseForStorage(file.name);
      const folder =
        mediaTarget.col.startsWith("SOUND") ? "media" :
        mediaTarget.col.startsWith("BACKGROUND") ? "media" :
        mediaTarget.col.startsWith("VIDEO") ? "media" :
        mediaTarget.col.startsWith("P1") ? "media" : "media";

      const fileRef = ref(storage, `${folder}/${cleanName}`);
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);

      // 2) Write filename + URL to the right columns
      const filenameCol = mediaToUrlCol[mediaTarget.col]; // maps *_URL <-> base col
      const urlCol      = mediaTarget.col.endsWith("_URL") ? mediaTarget.col : `${mediaTarget.col}_URL`;
      const nameCol     = mediaTarget.col.endsWith("_URL") ? filenameCol : mediaTarget.col;

      setRows((old) =>
        old.map((r, i) =>
          i === mediaTarget.row ? { ...r, [nameCol]: cleanName, [urlCol]: url } : r
        )
      );

      // 3) If this was a VIDEO (or P1 with a video), set TIME to the video length (secs)
      const isVideoDestination =
        /^VIDEO(_URL)?$/.test(mediaTarget.col) || /^P1(_URL)?$/.test(mediaTarget.col);

      if (isVideoDestination && looksLikeVideo(file)) {
        const dur = await getMediaDurationSec(file); // returns seconds
        if (Number.isFinite(dur) && dur > 0) {
          const secs = Math.max(1, Math.round(dur));
          setRows((old) =>
            old.map((r, i) => {
              if (i !== mediaTarget.row) return r;

              // Only override TIME if it looks “auto/empty”
              const current = String(r.TIME ?? "").trim();
              const looksAuto = current === "" || current === "0";
              return looksAuto ? { ...r, TIME: String(secs) } : r;
            })
          );
        }
      }

      setMessage("Media uploaded.");
    } catch (err) {
      console.error(err);
      setMessage("Upload failed: " + (err?.message || err));
    } finally {
      setUploadingMedia({ row: null, col: null });
      setMediaTarget({ row: null, col: null });
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }}
/>

{/* Unsaved Changes Warning Modal */}
<BobModal
  open={showUnsavedChangesModal}
  onClose={() => {
    setShowUnsavedChangesModal(false);
    setPendingNavigation(null);
  }}
  title="Quizmaster Bob says"
  message="You have unsaved changes. Are you sure you want to leave without saving?"
  confirmText="Leave Without Saving"
  cancelText="Cancel"
  onConfirm={() => {
    if (pendingNavigation) {
      pendingNavigation();
    }
  }}
/>

{/* Bob Modal for toggle all flags confirmation */}
<BobModal
  open={showToggleAllFlagsModal}
  onClose={() => setShowToggleAllFlagsModal(false)}
  title="Quizmaster Bob says"
  message="Toggle flags for numbered questions only? Upcoming Rounds, STARTROUND, ENDROUND, Display, Showrounds, END QUIZ, and rows after END QUIZ will be left unchanged."
  confirmText="Yes, toggle all"
  cancelText="Cancel"
  onConfirm={confirmToggleAllFlags}
/>







    </div>
  );
}